import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { Slider } from "../../components/ui/slider";
import { Checkbox } from "../../components/ui/checkbox";
import {
  ArrowLeft,
  Save,
  Layers,
  ChevronDown,
  ChevronUp,
  Building2,
  Palette,
  DollarSign,
  Plus,
  Minus,
  Calculator,
  FileText,
  Home,
  Image,
  Video,
  Box,
  Eye,
  CheckCircle2,
  AlertCircle,
  Presentation,
  Calendar,
  CreditCard,
  MapPin,
  X,
  Mountain,
  Trees,
  Repeat,
  Trash2,
  Monitor,
  Sparkles,
  Shield,
  Zap,
  Globe,
  Check,
  Paintbrush,
  HelpCircle,
  Info,
  Orbit,
  Film,
  AlertTriangle,
  Megaphone,
  RefreshCw,
  Clock
} from "lucide-react";
import { TimelineEstimateWidget } from "../../components/calculators/TimelineEstimateWidget";
import { calculateInteriorModeling, INTERIOR_MODELING_TIERS, normalizeTiers } from "./interiorModelingPricing";
import VideoAnimationPicker from "../../components/calculators/VideoAnimationPicker";
import CollateralBlock from "../../components/calculators/CollateralBlock";
import {
  calculateCollateral,
  COLLATERAL_DEFAULT_PRICES,
  COLLATERAL_EMPTY_STATE,
} from "./collateralPricing";
import {
  VIDEO_ANIMATION_TYPES,
  SOCIAL_MEDIA_PACK,
  getAnimationPrice,
  getAnimationShots,
  getAnimationDisplayName,
  getAnimationIncludes,
} from "./videoAnimationPricing";

const SALESPEOPLE = ["Julian Ruiz", "Jordan Evans"];

// VEXOS Quote Types
const VEXOS_QUOTE_TYPES = [
  { value: "building", label: "Building", description: "Multi-family, towers, large developments" },
  { value: "sfh", label: "SFH", description: "Single-family home" },
  { value: "both", label: "Both", description: "Mixed: SFH homes + building(s)" }
];

// SFH VEXOS Pricing
const SFH_BASE_PLATFORM_FEE = 10000;

// Interior Design Direction Configuration
const DESIGN_COMPLETENESS_LEVELS = [
  { 
    value: "full_package", 
    label: "Fully Developed Package", 
    fee: 0,
    shortLabel: "Full Package",
    description: "Client has a complete interior design package and TRP is replicating a defined design.",
    assets: [
      "Finish schedules",
      "Furniture layout plans",
      "Furniture selections",
      "Interior elevations (kitchen, bathrooms, millwork)",
      "Lighting plans",
      "Material specifications",
      "Appliance schedules",
      "Cabinetry details",
      "Flooring types and patterns",
      "Fixture selections",
      "Color palettes / paint schedules"
    ],
    questions: [
      "Does the client have finish schedules?",
      "Are there interior elevations?",
      "Is there a furniture layout?",
      "Are specific materials selected?",
      "Is there an interior designer package?"
    ],
    trpImpact: "Faster production, fewer revisions, pricing remains standard."
  },
  { 
    value: "partial", 
    label: "Partial Interior Design", 
    fee: 1000,
    shortLabel: "Partial Design",
    description: "Client has some direction but no complete package.",
    assets: [
      "Pinterest boards",
      "Reference images",
      "Mood boards",
      "Material inspiration",
      "Rough style direction",
      "Rough furniture ideas",
      "Partial drawings"
    ],
    questions: [
      "Do you have a full design package or just inspiration?",
      "Are the materials final or just ideas?",
      "Is an interior designer involved?",
      "Are furniture pieces specified or flexible?"
    ],
    trpImpact: "Conceptual interpretation required, more revisions, more creative design work."
  },
  { 
    value: "concept_creation", 
    label: "Full Concept Creation", 
    fee: 2000,
    shortLabel: "Concept Creation",
    description: "Client has no real interior design defined and expects TRP to create the look and direction.",
    assets: [
      '"Make it look modern / luxury / nice"',
      "No designer involvement",
      "General style preferences only"
    ],
    questions: [
      "What style are you aiming for?",
      "Do you have reference projects?",
      "Are there brands or designers you like?",
      "Do you have any inspiration at all?"
    ],
    trpImpact: "Full concept development, furniture selection, material selection, design creation from scratch, more revision risk, higher creative responsibility."
  }
];

const PROJECT_SIZES = [
  { value: "small", label: "Small Project", license: 1500 },
  { value: "medium", label: "Mid-size Project", license: 3000 },
  { value: "large", label: "Large Tower", license: 5000 }
];

// Exterior Modeling Complexity Tiers
const COMPLEXITY_TIERS = [
  { value: "small", label: "Small", description: "Cottage, small home" },
  { value: "medium", label: "Medium", description: "Townhome, mid-rise" },
  { value: "large", label: "Large", description: "High-rise tower" },
  { value: "xl", label: "XL", description: "Luxury tower, resort" }
];

// Site Environment Options  
const SITE_ENVIRONMENTS = [
  { value: "none", label: "None", description: "No site modeling needed" },
  { value: "simple", label: "Simple", description: "Minimal roads, basic landscape" },
  { value: "standard", label: "Standard", description: "Roads, sidewalks, landscape" },
  { value: "complex", label: "Complex", description: "Pools, waterfront, terrain" },
  { value: "masterplan", label: "Masterplan", description: "Golf course, marina, large community" }
];

// Default exterior modeling rates
const DEFAULT_EXT_RATES = {
  small: 12000,
  medium: 15000,
  large: 18000,
  xl: 25000,
  replication: 500,
  site_simple: 2000,
  site_standard: 5000,
  site_complex: 10000,
  site_masterplan: 15000,
  reality_capture: 8500
};

// VR Tour Configuration - Residential (point-based, residence-type agnostic)
const RESIDENTIAL_VR_TOURS = [
  { key: "studio", label: "4 Points", includedPoints: 4, rateKey: "studio",    recommendedFor: "Suitable for studios or 1-bedroom layouts" },
  { key: "1bed",   label: "6 Points", includedPoints: 6, rateKey: "1 bedroom", recommendedFor: "Suitable for 1–2 bedroom layouts" },
  { key: "2bed",   label: "8 Points", includedPoints: 8, rateKey: "2 bedroom", recommendedFor: "Suitable for 2–3 bedroom layouts" },
  { key: "3bed",   label: "10 Points", includedPoints: 10, rateKey: "3 bedroom", recommendedFor: "Suitable for larger residences or premium layouts" }
];

// VR Tour Configuration - Amenity
const AMENITY_VR_TOURS = [
  { key: "small", label: "Small", includedPoints: 4, rateKey: "amenity_small" },
  { key: "medium", label: "Medium", includedPoints: 6, rateKey: "amenity_medium" },
  { key: "large", label: "Large", includedPoints: 8, rateKey: "amenity_large" },
  { key: "xl", label: "XL", includedPoints: 10, rateKey: "amenity_xl" }
];

// Default rates (fallback if not in pricing rules)
const DEFAULT_VR_RATES = {
  studio: 14000,
  "1 bedroom": 18000,
  "2 bedroom": 20000,
  "3 bedroom": 22000,
  amenity_small: 12000,
  amenity_medium: 16000,
  amenity_large: 20000,
  amenity_xl: 24000,
  additional_point: 750
};

// Customizer Category Types
const CUSTOMIZER_CATEGORY_TYPES = [
  { value: "flooring", label: "Flooring" },
  { value: "cabinets", label: "Cabinets" },
  { value: "countertops", label: "Countertops" },
  { value: "backsplash", label: "Backsplash" },
  { value: "appliance_package", label: "Appliance Package" },
  { value: "plumbing_fixtures", label: "Plumbing Fixtures" },
  { value: "furniture_package", label: "Furniture Package" },
  { value: "paint_wall_finish", label: "Paint / Wall Finish" },
  { value: "lighting_package", label: "Lighting Package" },
  { value: "custom", label: "Custom Category" }
];

// Default Customizer Pricing (fallback)
const DEFAULT_CUSTOMIZER_PRICING = {
  baseFee: 2500,
  // Standard tiered option pricing
  optionPriceTier1: 800,
  optionPriceTier2: 700,
  optionPriceTier3Plus: 500,
  // Furniture Package tiered pricing (premium)
  furnitureTier1: 1800,
  furnitureTier2: 1500,
  furnitureTier3: 1200,
  furnitureTier4Plus: 1000
};

// Per-Tour Name Row — used in the editable list below the VR tier cards
function VRTourNameRow({ tour, idx, tierKey, tierLabel, includedBase, category, rate, additionalPointRate, viewChangeRate, onUpdate, onRemove }) {
  const additionalPts = Math.max(0, parseInt(tour.additionalPoints) || 0);
  const viewChangeCount = Math.max(0, parseInt(tour.viewChangeCount) || 0);
  const finalPts = (includedBase || 0) + additionalPts;
  const baseCost = rate;
  const addCost = additionalPts * (additionalPointRate || 0);
  const viewChangeCost = viewChangeCount * (viewChangeRate || 0);
  const totalCost = baseCost + addCost + viewChangeCost;
  const placeholder = category === "residential"
    ? (idx === 0 ? "e.g. Residence 301" : "e.g. Penthouse A")
    : (idx === 0 ? "e.g. Lobby" : "e.g. Pool Deck");
  const testidPrefix = `vr-tour-${category}-${tierKey}-${idx}`;
  return (
    <div
      className="grid grid-cols-12 gap-2 items-center p-2 rounded-md bg-background/70 border border-border/60"
      data-testid={`${testidPrefix}-row`}
    >
      <div className="col-span-5">
        <Input
          value={tour.name || ""}
          onChange={(e) => onUpdate("name", e.target.value)}
          placeholder={placeholder}
          className="h-7 text-xs"
          data-testid={`${testidPrefix}-name`}
        />
      </div>
      <div className="col-span-3 flex items-center gap-1.5">
        <Label className="text-[10px] text-muted-foreground whitespace-nowrap">+pts</Label>
        <Button
          variant="outline"
          size="icon"
          className="h-6 w-6"
          onClick={() => onUpdate("additionalPoints", Math.max(0, additionalPts - 1))}
          data-testid={`${testidPrefix}-addpts-minus`}
        >
          <Minus className="w-2.5 h-2.5" />
        </Button>
        <span className="w-5 text-center text-xs font-medium" data-testid={`${testidPrefix}-addpts-value`}>{additionalPts}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-6 w-6"
          onClick={() => onUpdate("additionalPoints", Math.min(5, additionalPts + 1))}
          data-testid={`${testidPrefix}-addpts-plus`}
        >
          <Plus className="w-2.5 h-2.5" />
        </Button>
      </div>
      <div className="col-span-3 text-[11px]" data-testid={`${testidPrefix}-points`}>
        <span className="font-medium text-emerald-600">{finalPts} pts</span>
        {additionalPts > 0 && (
          <span className="text-muted-foreground ml-1">({includedBase} + {additionalPts})</span>
        )}
      </div>
      <div className="col-span-1 flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-rose-500"
          onClick={onRemove}
          data-testid={`${testidPrefix}-remove`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      {/* View-Change Duplicates — second row */}
      <div className="col-span-12 flex items-center gap-2 pt-1.5 mt-1 border-t border-dashed border-border/60">
        <span className="text-[10px] text-muted-foreground whitespace-nowrap" title="Same floorplan/typology — only backplate + 1–3 rendered images change. Volume-priced: 1–5 $400, 6–10 $350, 11–20 $300, 21–50 $250, 51+ $200">View-Change Dupes</span>
        <Button
          variant="outline"
          size="icon"
          className="h-6 w-6"
          onClick={() => onUpdate("viewChangeCount", Math.max(0, viewChangeCount - 1))}
          data-testid={`${testidPrefix}-vc-minus`}
        >
          <Minus className="w-2.5 h-2.5" />
        </Button>
        <input
          type="number"
          min="0"
          inputMode="numeric"
          value={viewChangeCount}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") { onUpdate("viewChangeCount", 0); return; }
            const n = parseInt(v, 10);
            if (!Number.isNaN(n)) onUpdate("viewChangeCount", Math.max(0, n));
          }}
          onFocus={(e) => e.target.select()}
          className="w-12 h-6 text-center text-xs font-medium bg-transparent border border-border/60 rounded outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid={`${testidPrefix}-vc-value`}
        />
        <Button
          variant="outline"
          size="icon"
          className="h-6 w-6"
          onClick={() => onUpdate("viewChangeCount", viewChangeCount + 1)}
          data-testid={`${testidPrefix}-vc-plus`}
        >
          <Plus className="w-2.5 h-2.5" />
        </Button>
        {viewChangeCount > 0 && viewChangeRate > 0 && (
          <span className="text-[10px] text-amber-600 ml-auto font-mono">
            {viewChangeCount} × ${viewChangeRate} = ${(viewChangeCount * viewChangeRate).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

// VR Tour Card Component with Real-Time Customizer
function VRTourCard({  
  config, 
  quantity, 
  setQuantity, 
  additionalPoints, 
  setAdditionalPoints, 
  rate, 
  totalCost,
  customizerEnabled,
  setCustomizerEnabled,
  customizerCategories,
  setCustomizerCategories,
  customizerPricing,
  customizerCalc,
  hideAdditionalPoints = false
}) {
  const finalPoints = config.includedPoints + (additionalPoints || 0);
  
  // Add a new category to the customizer
  const addCategory = () => {
    const newCategory = {
      id: Date.now(),
      type: "flooring",
      customName: "",
      optionCount: 3
    };
    setCustomizerCategories([...customizerCategories, newCategory]);
  };
  
  // Update a category
  const updateCategory = (id, field, value) => {
    setCustomizerCategories(customizerCategories.map(cat => 
      cat.id === id ? { ...cat, [field]: value } : cat
    ));
  };
  
  // Remove a category
  const removeCategory = (id) => {
    setCustomizerCategories(customizerCategories.filter(cat => cat.id !== id));
  };
  
  return (
    <div className={`p-4 rounded-xl border-2 transition-all ${quantity > 0 ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border hover:border-muted-foreground/30'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold">{config.label}</h4>
          {config.recommendedFor ? (
            <p className="text-[11px] text-muted-foreground/80 leading-snug mt-0.5">{config.recommendedFor}</p>
          ) : (
            <p className="text-xs text-muted-foreground">{config.includedPoints} points included</p>
          )}
        </div>
        {quantity > 0 && !hideAdditionalPoints && (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            {finalPoints} pts
          </Badge>
        )}
        {quantity > 0 && hideAdditionalPoints && (
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
            ×{quantity}
          </Badge>
        )}
      </div>
      
      {/* Quantity Control */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Quantity</Label>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => {
                const newQty = Math.max(0, quantity - 1);
                setQuantity(newQty);
                if (newQty === 0 && customizerEnabled) {
                  setCustomizerEnabled(false);
                  setCustomizerCategories([]);
                }
              }}
            >
              <Minus className="w-3 h-3" />
            </Button>
            <span className="w-8 text-center font-medium">{quantity}</span>
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Additional Points Control */}
        {quantity > 0 && !hideAdditionalPoints && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Additional Points</Label>
              <span className="text-xs font-medium">+{additionalPoints}</span>
            </div>
            <Slider
              value={[additionalPoints]}
              onValueChange={(v) => setAdditionalPoints(v[0])}
              min={0}
              max={5}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0</span>
              <span>+5</span>
            </div>
          </div>
        )}

        {/* Base Cost Display */}
        {quantity > 0 && (
          <div className="pt-2 border-t flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {hideAdditionalPoints
                ? `${quantity} × ${config.includedPoints} pts (base)`
                : `${quantity} × $${rate.toLocaleString()}${additionalPoints > 0 ? ` + ${quantity * additionalPoints} pts` : ''}`}
            </span>
            <span className="font-semibold text-emerald-600">${totalCost.toLocaleString()}</span>
          </div>
        )}

        {/* Real-Time Customizer Add-On */}
        {quantity > 0 && (
          <div className="pt-3 mt-1 border-t border-dashed space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <Label className="text-xs font-medium text-amber-600">Real-Time Customizer</Label>
              </div>
              <Switch
                checked={customizerEnabled}
                onCheckedChange={(checked) => {
                  setCustomizerEnabled(checked);
                  if (checked && customizerCategories.length === 0) {
                    // Add one default category when enabling
                    addCategory();
                  }
                }}
                data-testid={`customizer-toggle-${config.key}`}
              />
            </div>
            
            {customizerEnabled && (
              <div className="space-y-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-xs text-muted-foreground">
                  Configure real-time finish, furniture, material, and upgrade selections for this tour.
                </p>
                
                {/* Base Fee Display */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Base Activation Fee</span>
                  <span className="font-medium">${customizerPricing.baseFee.toLocaleString()}</span>
                </div>
                
                {/* Categories */}
                <div className="space-y-2">
                  {customizerCategories.map((cat, idx) => {
                    // Tiered option pricing - check for Furniture Package
                    let pricePerOption;
                    const isFurniture = cat.type === "furniture_package";
                    
                    if (isFurniture) {
                      if (cat.optionCount === 1) pricePerOption = customizerPricing.furnitureTier1;
                      else if (cat.optionCount === 2) pricePerOption = customizerPricing.furnitureTier2;
                      else if (cat.optionCount === 3) pricePerOption = customizerPricing.furnitureTier3;
                      else pricePerOption = customizerPricing.furnitureTier4Plus;
                    } else {
                      if (cat.optionCount === 1) pricePerOption = customizerPricing.optionPriceTier1;
                      else if (cat.optionCount === 2) pricePerOption = customizerPricing.optionPriceTier2;
                      else pricePerOption = customizerPricing.optionPriceTier3Plus;
                    }
                    const categoryTotal = cat.optionCount * pricePerOption;
                    
                    return (
                      <div key={cat.id} className={`p-2 rounded-md space-y-2 ${isFurniture ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-background/60'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground w-6">#{idx + 1}</span>
                          <Select 
                            value={cat.type} 
                            onValueChange={(v) => updateCategory(cat.id, "type", v)}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CUSTOMIZER_CATEGORY_TYPES.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7" 
                            onClick={() => removeCategory(cat.id)}
                          >
                            <X className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                        
                        {cat.type === "custom" && (
                          <Input
                            placeholder="Custom category name..."
                            value={cat.customName}
                            onChange={(e) => updateCategory(cat.id, "customName", e.target.value)}
                            className="h-8 text-xs"
                          />
                        )}
                        
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Options</Label>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateCategory(cat.id, "optionCount", Math.max(1, cat.optionCount - 1))}
                            >
                              <Minus className="w-2 h-2" />
                            </Button>
                            <span className="w-6 text-center text-xs font-medium">{cat.optionCount}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateCategory(cat.id, "optionCount", cat.optionCount + 1)}
                            >
                              <Plus className="w-2 h-2" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex justify-between text-xs">
                          <span className={isFurniture ? "text-purple-600" : "text-muted-foreground"}>
                            {cat.optionCount} × ${pricePerOption.toLocaleString()}/option
                            {isFurniture && " (premium)"}
                          </span>
                          <span className={`font-medium ${isFurniture ? "text-purple-600" : ""}`}>${categoryTotal.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full h-8 text-xs gap-1"
                    onClick={addCategory}
                  >
                    <Plus className="w-3 h-3" />
                    Add Category
                  </Button>
                </div>
                
                {/* Customizer Total */}
                <div className="pt-2 border-t flex items-center justify-between">
                  <span className="text-xs font-medium">Customizer Total</span>
                  <span className="font-semibold text-amber-600">${customizerCalc.total.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Payment plan presets
const PAYMENT_PRESETS = {
  "50-50": {
    label: "50 / 50",
    milestones: [
      { percent: 50, label: "Due at Signing" },
      { percent: 50, label: "Due at Project Milestone" }
    ]
  },
  "40-30-30": {
    label: "40 / 30 / 30",
    milestones: [
      { percent: 40, label: "Due at Signing" },
      { percent: 30, label: "Due at Midpoint Review" },
      { percent: 30, label: "Due Prior to Final Delivery" }
    ]
  },
  "50-25-25": {
    label: "50 / 25 / 25",
    milestones: [
      { percent: 50, label: "Due at Signing" },
      { percent: 25, label: "Due at Midpoint" },
      { percent: 25, label: "Due at Final Delivery" }
    ]
  },
  "custom": {
    label: "Custom",
    milestones: []
  }
};

// Coverage calculation — tiers loaded from backend, fallback to defaults
const DEFAULT_INVENTORY_TIERS = [
  { min: 0, max: 1000, rate: 100, label: "0–1,000 units" },
  { min: 1001, max: 2000, rate: 75, label: "1,001–2,000 units" },
  { min: 2001, max: 4000, rate: 50, label: "2,001–4,000 units" },
  { min: 4001, max: Infinity, rate: 25, label: "4,001+ units" },
];

function calculateInventoryCostWithTiers(units, tiers) {
  // Flat/volume-based: one rate applies to ALL units based on total count
  const tier = tiers.find(t => units >= t.min && units <= t.max) || tiers[tiers.length - 1];
  return { total: units * tier.rate, rate: tier.rate, tierLabel: tier.label };
}

function calculateCoverage(floorsCovered, uniqueFloorplates, repeatFloorplates) {
  const totalCovered = uniqueFloorplates + repeatFloorplates;
  const gap = floorsCovered - totalCovered;
  
  let status, statusType;
  if (gap === 0) {
    status = "All floors accounted for";
    statusType = "success";
  } else if (gap > 0) {
    status = `Missing ${gap} floor${gap > 1 ? 's' : ''} of coverage`;
    statusType = "warning";
  } else {
    status = `Over-allocated by ${Math.abs(gap)} floor${Math.abs(gap) > 1 ? 's' : ''}`;
    statusType = "warning";
  }
  
  return { totalCovered, gap, status, statusType };
}

// Create empty exterior structure row
function createEmptyStructure() {
  return {
    id: crypto.randomUUID(),
    name: "",
    complexity: "medium",
    uniqueCount: 1,
    repeatCount: 0,
    notes: ""
  };
}

export default function CombinedCalculator() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = quoteId === "new";
  const templateKey = searchParams.get("template");

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Autosave plumbing — debounced save + status indicator + ref to track in-flight saves.
  // Status: "idle" | "saving" | "saved" | "error". `idle` = nothing pending, "saved" briefly
  // shown after a successful autosave, then auto-resets to "idle".
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
  const autoSaveTimerRef = useRef(null);
  const autoSavePendingRef = useRef(false);
  const handleSaveRef = useRef(null);
  const [pricingRules, setPricingRules] = useState([]);
  const [renderingTiers, setRenderingTiers] = useState({ interior: [], exterior: [] });
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [presentationMode, setPresentationMode] = useState(false);
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [proposalHtml, setProposalHtml] = useState(null);
  
  // Dynamic tiers from pricing rules
  const [complexityTiers, setComplexityTiers] = useState(COMPLEXITY_TIERS);
  const [siteEnvironments, setSiteEnvironments] = useState(SITE_ENVIRONMENTS);
  const [inventoryTiers, setInventoryTiers] = useState(DEFAULT_INVENTORY_TIERS);
  
  // Form state
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [projectSize, setProjectSize] = useState("medium");
  // === Project Intake metadata — pure documentation fields (no pricing impact).
  // Captured up-front so the proposal PDF + handoff to PMs include lot context,
  // floorplate diversity, and the development type from the very first save.
  // Empty-string defaults so the autosave payload stays clean for fresh quotes.
  const [lotSqFt, setLotSqFt] = useState("");                         // numeric — site area
  const [uniqueFloorplanCount, setUniqueFloorplanCount] = useState(""); // integer — distinct floor plans
  const [avgFloorplanSqFt, setAvgFloorplanSqFt] = useState("");        // numeric — average plan area
  const [unitCountIntake, setUnitCountIntake] = useState("");          // integer — total units in development
  const [developmentType, setDevelopmentType] = useState("");          // enum — Condo / Townhome / Mixed-use / Master plan / Lot subdivision
  const [notes, setNotes] = useState("");
  
  // Payment plan state
  const [paymentPreset, setPaymentPreset] = useState("50-50");
  const [customMilestones, setCustomMilestones] = useState([
    { percent: 50, label: "Due at Signing" },
    { percent: 50, label: "Due at Completion" }
  ]);
  
  // VEXOS Quote Type
  const [vexosQuoteType, setVexosQuoteType] = useState("building");
  const [bothActiveTab, setBothActiveTab] = useState("sfh"); // sub-tab for "both" mode
  
  // VEXOS inputs — Building (defaults to 0/empty for true blank-slate new quotes;
  // saved quotes hydrate over these via the restoration effect)
  const [residentialBuildings, setResidentialBuildings] = useState(0);
  const [amenityBuildings, setAmenityBuildings] = useState(0);
  const [floorsCovered, setFloorsCovered] = useState(0);
  const [coverageNotes, setCoverageNotes] = useState("");
  const [residentialUnits, setResidentialUnits] = useState(0);
  const [uniqueFloorplates, setUniqueFloorplates] = useState(0);
  const [repeatFloorplates, setRepeatFloorplates] = useState(0);
  const [floorplateEnabled, setFloorplateEnabled] = useState(false);
  
  // === NEW VEXOS PAYMENT MODEL (SaaS-style) ===================================
  // Replaces the old upfront-% + financing-monthly model. The new model has:
  //   - Tiered upfront Platform Setup Fee (unit-count driven, via getVexosPlatformSetupFee)
  //   - Tiered fixed Monthly Platform Fee (unit-count driven, via getVexosMonthlyFee) —
  //     NOT financing, NOT a payment plan, just a recurring SaaS fee.
  //   - Contract term selector: 12 / 24 / 36 months (default 12). Term does NOT
  //     affect the monthly fee — it only sets the platform agreement duration.
  //
  // LEGACY STATE — kept as harmless aliases so any code paths that still reference
  // them don't crash, but they no longer drive any UI or calculations.
  const [vexosUpfrontMode] = useState("percent");
  const [vexosUpfrontPercent] = useState(100);
  const [vexosUpfrontFixed] = useState(0);
  // Contract-term state — was `vexosPaymentTermMonths`, kept the same name to avoid
  // ripple changes across saved-quote hydration. Now constrained to 12/24/36.
  const [vexosPaymentTermMonths, setVexosPaymentTermMonths] = useState(12);
  // 12 months is the only term sold; a retired term on a reopened quote is
  // still shown so the rep can move it back to 12.
  const VEXOS_CONTRACT_TERM_OPTIONS = vexosPaymentTermMonths === 12 ? [12] : [vexosPaymentTermMonths, 12];
  
  // VEXOS inputs — SFH (for "both" mode, separate from building exterior structures)
  const [sfhInventory, setSfhInventory] = useState(10);
  const [sfhExteriorStructures, setSfhExteriorStructures] = useState([
    { id: "sfh-1", name: "Home", unit_type: "structure", complexity: "standard", uniqueCount: 1, repeatCount: 0 }
  ]);
  const addSfhStructure = () => setSfhExteriorStructures(prev => [...prev, { id: `sfh-${Date.now()}`, name: "", unit_type: "structure", complexity: "standard", uniqueCount: 0, repeatCount: 0 }]);
  const removeSfhStructure = (id) => setSfhExteriorStructures(prev => prev.filter(s => s.id !== id));
  const updateSfhStructure = (id, field, value) => setSfhExteriorStructures(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  
  // VEXOS Exterior Modeling inputs
  const [exteriorStructures, setExteriorStructures] = useState([createEmptyStructure()]);
  const [siteEnvironment, setSiteEnvironment] = useState("none");
  const [includeRealityCapture, setIncludeRealityCapture] = useState(false);
  const [realityCaptureRate, setRealityCaptureRate] = useState(8500);
  
  // VEXOS Orbit Configuration inputs
  const [includeMasterOrbit, setIncludeMasterOrbit] = useState(false);
  const [masterOrbitPrice, setMasterOrbitPrice] = useState(6000);
  const [includeGeneralAreaOrbit, setIncludeGeneralAreaOrbit] = useState(false);
  const [generalAreaOrbitPrice, setGeneralAreaOrbitPrice] = useState(7000);
  const [additionalOrbits, setAdditionalOrbits] = useState([]);
  const [additionalOrbitPrice, setAdditionalOrbitPrice] = useState(4000);
  
  // Additional Orbit Types
  const ADDITIONAL_ORBIT_TYPES = [
    { value: "ph_spotlight", label: "PH Spotlight Orbit", description: "Highlight penthouse or premium units" },
    { value: "amenity_upper", label: "Amenity / Upper Level Orbit", description: "Showcase amenity floors or upper luxury levels" },
    { value: "custom", label: "Custom Orbit", description: "Custom orbit experience" }
  ];
  
  // Visualization inputs - MODELING (Interior + Amenity only - Exterior is in VEXOS section)
  const [interiorSqft, setInteriorSqft] = useState(0);
  // Admin-editable interior modeling tiers (fetched from /calculators/interior-modeling-tiers; falls back to defaults)
  const [interiorTiers, setInteriorTiers] = useState(INTERIOR_MODELING_TIERS);

  // Reusable tier fetcher — called on mount AND on window focus so admin edits are reflected without full reload
  const refetchInteriorTiers = async () => {
    try {
      const tierRes = await apiClient.get("/calculators/interior-modeling-tiers");
      if (Array.isArray(tierRes.data) && tierRes.data.length > 0) {
        setInteriorTiers(normalizeTiers(tierRes.data));
      }
    } catch (e) { /* fallback to defaults */ }
  };

  useEffect(() => {
    const handler = () => { refetchInteriorTiers(); };
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, []);
  
  // Amenity Interior Modeling — sqft-based with complexity
  const [amenityEntries, setAmenityEntries] = useState([]);
  const [amenityBaseRate, setAmenityBaseRate] = useState(1.75);
  const [amenityMinPrice, setAmenityMinPrice] = useState(3000);
  const [amenityComplexityMultipliers, setAmenityComplexityMultipliers] = useState({
    standard: 1.0, premium: 1.10, signature: 1.20
  });
  
  const addAmenity = () => setAmenityEntries(prev => [...prev, {
    id: `am-${Date.now()}`, name: "", sqft: 0, complexity: "standard",
    modelingType: "interior_existing_structure", structureSize: "medium", sharedGroupId: ""
  }]);
  const removeAmenity = (id) => setAmenityEntries(prev => prev.filter(a => a.id !== id));
  const updateAmenity = (id, field, value) => setAmenityEntries(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  
  // Visualization inputs - RENDERING
  const [interiorRenders, setInteriorRenders] = useState(0);
  const [exteriorRenders, setExteriorRenders] = useState(0);
  const [aerialRenders, setAerialRenders] = useState(0);
  const [aerialRate, setAerialRate] = useState(6000);
  const [floorPlans, setFloorPlans] = useState(0);
  const [floorPlanRate, setFloorPlanRate] = useState(1000);
  
  // Visualization inputs - VR (per-tour arrays: each entry { id, name, additionalPoints })
  const [vrResidential, setVrResidential] = useState({
    studio: [],
    "1bed": [],
    "2bed": [],
    "3bed": []
  });
  
  const [vrAmenity, setVrAmenity] = useState({
    small: [],
    medium: [],
    large: [],
    xl: []
  });

  // Custom Amenity Tours — flexible, point-based, admin-priced (per-point rate derived from Small tier)
  const [amenityCustomTours, setAmenityCustomTours] = useState([]); // [{ id, name, points }]
  const [amenityCustomConfirmed, setAmenityCustomConfirmed] = useState(false);
  const [amenityCustomExpanded, setAmenityCustomExpanded] = useState(false);
  
  const [additionalPointRate, setAdditionalPointRate] = useState(750);
  
  // VR Tour Real-Time Customizer state (per tour type)
  const [vrResidentialCustomizer, setVrResidentialCustomizer] = useState({
    studio: { enabled: false, categories: [] },
    "1bed": { enabled: false, categories: [] },
    "2bed": { enabled: false, categories: [] },
    "3bed": { enabled: false, categories: [] }
  });
  
  const [vrAmenityCustomizer, setVrAmenityCustomizer] = useState({
    small: { enabled: false, categories: [] },
    medium: { enabled: false, categories: [] },
    large: { enabled: false, categories: [] },
    xl: { enabled: false, categories: [] }
  });
  
  // Dynamic customizer pricing from backend
  const [customizerPricing, setCustomizerPricing] = useState(DEFAULT_CUSTOMIZER_PRICING);
  
  // Video Animation inputs - FIXED PRICING MODEL (AI Motion + Cinematic Film + Social Media Pack)
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const [animationType, setAnimationType] = useState("cinematic"); // 'ai' | 'cinematic'
  const [animationDuration, setAnimationDuration] = useState(60); // 30, 60, or 90 seconds
  const [animationModelingAligned, setAnimationModelingAligned] = useState(null); // null, true, or false
  const [socialMediaPack, setSocialMediaPack] = useState(false);

  // Conceptual Exterior Modeling inputs
  const [conceptModelingEnabled, setConceptModelingEnabled] = useState(false);
  const [conceptApproach, setConceptApproach] = useState("plus"); // "lite", "plus", "studio"
  const [conceptBuildings, setConceptBuildings] = useState(1);
  const [conceptModLevel, setConceptModLevel] = useState("medium"); // "light", "medium", "heavy"
  const [conceptComplexity, setConceptComplexity] = useState("standard"); // "standard", "complex", "landmark"
  const [conceptLandmarkPrice, setConceptLandmarkPrice] = useState(20000);
  const [conceptSiteContext, setConceptSiteContext] = useState("none"); // "none", "simple", "medium", "complex"
  const [conceptRenderOptimization, setConceptRenderOptimization] = useState(false);
  const [conceptVexosStructuring, setConceptVexosStructuring] = useState(false);

  // Marketing Calculator inputs
  const [marketingEnabled, setMarketingEnabled] = useState(false);
  const [marketingMode, setMarketingMode] = useState("pre_sale"); // "pre_lease" or "pre_sale"
  const [marketingTier, setMarketingTier] = useState("t3");
  const [marketingUnits, setMarketingUnits] = useState(50);
  const [marketingTimeline, setMarketingTimeline] = useState(12);
  const [marketingConvRate, setMarketingConvRate] = useState(1.5);

  // Collateral Suite state — branding, digital, sales collateral, AI-enhanced content
  const [collateralEnabled, setCollateralEnabled] = useState(false);
  const [collateralState, setCollateralState] = useState(COLLATERAL_EMPTY_STATE);
  const [collateralPrices, setCollateralPrices] = useState(COLLATERAL_DEFAULT_PRICES);
  const [mktgAiAnimatedEnabled, setMktgAiAnimatedEnabled] = useState(false);
  const [mktgAiAnimatedQty, setMktgAiAnimatedQty] = useState(3);
  const [mktgAiEnhanceEnabled, setMktgAiEnhanceEnabled] = useState(false);
  const [mktgAiEnhanceQty, setMktgAiEnhanceQty] = useState(3);
  const [mktgContentMode, setMktgContentMode] = useState("package");
  const [mktgSelectedPackage, setMktgSelectedPackage] = useState("growth");
  const [mktgRetainerEnabled, setMktgRetainerEnabled] = useState(false);
  const [mktgContentEnabled, setMktgContentEnabled] = useState(false);
  
  // Legacy fallback - only used by references that haven't been migrated. Shared pricing imported from videoAnimationPricing.js
  const ANIMATION_PRICING = VIDEO_ANIMATION_TYPES.cinematic.pricing;
  
  // Interior Design Direction inputs
  const [interiorDesignSpacesOverride, setInteriorDesignSpacesOverride] = useState(null);
  const [interiorDesignSpaces, setInteriorDesignSpaces] = useState([]);
  const [showDesignGuidance, setShowDesignGuidance] = useState(false);

  // Manual timeline override (lifted from TimelineEstimateWidget so it persists in the quote
  // and flows into the PDF proposal export).
  const [timelineOverrideEnabled, setTimelineOverrideEnabled] = useState(false);
  const [timelineOverrideWeeks, setTimelineOverrideWeeks] = useState("");

  // V1 Delivery week override (visualization payment plan dynamic milestone)
  // Default = auto-calculated from end of Modeling phase. Override takes priority if set.
  const [vizV1DeliveryWeekOverride, setVizV1DeliveryWeekOverride] = useState("");

  // Timeline estimate snapshot (from TimelineEstimateWidget) — used to auto-derive V1 week
  const [timelineEstimateSnapshot, setTimelineEstimateSnapshot] = useState(null);

  // Module toggles — sales can build VEXOS-only, Visualization-only, or combined proposals
  // Default OFF for new quotes (per spec). Auto-enabled when restoring a quote with content.
  const [vexosModuleEnabled, setVexosModuleEnabled] = useState(false);
  const [vizModuleEnabled, setVizModuleEnabled] = useState(false);
  const [aiContentModuleEnabled, setAiContentModuleEnabled] = useState(false);

  // VEXOS Lot Explorer™ — standalone premium software product with its own
  // dedicated 50/25/25 payment structure (Deposit / V1 Delivery / Final Delivery).
  // Persisted as a `interactive_2d_sitemap` section so backend module-state filtering
  // and reconcile bucketing can keep it isolated from other VEXOS scope math.
  const [lotExplorerModuleEnabled, setLotExplorerModuleEnabled] = useState(false);
  const [lotExplorerLotCount, setLotExplorerLotCount] = useState(0);
  const [lotExplorerRatePerLot, setLotExplorerRatePerLot] = useState(250);
  const [lotExplorerIncludePoi, setLotExplorerIncludePoi] = useState(true);
  const [lotExplorerIncludeCrm, setLotExplorerIncludeCrm] = useState(true);
  const [lotExplorerIncludeMedia, setLotExplorerIncludeMedia] = useState(true);
  const [lotExplorerIncludeBackend, setLotExplorerIncludeBackend] = useState(true);
  // Lot Explorer Timeline — default 4 weeks, with manual override.
  // Rendered as its own native PDF section AFTER the Lot Explorer Scope of
  // Work block (not bundled into the unified production-timeline page).
  const [lotExplorerTimelineWeeks, setLotExplorerTimelineWeeks] = useState(4);
  const [lotExplorerTimelineOverrideEnabled, setLotExplorerTimelineOverrideEnabled] = useState(false);
  const [lotExplorerTimelineOverrideWeeks, setLotExplorerTimelineOverrideWeeks] = useState("");
  // Lot Explorer Management, Hosting & Support — $100/mo default.
  // First 3 months are included (no charge); after that, 12-month lock-in.
  // Recurring fee shown as a separate line, NOT bundled into the one-time
  // Lot Explorer subtotal (so the 50/25/25 payment plan math stays clean).
  const [lotExplorerHostingMonthly, setLotExplorerHostingMonthly] = useState(100);
  const [lotExplorerHostingFreeMonths, setLotExplorerHostingFreeMonths] = useState(3);
  const [lotExplorerHostingLockMonths, setLotExplorerHostingLockMonths] = useState(12);

  // AI Content Studio deliverables — quantity-based selection of preset bundles.
  // Each entry: { id, label, description, rate, qty, processSteps?, disclaimer?, salesTip?, isPremiumBlock?, unit? }.
  // Special case — `isMotionVariant`: a SINGLE-SELECT product with multiple duration variants.
  // The active variant becomes the rate; deselecting clears qty to 0. One AI Motion selection at a time.
  const AI_CONTENT_DELIVERABLES = [
    {
      id: "ai_motion_video",
      label: "AI Motion",
      tagline: "Fast. Marketing-grade. Rapid go-to-market.",
      description: "Optimized for speed and marketing performance. Uses existing render assets to create cinematic motion content ideal for ads, social, and rapid go-to-market.",
      isMotionVariant: true,
      variants: [
        { id: "30s", label: "30 Seconds", rate: 4500 },
        { id: "60s", label: "60 Seconds", rate: 8500 },
        { id: "90s", label: "90 Seconds", rate: 12000 },
      ],
      selectedVariantId: null,
      rate: 0, // dynamic — derived from selected variant
      qty: 0,
    },
    { id: "ai_reel_bundle", label: "AI Reel Bundle", description: "Pack of 5 short-form vertical reels (9:16) with captions.", rate: 2500 },
    { id: "social_media_clips", label: "Social Media Clips", description: "10 cutdowns optimized for IG/TikTok/Reels distribution.", rate: 1800 },
    { id: "ai_lifestyle_scenes", label: "AI Lifestyle Scenes", description: "5 photoreal lifestyle stills generated from project context.", rate: 1500 },
    { id: "content_pack", label: "Content Pack", description: "Monthly content drop: 8 stills + 4 short-form videos.", rate: 2200 },
    { id: "monthly_content_retainer", label: "Monthly Content Retainer", description: "Ongoing AI-driven content production at a fixed monthly rate.", rate: 4500 },
    // Premium deliverable restored from the legacy Marketing calculator —
    // showcases the AI-enhanced rendering pipeline and full sales positioning.
    {
      id: "ai_render_enhancement",
      label: "Advanced Render Enhancement",
      description: "Each image runs through a multi-step enhancement pipeline combining advanced AI upscaling, material refinement, lighting balance, atmospheric depth, edge sharpening, and Photoshop post-production to elevate realism, sharpness, and overall visual impact.",
      rate: 599,
      unit: "image",
      isPremiumBlock: true,
      tagline: "AI-Driven Enhancement Pipeline + Precision Photoshop + Cinematic Post-Production",
      badge: "Marketing Optimization Ready",
      processSteps: [
        "AI-based super resolution & detail reconstruction",
        "Texture refinement & material clarity adjustments",
        "Lighting balance & dynamic range optimization",
        "Atmospheric depth & environmental enhancements",
        "Edge sharpening & artifact cleanup",
        "Final Photoshop polish and color grading",
      ],
      disclaimer: "Delivered as final enhanced output — no revision rounds included.",
      salesTip: "This isn't just AI — we're running your images through an enhancement pipeline that combines AI processing with manual post-production to significantly improve realism and presentation quality.",
      salesShort: "It's the difference between a render and a polished marketing image.",
    },
  ];
  const [aiContentItems, setAiContentItems] = useState(
    AI_CONTENT_DELIVERABLES.map(d => ({ ...d, qty: 0 }))
  );
  // Helper: per-item subtotal handling both quantity-based items AND motion variants.
  // For a motion-variant item, the rate comes from the actively selected variant.
  const _aiItemSubtotal = (it) => {
    if (it.isMotionVariant) {
      const v = (it.variants || []).find(x => x.id === it.selectedVariantId);
      return v ? (v.rate || 0) : 0;
    }
    return (it.qty || 0) * (it.rate || 0);
  };
  const aiContentTotal = aiContentItems.reduce((s, it) => s + _aiItemSubtotal(it), 0);

  // Special Client Discount — preset 10/15/18 or custom (0-100). Applied to enabled-modules subtotal.
  const [specialDiscountPct, setSpecialDiscountPct] = useState(0);

  // === Pre-flight Reconciliation ===
  // Periodically calls `/calculators/quotes/{id}/reconcile` to confirm the
  // sidebar's `enabledSubtotal` matches what the PDF generator's section
  // rollup will produce. Drives the green ✓ / amber ⚠ badge next to the
  // Generate Proposal button so Sales catches scope-drift before clients do.
  const [reconcile, setReconcile] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const refreshReconcile = async () => {
    if (!quoteId || quoteId === "new" || isNew) return;
    setReconcileLoading(true);
    try {
      const res = await apiClient.get(`/calculators/quotes/${quoteId}/reconcile`);
      setReconcile(res.data);
    } catch {
      setReconcile(null);
    } finally {
      setReconcileLoading(false);
    }
  };
  useEffect(() => {
    // Auto-run on quote load. Subsequent runs fire from the save success path.
    if (quoteId && quoteId !== "new" && !isNew) refreshReconcile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, isNew]);
  
  // Collapsible sections
  // For brand-new proposals every module starts collapsed (matches the "blank slate" UX).
  // For existing/loaded quotes, expand modules that have data so the rep sees their work immediately.
  const [expandedSections, setExpandedSections] = useState({
    // Project & VEXOS Setup card now hosts Project Intake fields at the top —
    // default it to expanded so a fresh quote shows the intake fields immediately
    // (otherwise users would have to click to reveal Name/Client/Salesperson).
    vexos: true,
    visualization: false,
    conceptModeling: false,
    marketing: false,
    collateral: false,
    aiContent: false,
    lotExplorer: false,
    addons: false
  });

  // Apply opinionated template defaults when launching a fresh quote with ?template=
  useEffect(() => {
    if (!isNew || !templateKey) return;
    if (templateKey === "presale_condo") {
      setVexosQuoteType("building");
      setResidentialBuildings(1);
      setResidentialUnits(120);
      setUniqueFloorplates(6);
      setRepeatFloorplates(8);
      setIncludeMasterOrbit(true);
      setIncludeGeneralAreaOrbit(true);
      setInteriorRenders(8);
      setExteriorRenders(2);
      setAerialRenders(2);
      setFloorPlans(6);
      setVrResidential(prev => ({ ...prev, enabled: true, units: 4 }));
      setMarketingEnabled(true);
      setMarketingMode("pre_sale");
      setMarketingTier("t3");
      setMarketingUnits(120);
      setCollateralEnabled(true);
      setCollateralState({
        ...COLLATERAL_EMPTY_STATE,
        toggles: { ...COLLATERAL_EMPTY_STATE.toggles, landing_page: true, branding: true, flipbook: true },
      });
      setNotes("Pre-Sale Condo Launch — full go-to-market stack.");
    } else if (templateKey === "townhome") {
      setVexosQuoteType("sfh");
      setSfhInventory(20);
      setInteriorRenders(6);
      setExteriorRenders(4);
      setAerialRenders(1);
      setFloorPlans(4);
      setMarketingEnabled(true);
      setMarketingMode("pre_sale");
      setMarketingTier("t3");
      setMarketingUnits(20);
      setCollateralEnabled(true);
      setCollateralState({
        ...COLLATERAL_EMPTY_STATE,
        toggles: { ...COLLATERAL_EMPTY_STATE.toggles, landing_page: true, flipbook: true },
      });
      setNotes("Townhome Development Package — streamlined marketing scope.");
    } else if (templateKey === "investor_pitch") {
      setVexosQuoteType("building");
      setResidentialBuildings(1);
      setExteriorRenders(4);
      setAerialRenders(4);
      setInteriorRenders(2);
      setCollateralEnabled(true);
      setCollateralState({
        ...COLLATERAL_EMPTY_STATE,
        toggles: { ...COLLATERAL_EMPTY_STATE.toggles, branding: true, investment_presentation: true, flipbook: true },
      });
      setNotes("Investor Pitch Package — capital-raise materials.");
    } else if (templateKey === "luxury_spec") {
      setVexosQuoteType("sfh");
      setSfhInventory(1);
      setInteriorRenders(8);
      setExteriorRenders(4);
      setAerialRenders(2);
      setAnimationEnabled(true);
      setSocialMediaPack(true);
      setCollateralEnabled(true);
      setCollateralState({
        ...COLLATERAL_EMPTY_STATE,
        aiRetainerTier: "6",
        retainerMonths: 6,
        toggles: { ...COLLATERAL_EMPTY_STATE.toggles, landing_page: true, branding: true },
      });
      setNotes("Luxury Spec Home Marketing — flagship visual assets.");
    } else if (templateKey === "ai_content") {
      // AI Content Studio quick-start — auto-enable module + populate starter bundle.
      setAiContentModuleEnabled(true);
      setAiContentItems(prev => prev.map(d => {
        if (d.id === "ai_motion_video") return { ...d, qty: 1 };
        if (d.id === "ai_reel_bundle") return { ...d, qty: 1 };
        if (d.id === "social_media_clips") return { ...d, qty: 1 };
        return d;
      }));
      setExpandedSections(prev => ({ ...prev, aiContent: true }));
      setNotes("AI Content Studio — AI-driven reels & social bundle.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, templateKey]);


  useEffect(() => {
    async function loadData() {
      try {
        const [vexosRes, vizRes, inventoryRes, tiersRes, termsRes] = await Promise.all([
          apiClient.get("/calculators/pricing-rules?calculator_type=vexos"),
          apiClient.get("/calculators/pricing-rules?calculator_type=visualization"),
          apiClient.get("/calculators/inventory-tiers").catch(() => ({ data: [] })),
          apiClient.get("/calculators/rendering-tiers").catch(() => ({ data: { interior: [], exterior: [] } })),
          apiClient.get("/calculators/payment-terms").catch(() => ({ data: { terms: [] } }))
        ]);
        const allRules = [...vexosRes.data, ...vizRes.data];
        setPricingRules(allRules);
        setRenderingTiers(tiersRes.data || { interior: [], exterior: [] });
        const terms = (termsRes.data?.terms || []).filter(t => t.active !== false);
        setPaymentTerms(terms);
        // Apply default term if the current one isn't in the list (only on fresh quotes)
        if (isNew && terms.length > 0) {
          const defaultTerm = terms.find(t => t.is_default) || terms.find(t => !t.is_extended) || terms[0];
          if (defaultTerm && !terms.some(t => t.months === vexosPaymentTermMonths)) {
            setVexosPaymentTermMonths(defaultTerm.months);
          }
        }
        // Pull live collateral pricing (admin-managed)
        apiClient.get("/calculators/marketing-pricing").then(r => {
          if (r.data && Object.keys(r.data).length > 0) {
            setCollateralPrices(prev => ({ ...prev, ...r.data }));
          }
        }).catch(() => {});
        
        // Load inventory tiers from backend
        if (inventoryRes.data?.length > 0) {
          const backendTiers = inventoryRes.data
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map(r => ({
              min: r.tier_config?.min_units ?? 0,
              max: r.tier_config?.max_units === 999999999 ? Infinity : (r.tier_config?.max_units ?? Infinity),
              rate: r.default_rate,
              label: r.label,
            }));
          setInventoryTiers(backendTiers);
        }
        
        // Load dynamic complexity tiers from pricing rules
        const complexityFromRules = allRules
          .filter(r => r.category === "complexity" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase().split(' ')[0],
            label: r.label.split(' (')[0], // Extract just "Small", "Medium", etc.
            description: r.description,
            rate: r.default_rate
          }));
        if (complexityFromRules.length > 0) {
          setComplexityTiers(complexityFromRules);
        }
        
        // Load dynamic site environments from pricing rules
        const siteEnvFromRules = allRules
          .filter(r => r.category === "site_environment" && r.active)
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(r => ({
            value: r.tier_config?.tier_value || r.label.toLowerCase(),
            label: r.label,
            description: r.description,
            rate: r.default_rate
          }));
        if (siteEnvFromRules.length > 0) {
          setSiteEnvironments(siteEnvFromRules);
        }
        
        const interiorModelRate = allRules.find(r => r.label.toLowerCase().includes("interior modeling"))?.default_rate || 0.85;
        void interiorModelRate; // legacy flat rate no longer used — tiered rate applied via INTERIOR_MODELING_TIERS
        // Fetch admin-editable interior modeling tiers
        await refetchInteriorTiers();
        
        // Load amenity interior pricing from rules
        const amenityRules = allRules.filter(r => r.category === "amenity_interior" && r.active);
        const amenityBaseRule = amenityRules.find(r => r.tier_config?.type === "amenity_base_rate");
        const amenityMinRule = amenityRules.find(r => r.tier_config?.type === "amenity_minimum");
        if (amenityBaseRule) setAmenityBaseRate(amenityBaseRule.default_rate);
        if (amenityMinRule) setAmenityMinPrice(amenityMinRule.default_rate);
        const complexityRulesAmenity = amenityRules.filter(r => r.tier_config?.type === "amenity_complexity");
        if (complexityRulesAmenity.length > 0) {
          const mults = {};
          complexityRulesAmenity.forEach(r => { mults[r.tier_config.tier_value] = r.default_rate; });
          setAmenityComplexityMultipliers(prev => ({ ...prev, ...mults }));
        }
        
        // Get additional point rate from rules
        const pointRule = allRules.find(r => r.label.toLowerCase().includes("additional point"));
        if (pointRule) setAdditionalPointRate(pointRule.default_rate);
        
        // Load Suborbit price from rules. The rule's label was renamed
        // from "Master Orbit" to "Suborbit"; match both so a DB that hasn't
        // been re-seeded still resolves.
        const masterOrbitRule = allRules.find(r => 
          r.category === "master_orbit" ||
          r.label.toLowerCase().includes("suborbit") ||
          r.label.toLowerCase().includes("master orbit")
        );
        if (masterOrbitRule) setMasterOrbitPrice(masterOrbitRule.default_rate);
        
        // Load General Area Orbit price from rules
        const generalAreaOrbitRule = allRules.find(r => 
          r.label.toLowerCase().includes("general area orbit") || 
          r.category === "general_area_orbit"
        );
        if (generalAreaOrbitRule) setGeneralAreaOrbitPrice(generalAreaOrbitRule.default_rate);
        
        // Load Additional Orbit price from rules
        const additionalOrbitRule = allRules.find(r => 
          r.label.toLowerCase().includes("additional orbit") || 
          r.category === "additional_orbit"
        );
        if (additionalOrbitRule) setAdditionalOrbitPrice(additionalOrbitRule.default_rate);

        // Load Reality Capture add-on rate from pricing admin
        const realityRule = allRules.find(r =>
          r.label.toLowerCase().includes("reality capture")
        );
        if (realityRule) setRealityCaptureRate(realityRule.default_rate);

        // Load Floor Plan rate from pricing admin
        const floorPlanRule = allRules.find(r =>
          r.label.toLowerCase().includes("floor plan") && r.calculator_type === "visualization"
        );
        if (floorPlanRule) setFloorPlanRate(floorPlanRule.default_rate);
        
        // Load Customizer pricing from rules
        const customizerBaseRule = allRules.find(r => r.category === "customizer_base");
        const customizerTier1Rule = allRules.find(r => r.category === "customizer_option_tier1");
        const customizerTier2Rule = allRules.find(r => r.category === "customizer_option_tier2");
        const customizerTier3Rule = allRules.find(r => r.category === "customizer_option_tier3plus");
        // Furniture package tiers
        const furnitureTier1Rule = allRules.find(r => r.category === "customizer_furniture_tier1");
        const furnitureTier2Rule = allRules.find(r => r.category === "customizer_furniture_tier2");
        const furnitureTier3Rule = allRules.find(r => r.category === "customizer_furniture_tier3");
        const furnitureTier4Rule = allRules.find(r => r.category === "customizer_furniture_tier4plus");
        
        setCustomizerPricing({
          baseFee: customizerBaseRule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.baseFee,
          optionPriceTier1: customizerTier1Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier1,
          optionPriceTier2: customizerTier2Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier2,
          optionPriceTier3Plus: customizerTier3Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.optionPriceTier3Plus,
          furnitureTier1: furnitureTier1Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier1,
          furnitureTier2: furnitureTier2Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier2,
          furnitureTier3: furnitureTier3Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier3,
          furnitureTier4Plus: furnitureTier4Rule?.default_rate || DEFAULT_CUSTOMIZER_PRICING.furnitureTier4Plus
        });
        
        if (!isNew) {
          const quoteRes = await apiClient.get(`/calculators/quotes/${quoteId}`);
          const quote = quoteRes.data;
          
          setProjectName(quote.project_name || "");
          setClientName(quote.client_name || "");
          setSalesperson(quote.salesperson || "");
          setNotes(quote.notes || "");

          // Restore Project Intake metadata (lot/floorplan/units/development type).
          // Read from project_metadata first (canonical); fall back to top-level
          // for legacy quotes that may have stored these at the root.
          const _pm = quote.project_metadata || {};
          setLotSqFt(_pm.lot_sq_ft != null ? String(_pm.lot_sq_ft) : (quote.lot_sq_ft != null ? String(quote.lot_sq_ft) : ""));
          setUniqueFloorplanCount(_pm.unique_floorplan_count != null ? String(_pm.unique_floorplan_count) : "");
          setAvgFloorplanSqFt(_pm.avg_floorplan_sq_ft != null ? String(_pm.avg_floorplan_sq_ft) : "");
          setUnitCountIntake(_pm.unit_count != null ? String(_pm.unit_count) : "");
          setDevelopmentType(_pm.development_type || "");

          // Restore manual timeline override (top-level or nested in payment_plan)
          const _ovWeeks = quote.timeline_override_weeks ?? quote.payment_plan?.timeline_override_weeks;
          if (_ovWeeks != null && _ovWeeks !== "" && Number(_ovWeeks) > 0) {
            setTimelineOverrideEnabled(true);
            setTimelineOverrideWeeks(String(_ovWeeks));
          }

          // Restore V1 delivery week override
          const _v1Override = quote.payment_plan?.viz_v1_delivery_week_override;
          if (_v1Override != null && _v1Override !== "" && Number(_v1Override) > 0) {
            setVizV1DeliveryWeekOverride(String(_v1Override));
          }

          // Restore module toggles + special discount (default OFF/0 if unset).
          // For legacy quotes lacking module_state, auto-detect from saved sections.
          const _ms = quote.payment_plan?.module_state || quote.module_state || {};
          let _vexosEnabled = false, _vizEnabled = false;
          if (typeof _ms.vexos_enabled === "boolean") {
            setVexosModuleEnabled(_ms.vexos_enabled);
            _vexosEnabled = _ms.vexos_enabled;
          } else if (Array.isArray(quote.sections)) {
            const hasVexos = quote.sections.some(s => (s.section_type || "").startsWith("vexos_"));
            if (hasVexos) { setVexosModuleEnabled(true); _vexosEnabled = true; }
          }
          if (typeof _ms.viz_enabled === "boolean") {
            setVizModuleEnabled(_ms.viz_enabled);
            _vizEnabled = _ms.viz_enabled;
          } else if (Array.isArray(quote.sections)) {
            const hasViz = quote.sections.some(s => (s.section_type || "").startsWith("viz_"));
            if (hasViz) { setVizModuleEnabled(true); _vizEnabled = true; }
          }
          // AI Content Studio — restore toggle + deliverable quantities.
          let _aiEnabled = false;
          if (typeof _ms.ai_content_enabled === "boolean") {
            setAiContentModuleEnabled(_ms.ai_content_enabled);
            _aiEnabled = _ms.ai_content_enabled;
          } else if (Array.isArray(quote.sections)) {
            const hasAi = quote.sections.some(s => (s.section_type || "") === "ai_content_studio");
            if (hasAi) { setAiContentModuleEnabled(true); _aiEnabled = true; }
          }
          // VEXOS Lot Explorer™ — restore toggle + inputs from saved section
          let _lotExplorerEnabled = false;
          if (typeof _ms.lot_explorer_enabled === "boolean") {
            setLotExplorerModuleEnabled(_ms.lot_explorer_enabled);
            _lotExplorerEnabled = _ms.lot_explorer_enabled;
          } else if (Array.isArray(quote.sections)) {
            const hasLE = quote.sections.some(s => (s.section_type || "") === "interactive_2d_sitemap");
            if (hasLE) { setLotExplorerModuleEnabled(true); _lotExplorerEnabled = true; }
          }
          if (Array.isArray(quote.sections)) {
            const _leSec = quote.sections.find(s => s.section_type === "interactive_2d_sitemap");
            if (_leSec) {
              const _leMeta = _leSec.metadata || {};
              if (typeof _leMeta.lot_count === "number") setLotExplorerLotCount(_leMeta.lot_count);
              if (typeof _leMeta.rate_per_lot === "number") setLotExplorerRatePerLot(_leMeta.rate_per_lot);
              if (typeof _leMeta.include_poi === "boolean") setLotExplorerIncludePoi(_leMeta.include_poi);
              if (typeof _leMeta.include_crm === "boolean") setLotExplorerIncludeCrm(_leMeta.include_crm);
              if (typeof _leMeta.include_media === "boolean") setLotExplorerIncludeMedia(_leMeta.include_media);
              if (typeof _leMeta.include_backend === "boolean") setLotExplorerIncludeBackend(_leMeta.include_backend);
              // Timeline restoration — default weeks + override state
              if (typeof _leMeta.timeline_default_weeks === "number") setLotExplorerTimelineWeeks(_leMeta.timeline_default_weeks);
              else if (typeof _leMeta.timeline_weeks === "number" && !_leMeta.timeline_is_overridden) setLotExplorerTimelineWeeks(_leMeta.timeline_weeks);
              if (_leMeta.timeline_is_overridden) {
                setLotExplorerTimelineOverrideEnabled(true);
                if (typeof _leMeta.timeline_weeks === "number") setLotExplorerTimelineOverrideWeeks(String(_leMeta.timeline_weeks));
              }
              // Hosting / Management / Support — restore monthly + lock-in config
              if (typeof _leMeta.hosting_monthly === "number") setLotExplorerHostingMonthly(_leMeta.hosting_monthly);
              if (typeof _leMeta.hosting_free_months === "number") setLotExplorerHostingFreeMonths(_leMeta.hosting_free_months);
              if (typeof _leMeta.hosting_lock_months === "number") setLotExplorerHostingLockMonths(_leMeta.hosting_lock_months);
            }
          }
          if (Array.isArray(quote.sections)) {
            const _aiSec = quote.sections.find(s => s.section_type === "ai_content_studio");
            if (_aiSec && Array.isArray(_aiSec.line_items)) {
              setAiContentItems(prev => prev.map(d => {
                // Motion variant — find a saved line item whose label starts with the deliverable label
                // and pull the variant id out of the line_item.metadata (or infer from the suffix).
                if (d.isMotionVariant) {
                  const match = _aiSec.line_items.find(li => (li.label || "").startsWith(d.label));
                  if (!match) return { ...d, selectedVariantId: null, qty: 0 };
                  const variantId = match?.metadata?.motion_variant_id
                    || (d.variants || []).find(v => (match.label || "").includes(v.label))?.id
                    || null;
                  return { ...d, selectedVariantId: variantId, qty: variantId ? 1 : 0 };
                }
                const match = _aiSec.line_items.find(li => li.label === d.label);
                return match ? { ...d, qty: match.quantity || 0 } : d;
              }));
            }
          }
          // Auto-expand sections that have actual data on load — keeps the "collapsed by default"
          // behaviour for new proposals while showing existing work immediately when a quote is opened.
          setExpandedSections(prev => ({
            ...prev,
            vexos: _vexosEnabled || prev.vexos,
            visualization: _vizEnabled || prev.visualization,
            aiContent: _aiEnabled || prev.aiContent,
            lotExplorer: _lotExplorerEnabled || prev.lotExplorer,
          }));
          const _disc = quote.payment_plan?.special_discount_pct;
          if (typeof _disc === "number" && _disc >= 0 && _disc <= 100) setSpecialDiscountPct(_disc);

          // Restore payment plan
          if (quote.payment_plan?.preset) {
            setPaymentPreset(quote.payment_plan.preset);
            if (quote.payment_plan.preset === "custom" && quote.payment_plan.milestones) {
              setCustomMilestones(quote.payment_plan.milestones);
            }
          }
          
          // === Local hydration accumulators — collected during the section loop
          // so we can do a single REPLACE-semantics state commit after iteration.
          // This makes hydration idempotent (no doubling on re-mount) and resilient
          // to legacy DB rows that contain duplicate line items per tour.
          const _vrResAccum = { studio: [], "1bed": [], "2bed": [], "3bed": [] };
          const _vrAmenAccum = { small: [], medium: [], large: [], xl: [] };
          const _amenityCustomAccum = [];
          const _amenityEntriesAccum = [];

          quote.sections?.forEach(section => {
            const sectionType = section.section_type || "";
            const sectionMeta = section.metadata || {};

            // --- Restore vexosQuoteType from any section that stores it ---
            if (sectionMeta.vexos_quote_type === "sfh") {
              setVexosQuoteType("sfh");
            } else if (sectionMeta.vexos_quote_type === "building") {
              setVexosQuoteType("building");
            }
            
            // --- Restore floorplate enabled from platform or floorplate section ---
            if (sectionType === "vexos_platform" && sectionMeta.floorplate_enabled !== undefined) {
              setFloorplateEnabled(sectionMeta.floorplate_enabled);
            }
            if (sectionType === "vexos_floorplates") {
              setFloorplateEnabled(true);
            }
            
            // --- Restore Exterior Modeling (structures, site env, reality capture) ---
            if (sectionType === "vexos_exterior_modeling") {
              const meta = sectionMeta;
              // Support both metadata naming conventions: SFH legacy used `site_cost`
              // and `reality_capture`, Building/new-SFH use `site_environment_cost`
              // and `reality_capture_enabled`. Either correctly restores the UI.
              setSiteEnvironment(meta.site_environment || "none");
              setIncludeRealityCapture(meta.reality_capture_enabled || meta.reality_capture || false);
              if (meta.reality_capture_rate) setRealityCaptureRate(meta.reality_capture_rate);

              // Prefer raw exterior_structures array from metadata (preserves empty/zero
              // rows + custom names). Fall back to deriving from line items for legacy quotes.
              if (Array.isArray(meta.exterior_structures) && meta.exterior_structures.length > 0) {
                setExteriorStructures(meta.exterior_structures.map((s, idx) => ({
                  id: s.id || crypto.randomUUID(),
                  name: s.name ?? "",
                  complexity: s.complexity || "medium",
                  uniqueCount: s.uniqueCount || 0,
                  repeatCount: s.repeatCount || 0,
                  notes: s.notes || ""
                })));
              } else {
                // Restore structures from line items (legacy)
                const structures = [];
                section.line_items?.forEach(item => {
                  const itemMeta = item.metadata || {};
                  const isStructure = itemMeta.item_type === "structure" || item.unit_type === "structure";
                  if (isStructure) {
                    structures.push({
                      id: item.id || crypto.randomUUID(),
                      name: itemMeta.structure_name || item.label || "",
                      complexity: itemMeta.complexity_tier || itemMeta.complexity || "medium",
                      uniqueCount: itemMeta.unique_count || 0,
                      repeatCount: itemMeta.repeat_count || 0,
                      notes: itemMeta.notes || ""
                    });
                  }
                });
                if (structures.length > 0) {
                  setExteriorStructures(structures);
                }
              }
            }
            
            // --- Restore Orbit Configuration ---
            if (sectionType === "vexos_orbit_configuration") {
              const meta = sectionMeta;
              setIncludeMasterOrbit(meta.master_orbit_enabled || false);
              setIncludeGeneralAreaOrbit(meta.general_area_orbit_enabled || false);
              if (meta.master_orbit_price) setMasterOrbitPrice(meta.master_orbit_price);
              if (meta.general_area_orbit_price) setGeneralAreaOrbitPrice(meta.general_area_orbit_price);
              if (meta.additional_orbit_price) setAdditionalOrbitPrice(meta.additional_orbit_price);
              
              // Restore additional orbits from line items
              const orbits = [];
              section.line_items?.forEach(item => {
                const itemMeta = item.metadata || {};
                if (itemMeta.item_type === "additional_orbit") {
                  orbits.push({
                    id: item.id || crypto.randomUUID(),
                    type: itemMeta.orbit_type || "ph_spotlight",
                    customName: itemMeta.custom_name || "",
                    quantity: item.quantity || 1
                  });
                }
              });
              if (orbits.length > 0) {
                setAdditionalOrbits(orbits);
              }
            }
            
            // --- Restore Animation ---
            if (sectionType === "viz_animation") {
              const meta = sectionMeta;
              if (section.line_items?.length > 0) {
                setAnimationEnabled(true);
                if (meta.animation_type) setAnimationType(meta.animation_type);
                if (meta.duration_seconds) setAnimationDuration(meta.duration_seconds);
                if (meta.modeling_aligned !== null && meta.modeling_aligned !== undefined) {
                  setAnimationModelingAligned(meta.modeling_aligned);
                }
                if (meta.social_media_pack === true) setSocialMediaPack(true);
                if (section.line_items.some(li => (li.label || "").toLowerCase().includes("social media pack"))) {
                  setSocialMediaPack(true);
                }
              }
            }
            
            // --- Restore Interior Design Direction ---
            if (sectionType === "viz_interior_design_direction" && sectionMeta) {
              const meta = sectionMeta;
              if (meta.overridden_spaces !== null && meta.overridden_spaces !== undefined) {
                setInteriorDesignSpacesOverride(meta.overridden_spaces);
              }
              if (meta.spaces && Array.isArray(meta.spaces)) {
                setInteriorDesignSpaces(meta.spaces.map((s, idx) => ({
                  id: crypto.randomUUID(),
                  name: s.name || `Space ${idx + 1}`,
                  completeness_level: s.completeness_level || "full_package",
                  notes: s.notes || ""
                })));
              }
            }

            // --- Restore Conceptual Exterior Modeling ---
            if (sectionType === "concept_exterior_modeling" && sectionMeta) {
              const meta = sectionMeta;
              setConceptModelingEnabled(true);
              if (meta.approach) setConceptApproach(meta.approach);
              if (meta.buildings) setConceptBuildings(meta.buildings);
              if (meta.modification_level) setConceptModLevel(meta.modification_level);
              if (meta.complexity_level) setConceptComplexity(meta.complexity_level);
              if (meta.site_context) setConceptSiteContext(meta.site_context);
              setConceptRenderOptimization(meta.render_optimization || false);
              setConceptVexosStructuring(meta.vexos_structuring || false);
            }

            // --- Restore Interior Modeling raw UI state from section metadata ---
            // (Persisted regardless of cost so empty/zero-cost amenities survive a reload.)
            if (sectionType === "viz_interior_modeling" && sectionMeta) {
              const m = sectionMeta;
              if (typeof m.interior_sqft === "number") setInteriorSqft(m.interior_sqft);
              if (Array.isArray(m.amenity_entries) && m.amenity_entries.length > 0) {
                setAmenityEntries(m.amenity_entries.map((a, idx) => ({
                  id: a.id || `am-${Date.now()}-${idx}`,
                  name: a.name || "",
                  sqft: a.sqft || 0,
                  complexity: a.complexity || "standard",
                  modelingType: a.modelingType || "interior_existing_structure",
                  structureSize: a.structureSize || "medium",
                  sharedGroupId: a.sharedGroupId || ""
                })));
              }
            }

            // --- Restore Collateral Suite ---
            if (sectionType === "marketing_collateral" && sectionMeta) {
              const m = sectionMeta;
              setCollateralEnabled(true);
              setCollateralState({
                toggles: m.infra_toggles || COLLATERAL_EMPTY_STATE.toggles,
                billboardOption: m.billboard_option || "none",
                aiRetainerTier: m.ai_retainer_tier || "none",
                aiEnhanceQty: m.ai_enhance_qty ?? 3,
                retainerMonths: m.retainer_months ?? 6,
              });
            }


            // --- Restore Marketing Strategy ---
            if (sectionType === "marketing_strategy" && sectionMeta) {
              const meta = sectionMeta;
              setMarketingEnabled(true);
              if (meta.mode) setMarketingMode(meta.mode);
              if (meta.tier_label) {
                const allTiers = meta.mode === "pre_lease"
                  ? [{ id: "t1", label: "below $1,500" }, { id: "t2", label: "$1,500 - $2,500" }, { id: "t3", label: "$2,500 - $4,500" }, { id: "t4", label: "$4,500+" }]
                  : [{ id: "t1", label: "below $600K" }, { id: "t2", label: "$600K - $1.2M" }, { id: "t3", label: "$1.2M - $2.5M" }, { id: "t4", label: "$2.5M+" }];
                const matched = allTiers.find(t => t.label === meta.tier_label);
                if (matched) setMarketingTier(matched.id);
              }
              if (meta.units) setMarketingUnits(meta.units);
              if (meta.timeline) {
                // Clamp to new 6–60 range + snap to nearest 3-month step
                // so legacy quotes (saved with min=3) auto-correct.
                const raw = Number(meta.timeline) || 12;
                const clamped = Math.max(6, Math.min(60, raw));
                const snapped = 6 + Math.round((clamped - 6) / 3) * 3;
                setMarketingTimeline(snapped);
              }
              if (meta.conversion_rate) setMarketingConvRate(meta.conversion_rate);
              if (meta.ai_animated_enabled) { setMktgAiAnimatedEnabled(true); setMktgAiAnimatedQty(meta.ai_animated_qty || 3); }
              if (meta.ai_enhance_enabled) { setMktgAiEnhanceEnabled(true); setMktgAiEnhanceQty(meta.ai_enhance_qty || 3); }
              if (meta.content_mode) setMktgContentMode(meta.content_mode);
              if (meta.selected_package) setMktgSelectedPackage(meta.selected_package);
              if (meta.retainer_enabled) setMktgRetainerEnabled(true);
            }
            
            // --- Restore simple line-item fields via label matching ---
            // Skip amenity restoration here if the section already had raw amenity_entries
            // metadata — that path is the canonical hydration source above.
            const _vizMetaHasAmenities = sectionType === "viz_interior_modeling" && Array.isArray(sectionMeta?.amenity_entries) && sectionMeta.amenity_entries.length > 0;

            // --- Restore Residential Buildings from vexos_platform section metadata ---
            // Persisted via metadata.residential_buildings so single-building quotes
            // (which don't emit an "Additional Buildings" line item) survive a reload.
            if (sectionType === "vexos_platform" && typeof sectionMeta?.residential_buildings === "number") {
              setResidentialBuildings(sectionMeta.residential_buildings);
            }

            section.line_items?.forEach(item => {
              const meta = item.metadata || {};
              // Legacy: "Residential Building" line item — only used by very old quotes.
              if (item.label.includes("Residential Building")) setResidentialBuildings(item.quantity || 1);
              // New format: "Additional Buildings" qty = (residentialBuildings - 1).
              // Reconstruct the original count so legacy quotes (without metadata.residential_buildings) still hydrate.
              else if (item.label.includes("Additional Building")) {
                setResidentialBuildings(prev => prev || ((item.quantity || 0) + 1));
              }
              else if (item.label.includes("Amenity Building")) setAmenityBuildings(item.quantity || 0);
              else if (item.label.includes("Floors Covered")) {
                setFloorsCovered(meta.floors_covered || 10);
                setCoverageNotes(meta.coverage_notes || "");
              }
              else if (item.label.includes("Inventory")) setResidentialUnits(item.quantity || 100);
              else if (item.label.includes("Unique")) setUniqueFloorplates(item.quantity || 5);
              else if (item.label.includes("Repeat")) setRepeatFloorplates(item.quantity || 5);
              else if (item.label.includes("License")) {
                setVexosPaymentTermMonths(meta.vexos_payment_term_months || meta.term_months || 12);
                if (meta.vexos_upfront_percent) setVexosUpfrontPercent(meta.vexos_upfront_percent);
                if (meta.vexos_upfront_mode || meta.upfront_mode) setVexosUpfrontMode(meta.vexos_upfront_mode || meta.upfront_mode || "percent");
                if (meta.vexos_upfront_fixed || meta.upfront_fixed) setVexosUpfrontFixed(meta.vexos_upfront_fixed || meta.upfront_fixed || 0);
                if (meta.vexos_payment_term_months) setVexosPaymentTermMonths(meta.vexos_payment_term_months);
                if (meta.project_size) {
                  setProjectSize(meta.project_size);
                } else {
                  if (item.rate === 1500) setProjectSize("small");
                  else if (item.rate === 3000) setProjectSize("medium");
                  else setProjectSize("large");
                }
              }
              else if (item.label.includes("Interior Modeling") && !item.label.includes("Amenity")) {
                setInteriorSqft(meta.sqft || item.quantity || 0);
              }
              else if (item.label.includes("Amenity") && (item.unit_type === "amenity_sqft" || meta.sqft)) {
                if (_vizMetaHasAmenities) return;  // already hydrated from section metadata
                // New sqft-based amenity format — accumulate into local map for replace-semantics
                _amenityEntriesAccum.push({
                  id: `am-${Date.now()}-${_amenityEntriesAccum.length}`,
                  name: item.label.replace(" Interior", "").replace(" + Structure", ""),
                  sqft: meta.sqft || 0,
                  complexity: meta.complexity || "standard",
                  modelingType: meta.modelingType || "interior_existing_structure",
                  structureSize: meta.structureSize || "medium",
                  sharedGroupId: meta.sharedGroupId || ""
                });
              }
              else if (item.label.includes("Amenity Modeling")) {
                if (_vizMetaHasAmenities) return;  // already hydrated from section metadata
                // Legacy flat-rate format — convert to single entry
                const count = item.quantity || 1;
                for (let i = 0; i < count; i++) {
                  _amenityEntriesAccum.push({
                    id: `am-legacy-${i}`, name: `Amenity ${i + 1}`, sqft: 2000, complexity: "standard",
                    modelingType: "interior_existing_structure", structureSize: "medium", sharedGroupId: ""
                  });
                }
              }
              else if (item.label.includes("Interior Render")) setInteriorRenders(item.quantity || 0);
              else if (item.label.includes("Exterior Render")) setExteriorRenders(item.quantity || 0);
              else if (item.label.includes("Aerial")) {
                setAerialRenders(item.quantity || 0);
                setAerialRate(item.rate || 6000);
              }
              else if (item.label.includes("Floor Plan")) {
                setFloorPlans(item.quantity || 0);
                setFloorPlanRate(item.rate || 1000);
              }
              // VR Tour restoration - Residential (per-tour array model)
              // Accumulate into local map for replace-semantics; dedupe by tour_id.
              else if (meta.category === "residential" || (item.label.includes("VR Tour") && !item.label.includes("Amenity"))) {
                const tourType = meta.tour_type ||
                  (item.label.includes("Studio") || item.label.includes("4 Point") || item.label.includes("4-Point") ? "studio" :
                   item.label.includes("1 Bed") || item.label.includes("6 Point") || item.label.includes("6-Point") ? "1bed" :
                   item.label.includes("2 Bed") || item.label.includes("8 Point") || item.label.includes("8-Point") ? "2bed" :
                   item.label.includes("3") || item.label.includes("10 Point") || item.label.includes("10-Point") ? "3bed" : null);
                if (tourType && _vrResAccum[tourType]) {
                  const isLegacy = !meta.tour_id && (item.quantity || 1) > 1;
                  const expandCount = isLegacy ? (item.quantity || 1) : 1;
                  const additionalPts = meta.additional_points || 0;
                  const tourName = meta.tour_name || (isLegacy ? "" : (item.label.replace(/^VR Tour\s*—\s*/, '') !== meta.tier_label ? item.label.replace(/^VR Tour\s*—\s*/, '') : ""));
                  for (let i = 0; i < expandCount; i++) {
                    // Dedupe by tour_id within the same tier — protects against
                    // corrupted DB rows with duplicate line items for the same tour.
                    const tid = meta.tour_id;
                    if (tid && _vrResAccum[tourType].some(t => t.id === tid)) continue;
                    _vrResAccum[tourType].push({
                      id: tid || crypto.randomUUID(),
                      name: expandCount > 1 ? "" : (tourName || ""),
                      additionalPoints: additionalPts
                    });
                  }
                }
              }
              // VR Tour restoration - Amenity (per-tour array model)
              else if (meta.category === "amenity") {
                const tourType = meta.tour_type;
                if (tourType === "custom") {
                  const cid = meta.custom_space_id;
                  if (!cid || !_amenityCustomAccum.some(t => t.id === cid)) {
                    _amenityCustomAccum.push({
                      id: cid || crypto.randomUUID(),
                      name: meta.custom_name || item.label.replace(/^Amenity VR Tour\s*—\s*/, ''),
                      points: meta.final_points || meta.included_points || 1
                    });
                  }
                  if (meta.modeling_confirmed) setAmenityCustomConfirmed(true);
                  setAmenityCustomExpanded(true);
                } else if (tourType && _vrAmenAccum[tourType]) {
                  const isLegacy = !meta.tour_id && (item.quantity || 1) > 1;
                  const expandCount = isLegacy ? (item.quantity || 1) : 1;
                  const additionalPts = meta.additional_points || 0;
                  const tourName = meta.tour_name || (isLegacy ? "" : (item.label.replace(/^Amenity VR Tour\s*—\s*/, '') !== meta.tier_label ? item.label.replace(/^Amenity VR Tour\s*—\s*/, '') : ""));
                  for (let i = 0; i < expandCount; i++) {
                    const tid = meta.tour_id;
                    if (tid && _vrAmenAccum[tourType].some(t => t.id === tid)) continue;
                    _vrAmenAccum[tourType].push({
                      id: tid || crypto.randomUUID(),
                      name: expandCount > 1 ? "" : (tourName || ""),
                      additionalPoints: additionalPts
                    });
                  }
                }
              }
            });
          });

          // === REPLACE-semantics commit: write the accumulated VR/amenity state ONCE
          // (instead of progressive appends). This makes hydration idempotent and
          // resilient to legacy DB rows that contain duplicate line items per tour.
          setVrResidential(_vrResAccum);
          setVrAmenity(_vrAmenAccum);
          if (_amenityCustomAccum.length > 0) setAmenityCustomTours(_amenityCustomAccum);
          if (_amenityEntriesAccum.length > 0) setAmenityEntries(_amenityEntriesAccum);
        }
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [quoteId, isNew]);

  function getRate(label, type = null) {
    const filtered = type ? pricingRules.filter(r => r.calculator_type === type) : pricingRules;
    const rule = filtered.find(r => r.label.toLowerCase().includes(label.toLowerCase()));
    return rule?.default_rate || DEFAULT_VR_RATES[label] || 0;
  }

  // === STRUCTURED PRICING-ADMIN LOOKUP ===========================================
  // Pricing Admin rows for VR have category + tier_config (NOT label-string match).
  // These helpers replace the legacy substring-on-label lookup that silently
  // fell through to hardcoded DEFAULT_VR_RATES whenever the key contained an
  // underscore (e.g. "amenity_small" vs label "Amenity VR Tour - Small").
  //
  // Lookup contract:
  //   category="vr_amenity",    tier_config.amenity_size ∈ {small, medium, large, xl}
  //   category="vr_residential", tier_config.unit_type   ∈ {studio, 1bed, 2bed, 3bed}
  //   category="vr_addon",       label="VR Additional Point"
  //
  // On miss: console.error + fall back to DEFAULT_VR_RATES with a visible
  // warning so it never silently desyncs from Pricing Admin again.
  function getAmenityVrRate(amenitySize) {
    const rule = (pricingRules || []).find(r =>
      r.category === "vr_amenity" &&
      r.calculator_type === "visualization" &&
      r.tier_config?.amenity_size === amenitySize
    );
    if (rule && typeof rule.default_rate === "number") return rule.default_rate;
    const fallbackKey = `amenity_${amenitySize}`;
    if (pricingRules && pricingRules.length > 0) {
      // Only warn AFTER pricing rules have loaded — avoids noisy errors during initial mount.
      // eslint-disable-next-line no-console
      console.error(
        `[Pricing] Missing vr_amenity rule for amenity_size="${amenitySize}". ` +
        `Falling back to DEFAULT_VR_RATES.${fallbackKey} = $${DEFAULT_VR_RATES[fallbackKey]}. ` +
        `Add or activate this rule under Pricing Admin → VR_AMENITY.`
      );
    }
    return DEFAULT_VR_RATES[fallbackKey] || 0;
  }

  function getResidentialVrRate(unitTypeKey) {
    const rule = (pricingRules || []).find(r =>
      r.category === "vr_residential" &&
      r.calculator_type === "visualization" &&
      r.tier_config?.unit_type === unitTypeKey
    );
    if (rule && typeof rule.default_rate === "number") return rule.default_rate;
    // Legacy fallback by rateKey (matches DEFAULT_VR_RATES keys like "studio", "1 bedroom")
    const legacyKeyMap = { studio: "studio", "1bed": "1 bedroom", "2bed": "2 bedroom", "3bed": "3 bedroom" };
    const legacyKey = legacyKeyMap[unitTypeKey] || unitTypeKey;
    if (pricingRules && pricingRules.length > 0) {
      // eslint-disable-next-line no-console
      console.error(
        `[Pricing] Missing vr_residential rule for unit_type="${unitTypeKey}". ` +
        `Falling back to DEFAULT_VR_RATES["${legacyKey}"] = $${DEFAULT_VR_RATES[legacyKey]}. ` +
        `Add or activate this rule under Pricing Admin → VR_RESIDENTIAL.`
      );
    }
    return DEFAULT_VR_RATES[legacyKey] || 0;
  }

  // === VIDEO ANIMATION ADMIN-EDITABLE LOOKUP ====================================
  // Pulls price/shots from Pricing Admin (category="video_animation" + tier_config
  // {motion_type, duration_seconds}). Falls back to the hardcoded pricing in
  // videoAnimationPricing.js so legacy quotes / first-load (before rules
  // arrive) never break. On a Pricing-Admin miss, console.errors the same way
  // VR amenity does — surfaces drift loudly.
  function getVideoAnimationRule(motionType, duration) {
    return (pricingRules || []).find(r =>
      r.category === "video_animation" &&
      r.calculator_type === "visualization" &&
      r.tier_config?.motion_type === motionType &&
      Number(r.tier_config?.duration_seconds) === Number(duration)
    );
  }

  function getVideoAnimationRate(motionType, duration) {
    const rule = getVideoAnimationRule(motionType, duration);
    if (rule && typeof rule.default_rate === "number") return rule.default_rate;
    const fallback = getAnimationPrice(motionType, duration);
    if (pricingRules && pricingRules.length > 0) {
      // eslint-disable-next-line no-console
      console.error(
        `[Pricing] Missing video_animation rule for motion_type="${motionType}", duration=${duration}s. ` +
        `Falling back to hardcoded $${fallback}. Add or activate this rule under Pricing Admin → Visualization → Video Animation.`
      );
    }
    return fallback;
  }

  // === VEXOS PLATFORM SETUP TIERED LOOKUP =======================================
  // Replaces the old flat $25,000 "Base Platform Setup" and the hardcoded
  // SFH_BASE_PLATFORM_FEE = $10,000. The fee is now driven by total dwelling
  // units (residential building units + SFH homes; amenities don't count).
  //
  // Pricing Admin rows (category="platform", calculator_type="vexos") expose
  // tier_config = {min_units, max_units} where max_units=null means "no upper bound".
  //
  // Hardcoded fallback mirrors the canonical tiers in case pricing rules
  // haven't loaded yet OR a tier was accidentally deactivated.
  const VEXOS_PLATFORM_SETUP_FALLBACK_TIERS = [
    { min: 0,   max: 99,   fee: 10000 },
    { min: 100, max: 300,  fee: 15000 },
    { min: 301, max: null, fee: 20000 },
  ];
  function getVexosPlatformSetupFee(totalUnits) {
    const units = Math.max(0, Number(totalUnits) || 0);
    const rules = (pricingRules || []).filter(r =>
      r.category === "platform" &&
      r.calculator_type === "vexos" &&
      r.active !== false &&
      r.tier_config &&
      typeof r.tier_config.min_units === "number" &&
      // Disambiguate from the new monthly-fee tiers (same category + min_units).
      // Legacy setup rows lack fee_type — treat them as setup by default.
      (r.tier_config.fee_type || "setup") === "setup"
    );
    const match = rules.find(r => {
      const min = r.tier_config.min_units;
      const max = r.tier_config.max_units;
      return units >= min && (max === null || max === undefined || units <= max);
    });
    if (match && typeof match.default_rate === "number") return match.default_rate;
    const fallback = VEXOS_PLATFORM_SETUP_FALLBACK_TIERS.find(t =>
      units >= t.min && (t.max === null || units <= t.max)
    );
    if (pricingRules && pricingRules.length > 0 && !match) {
      // eslint-disable-next-line no-console
      console.error(
        `[Pricing] Missing platform setup tier for ${units} units. Falling back to ` +
        `hardcoded $${fallback ? fallback.fee : 0}. Add or activate the matching tier ` +
        `under Pricing Admin → VEXOS → Platform.`
      );
    }
    return fallback ? fallback.fee : 0;
  }

  // === VEXOS MONTHLY PLATFORM FEE TIERED LOOKUP =================================
  // Recurring SaaS-style platform/hosting/support fee. Fixed by unit tier — NOT
  // financing, NOT calculated against the setup-fee balance.
  //   - 0–99 units      = $2,500 / month
  //   - 100–300 units   = $5,000 / month
  //   - 301+ units      = $7,500 / month
  const VEXOS_MONTHLY_FEE_FALLBACK_TIERS = [
    { min: 0,   max: 99,   fee: 2500 },
    { min: 100, max: 300,  fee: 5000 },
    { min: 301, max: null, fee: 7500 },
  ];
  function getVexosMonthlyFee(totalUnits) {
    const units = Math.max(0, Number(totalUnits) || 0);
    const rules = (pricingRules || []).filter(r =>
      r.category === "platform" &&
      r.calculator_type === "vexos" &&
      r.active !== false &&
      r.tier_config &&
      typeof r.tier_config.min_units === "number" &&
      r.tier_config.fee_type === "monthly"
    );
    const match = rules.find(r => {
      const min = r.tier_config.min_units;
      const max = r.tier_config.max_units;
      return units >= min && (max === null || max === undefined || units <= max);
    });
    if (match && typeof match.default_rate === "number") return match.default_rate;
    const fallback = VEXOS_MONTHLY_FEE_FALLBACK_TIERS.find(t =>
      units >= t.min && (t.max === null || units <= t.max)
    );
    if (pricingRules && pricingRules.length > 0 && !match) {
      // eslint-disable-next-line no-console
      console.error(
        `[Pricing] Missing monthly platform fee tier for ${units} units. Falling back to ` +
        `hardcoded $${fallback ? fallback.fee : 0}. Add or activate the matching tier ` +
        `under Pricing Admin → VEXOS → Platform (fee_type=monthly).`
      );
    }
    return fallback ? fallback.fee : 0;
  }

  // Get structure tier rate for exterior modeling - uses complexity category from pricing rules
  function getStructureRate(complexity) {
    // First try to find from the new complexity category pricing rules
    const complexityRules = pricingRules.filter(r => r.category === "complexity");
    if (complexityRules.length > 0) {
      const rule = complexityRules.find(r => 
        r.tier_config?.tier_value === complexity || 
        r.label.toLowerCase().includes(complexity.toLowerCase())
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      small: getRate("structure - small", "vexos") || DEFAULT_EXT_RATES.small,
      medium: getRate("structure - medium", "vexos") || DEFAULT_EXT_RATES.medium,
      large: getRate("structure - large", "vexos") || DEFAULT_EXT_RATES.large,
      xl: getRate("structure - xl", "vexos") || DEFAULT_EXT_RATES.xl
    };
    return rateMap[complexity] || DEFAULT_EXT_RATES.medium;
  }

  // Get site environment rate - uses site_environment category from pricing rules
  function getSiteRate(env) {
    if (env === "none") return 0;
    
    // First try to find from the new site_environment category pricing rules
    const siteRules = pricingRules.filter(r => r.category === "site_environment");
    if (siteRules.length > 0) {
      const rule = siteRules.find(r => 
        r.tier_config?.tier_value === env || 
        r.label.toLowerCase() === env.toLowerCase()
      );
      if (rule) return rule.default_rate;
    }
    
    // Fallback to old label-based lookup
    const rateMap = {
      simple: getRate("site environment - simple", "vexos") || DEFAULT_EXT_RATES.site_simple,
      standard: getRate("site environment - standard", "vexos") || DEFAULT_EXT_RATES.site_standard,
      complex: getRate("site environment - complex", "vexos") || DEFAULT_EXT_RATES.site_complex,
      masterplan: getRate("site environment - masterplan", "vexos") || DEFAULT_EXT_RATES.site_masterplan
    };
    return rateMap[env] || 0;
  }

  // Get replication rate
  function getReplicationRate() {
    return getRate("replication rate", "vexos") || DEFAULT_EXT_RATES.replication;
  }

  // Structure management functions
  function addStructure() {
    setExteriorStructures([...exteriorStructures, createEmptyStructure()]);
  }

  function updateStructure(id, field, value) {
    setExteriorStructures(exteriorStructures.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    ));
  }

  function removeStructure(id) {
    if (exteriorStructures.length > 1) {
      setExteriorStructures(exteriorStructures.filter(s => s.id !== id));
    }
  }
  
  // Additional orbit management functions
  function addAdditionalOrbit() {
    setAdditionalOrbits([...additionalOrbits, {
      id: crypto.randomUUID(),
      type: "ph_spotlight",
      customName: "",
      quantity: 1
    }]);
  }
  
  function updateAdditionalOrbit(id, field, value) {
    setAdditionalOrbits(additionalOrbits.map(o => 
      o.id === id ? { ...o, [field]: value } : o
    ));
  }
  
  function removeAdditionalOrbit(id) {
    setAdditionalOrbits(additionalOrbits.filter(o => o.id !== id));
  }

  // VR helpers — per-tour array model
  // Each tour: { id: string, name: string, additionalPoints: number }
  function vrTierAdd(setter, key) {
    setter(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), { id: crypto.randomUUID(), name: "", additionalPoints: 0 }]
    }));
  }
  function vrTierRemoveLast(setter, key) {
    setter(prev => ({
      ...prev,
      [key]: (prev[key] || []).slice(0, -1)
    }));
  }
  function vrTierSetCount(setter, key, newCount) {
    setter(prev => {
      const cur = prev[key] || [];
      if (newCount > cur.length) {
        const toAdd = Array.from({ length: newCount - cur.length }, () => ({ id: crypto.randomUUID(), name: "", additionalPoints: 0 }));
        return { ...prev, [key]: [...cur, ...toAdd] };
      }
      return { ...prev, [key]: cur.slice(0, Math.max(0, newCount)) };
    });
  }
  function vrTourUpdate(setter, key, idx, field, value) {
    setter(prev => ({
      ...prev,
      [key]: (prev[key] || []).map((t, i) => i === idx ? { ...t, [field]: value } : t)
    }));
  }
  function vrTourRemoveAt(setter, key, idx) {
    setter(prev => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== idx)
    }));
  }
  
  // Update VR Residential Customizer
  function updateResidentialCustomizer(key, field, value) {
    setVrResidentialCustomizer(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }
  
  // Update VR Amenity Customizer
  function updateAmenityCustomizer(key, field, value) {
    setVrAmenityCustomizer(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  }
  
  // Calculate customizer cost for a single tour type
  function calculateCustomizerCost(customizerData) {
    if (!customizerData.enabled || customizerData.categories.length === 0) {
      return { baseFee: 0, categoriesCost: 0, total: 0, categoryDetails: [] };
    }
    
    const baseFee = customizerPricing.baseFee;
    let categoriesCost = 0;
    const categoryDetails = [];
    
    customizerData.categories.forEach(cat => {
      let pricePerOption;
      let tierLabel;
      
      // Check if this is Furniture Package (premium pricing)
      if (cat.type === "furniture_package") {
        if (cat.optionCount === 1) {
          pricePerOption = customizerPricing.furnitureTier1;
          tierLabel = "1 option";
        } else if (cat.optionCount === 2) {
          pricePerOption = customizerPricing.furnitureTier2;
          tierLabel = "2 options";
        } else if (cat.optionCount === 3) {
          pricePerOption = customizerPricing.furnitureTier3;
          tierLabel = "3 options";
        } else {
          pricePerOption = customizerPricing.furnitureTier4Plus;
          tierLabel = `${cat.optionCount} options`;
        }
      } else {
        // Standard tiered option pricing
        if (cat.optionCount === 1) {
          pricePerOption = customizerPricing.optionPriceTier1;
          tierLabel = "1 option";
        } else if (cat.optionCount === 2) {
          pricePerOption = customizerPricing.optionPriceTier2;
          tierLabel = "2 options";
        } else {
          pricePerOption = customizerPricing.optionPriceTier3Plus;
          tierLabel = `${cat.optionCount} options`;
        }
      }
      
      const categoryTotal = cat.optionCount * pricePerOption;
      categoriesCost += categoryTotal;
      
      categoryDetails.push({
        type: cat.type,
        name: cat.type === "custom" ? cat.customName : CUSTOMIZER_CATEGORY_TYPES.find(t => t.value === cat.type)?.label || cat.type,
        optionCount: cat.optionCount,
        pricePerOption,
        tierLabel,
        lineTotal: categoryTotal,
        isFurniture: cat.type === "furniture_package"
      });
    });
    
    return {
      baseFee,
      categoriesCost,
      total: baseFee + categoriesCost,
      categoryDetails
    };
  }

  // Calculate auto-detected interior spaces for design direction
  const autoDetectedInteriorSpaces = useMemo(() => {
    // Count interior rendering deliverables
    const interiorRenderSpaces = interiorRenders;
    
    // Count residential VR tours (each type counts as interior spaces)
    const residentialVRCount = Object.values(vrResidential).reduce((sum, v) => sum + (Array.isArray(v) ? v.length : 0), 0);
    
    // Count amenity VR tours (interior amenities)
    const amenityVRCount = Object.values(vrAmenity).reduce((sum, v) => sum + (Array.isArray(v) ? v.length : 0), 0);
    
    // Count amenity spaces modeled
    const amenityModeled = amenityEntries.length;
    
    // Use the maximum of these as a starting estimate
    // Interior renders and VR tours are the primary indicators
    return Math.max(interiorRenderSpaces, residentialVRCount + amenityVRCount, amenityModeled);
  }, [interiorRenders, vrResidential, vrAmenity, amenityEntries]);

  // Check if interior visualization scope exists (triggers showing the section)
  const hasInteriorVisualization = useMemo(() => {
    return interiorRenders > 0 || 
           Object.values(vrResidential).some(v => Array.isArray(v) && v.length > 0) ||
           Object.values(vrAmenity).some(v => Array.isArray(v) && v.length > 0) ||
           amenityEntries.length > 0 ||
           interiorSqft > 0;
  }, [interiorRenders, vrResidential, vrAmenity, amenityEntries, interiorSqft]);

  // Get the effective space count (override or auto-detected)
  const effectiveInteriorSpaceCount = useMemo(() => {
    if (interiorDesignSpacesOverride !== null && interiorDesignSpacesOverride >= 0) {
      return interiorDesignSpacesOverride;
    }
    return autoDetectedInteriorSpaces;
  }, [interiorDesignSpacesOverride, autoDetectedInteriorSpaces]);

  // Initialize/sync interior design spaces when count changes
  useEffect(() => {
    setInteriorDesignSpaces(prev => {
      const targetCount = effectiveInteriorSpaceCount;
      if (prev.length === targetCount) return prev;
      
      if (prev.length < targetCount) {
        // Add new spaces
        const newSpaces = [...prev];
        for (let i = prev.length; i < targetCount; i++) {
          newSpaces.push({
            id: crypto.randomUUID(),
            name: `Space ${i + 1}`,
            completeness_level: "full_package",
            notes: ""
          });
        }
        return newSpaces;
      } else {
        // Remove excess spaces
        return prev.slice(0, targetCount);
      }
    });
  }, [effectiveInteriorSpaceCount]);

  // Update interior design space
  function updateInteriorDesignSpace(id, field, value) {
    setInteriorDesignSpaces(prev => 
      prev.map(space => 
        space.id === id ? { ...space, [field]: value } : space
      )
    );
  }

  // Calculate interior design direction fee
  const interiorDesignDirectionCalc = useMemo(() => {
    if (!hasInteriorVisualization || interiorDesignSpaces.length === 0) {
      return { spaces: [], totalFee: 0 };
    }

    // Allow admin-managed override of Partial / Concept Creation fees from /pricing-rules.
    // Falls back to hardcoded DESIGN_COMPLETENESS_LEVELS defaults if no rule is configured.
    const designRules = (pricingRules || []).filter(
      r => r.category === "interior_design_direction" && r.active !== false
    );
    const ruleByLevel = {};
    designRules.forEach(r => {
      const levelKey = r?.tier_config?.completeness_level;
      if (levelKey) ruleByLevel[levelKey] = Number(r.default_rate) || 0;
    });

    const spacesWithFees = interiorDesignSpaces.map(space => {
      const levelConfig = DESIGN_COMPLETENESS_LEVELS.find(l => l.value === space.completeness_level);
      const adminFee = ruleByLevel[space.completeness_level];
      const fee = adminFee != null ? adminFee : (levelConfig?.fee || 0);
      return {
        ...space,
        fee,
        levelLabel: levelConfig?.shortLabel || "Unknown"
      };
    });

    const totalFee = spacesWithFees.reduce((sum, s) => sum + s.fee, 0);

    return { spaces: spacesWithFees, totalFee };
  }, [hasInteriorVisualization, interiorDesignSpaces, pricingRules]);

  const coverage = useMemo(() => {
    return calculateCoverage(floorsCovered, uniqueFloorplates, repeatFloorplates);
  }, [floorsCovered, uniqueFloorplates, repeatFloorplates]);

  // Calculate Exterior Modeling totals (VEXOS side)
  const exteriorCalc = useMemo(() => {
    const replicationRate = getReplicationRate();
    
    const structureDetails = exteriorStructures.map(s => {
      const tierRate = getStructureRate(s.complexity);
      const primaryCost = s.uniqueCount * tierRate;
      const replicationCost = s.repeatCount * replicationRate;
      const totalCost = primaryCost + replicationCost;
      
      return {
        ...s,
        tierRate,
        replicationRate,
        primaryCost,
        replicationCost,
        totalCost
      };
    });

    const totalUniqueStructures = structureDetails.reduce((sum, s) => sum + s.uniqueCount, 0);
    const totalReplicatedPlacements = structureDetails.reduce((sum, s) => sum + s.repeatCount, 0);
    const primaryStructureSubtotal = structureDetails.reduce((sum, s) => sum + s.primaryCost, 0);
    const replicationSubtotal = structureDetails.reduce((sum, s) => sum + s.replicationCost, 0);
    const siteEnvironmentCost = getSiteRate(siteEnvironment);
    const realityCaptureCost = includeRealityCapture ? realityCaptureRate : 0;
    
    const exteriorModelingTotal = primaryStructureSubtotal + replicationSubtotal + siteEnvironmentCost + realityCaptureCost;

    return {
      structureDetails,
      totalUniqueStructures,
      totalReplicatedPlacements,
      primaryStructureSubtotal,
      replicationSubtotal,
      siteEnvironmentCost,
      realityCaptureCost,
      exteriorModelingTotal
    };
  }, [exteriorStructures, siteEnvironment, includeRealityCapture, realityCaptureRate, pricingRules]);

  // Calculate orbit configuration totals
  const orbitCalc = useMemo(() => {
    const masterOrbitCost = includeMasterOrbit ? masterOrbitPrice : 0;
    const generalAreaOrbitCost = includeGeneralAreaOrbit ? generalAreaOrbitPrice : 0;
    
    // Calculate additional orbits costs
    const additionalOrbitDetails = additionalOrbits.map(orbit => {
      const orbitType = ADDITIONAL_ORBIT_TYPES.find(t => t.value === orbit.type);
      const subtotal = orbit.quantity * additionalOrbitPrice;
      return {
        ...orbit,
        typeLabel: orbitType?.label || "Custom Orbit",
        unitPrice: additionalOrbitPrice,
        subtotal
      };
    });
    
    const additionalOrbitsTotal = additionalOrbitDetails.reduce((sum, o) => sum + o.subtotal, 0);
    const totalAdditionalOrbitCount = additionalOrbits.reduce((sum, o) => sum + o.quantity, 0);
    const orbitConfigurationTotal = masterOrbitCost + generalAreaOrbitCost + additionalOrbitsTotal;
    
    return {
      masterOrbitCost,
      generalAreaOrbitCost,
      additionalOrbitDetails,
      additionalOrbitsTotal,
      totalAdditionalOrbitCount,
      orbitConfigurationTotal
    };
  }, [includeMasterOrbit, masterOrbitPrice, includeGeneralAreaOrbit, generalAreaOrbitPrice, additionalOrbits, additionalOrbitPrice]);

  // SFH Exterior Calc (for "both" mode — separate structures from building)
  const sfhExteriorCalc = useMemo(() => {
    const replicationRate = getReplicationRate();
    const structureDetails = sfhExteriorStructures.map(s => {
      const tierRate = getStructureRate(s.complexity);
      const primaryCost = s.uniqueCount * tierRate;
      const replicationCost = s.repeatCount * replicationRate;
      return { ...s, tierRate, replicationRate, primaryCost, replicationCost, totalCost: primaryCost + replicationCost };
    });
    const exteriorModelingTotal = structureDetails.reduce((sum, s) => sum + s.totalCost, 0);
    const totalUniqueStructures = structureDetails.reduce((sum, s) => sum + s.uniqueCount, 0);
    return { structureDetails, exteriorModelingTotal, totalUniqueStructures };
  }, [sfhExteriorStructures, pricingRules]);

  // Calculate VEXOS totals - ONE-TIME only (no license)
  const vexosCalc = useMemo(() => {
    // Total dwelling units across both modes — drives the tiered Platform Setup Fee.
    const totalUnitsForSetup =
      (Number(residentialUnits) || 0) + (Number(sfhInventory) || 0);
    const platformSetupFee = getVexosPlatformSetupFee(totalUnitsForSetup);

    // SFH Mode - Simplified pricing + Exterior Modeling + Orbit Configuration
    if (vexosQuoteType === "sfh") {
      const monthlyLicense = 0; // Eliminated — structured payment model
      
      // SFH Implementation = Base Platform Fee + Exterior Modeling + Orbit Configuration
      const implementationTotal = platformSetupFee + exteriorCalc.exteriorModelingTotal + orbitCalc.orbitConfigurationTotal;
      
      return {
        quoteType: "sfh",
        baseSetup: platformSetupFee,
        additionalBuildingsCost: 0,
        amenityCost: 0,
        unitsCost: 0,
        platformSubtotal: platformSetupFee,
        uniqueFloorplateRate: 0,
        repeatFloorplateRate: 0,
        uniqueFloorplateCost: 0,
        repeatFloorplateCost: 0,
        floorplateSubtotal: 0,
        monthlyLicense,
        exteriorModelingTotal: exteriorCalc.exteriorModelingTotal,
        orbitConfigurationTotal: orbitCalc.orbitConfigurationTotal,
        implementationTotal
      };
    }
    
    // Both Mode - SFH homes + Building combined
    if (vexosQuoteType === "both") {
      // Single tiered Platform Setup Fee (replaces former dual fee of
      // baseSetup $25k + SFH_BASE_PLATFORM_FEE $10k). Now driven by total units.
      const baseSetup = platformSetupFee;
      const perBuilding = getRate("additional residential", "vexos");
      const perAmenity = getRate("amenity building", "vexos");
      const uniqueFloorplateRate = getRate("first unique", "vexos");
      const repeatFloorplateRate = getRate("repeat", "vexos");
      const monthlyLicense = 0; // Eliminated — structured payment model
      
      // Building side
      const additionalBuildingsCost = Math.max(0, residentialBuildings - 1) * perBuilding;
      const amenityCost = amenityBuildings * perAmenity;
      const bldgInventoryResult = calculateInventoryCostWithTiers(residentialUnits, inventoryTiers);
      const bldgUnitsCost = bldgInventoryResult.total;
      const bldgPlatformSubtotal = baseSetup + additionalBuildingsCost + amenityCost + bldgUnitsCost;
      const uniqueFloorplateCost = floorplateEnabled ? uniqueFloorplates * uniqueFloorplateRate : 0;
      const repeatFloorplateCost = floorplateEnabled ? repeatFloorplates * repeatFloorplateRate : 0;
      const floorplateSubtotal = uniqueFloorplateCost + repeatFloorplateCost;
      
      // SFH side — exterior modeling + inventory (same tier pricing).
      // NOTE: no separate SFH base fee anymore — the single tiered Platform
      // Setup Fee above already covers SFH units in the combined unit count.
      const sfhInventoryResult = calculateInventoryCostWithTiers(sfhInventory, inventoryTiers);
      const sfhUnitsCost = sfhInventoryResult.total;
      const sfhModelingTotal = sfhExteriorCalc.exteriorModelingTotal;
      const sfhSubtotal = sfhModelingTotal + sfhUnitsCost;
      
      const combinedImplementation = bldgPlatformSubtotal + exteriorCalc.exteriorModelingTotal + orbitCalc.orbitConfigurationTotal + floorplateSubtotal + sfhSubtotal;
      
      return {
        quoteType: "both",
        baseSetup,
        additionalBuildingsCost,
        amenityCost,
        unitsCost: bldgUnitsCost,
        effectiveUnitRate: bldgInventoryResult.rate,
        inventoryTierRate: bldgInventoryResult.rate,
        inventoryTierLabel: bldgInventoryResult.tierLabel,
        platformSubtotal: bldgPlatformSubtotal,
        uniqueFloorplateRate,
        repeatFloorplateRate,
        uniqueFloorplateCost,
        repeatFloorplateCost,
        floorplateSubtotal,
        monthlyLicense,
        exteriorModelingTotal: exteriorCalc.exteriorModelingTotal,
        orbitConfigurationTotal: orbitCalc.orbitConfigurationTotal,
        // SFH-specific — in Both mode the single tiered Platform Setup Fee
        // covers SFH units within the combined total, so there's no separate
        // SFH base fee. Field kept for back-compat but set to 0.
        sfhBaseFee: 0,
        sfhModelingTotal,
        sfhUnitsCost,
        sfhInventoryRate: sfhInventoryResult.rate,
        sfhInventoryLabel: sfhInventoryResult.tierLabel,
        sfhSubtotal,
        implementationTotal: combinedImplementation
      };
    }
    
    // Building Mode - Full pricing (tiered Platform Setup Fee by total units)
    const baseSetup = platformSetupFee;
    const perBuilding = getRate("additional residential", "vexos");
    const perAmenity = getRate("amenity building", "vexos");
    const perUnit = getRate("residential unit", "vexos");
    const uniqueFloorplateRate = getRate("first unique", "vexos");
    const repeatFloorplateRate = getRate("repeat", "vexos");
    const monthlyLicense = 0; // Eliminated — structured payment model

    const additionalBuildingsCost = Math.max(0, residentialBuildings - 1) * perBuilding;
    const amenityCost = amenityBuildings * perAmenity;
    const inventoryResult = calculateInventoryCostWithTiers(residentialUnits, inventoryTiers);
    const unitsCost = inventoryResult.total;
    const inventoryTierRate = inventoryResult.rate;
    const inventoryTierLabel = inventoryResult.tierLabel;
    const effectiveUnitRate = inventoryTierRate;
    const platformSubtotal = baseSetup + additionalBuildingsCost + amenityCost + unitsCost;

    const uniqueFloorplateCost = floorplateEnabled ? uniqueFloorplates * uniqueFloorplateRate : 0;
    const repeatFloorplateCost = floorplateEnabled ? repeatFloorplates * repeatFloorplateRate : 0;
    const floorplateSubtotal = uniqueFloorplateCost + repeatFloorplateCost;

    // One-time implementation total (NO license, includes Exterior Modeling + Orbit Configuration)
    const implementationTotal = platformSubtotal + exteriorCalc.exteriorModelingTotal + orbitCalc.orbitConfigurationTotal + floorplateSubtotal;

    return {
      quoteType: "building",
      baseSetup,
      additionalBuildingsCost,
      amenityCost,
      unitsCost,
      effectiveUnitRate,
      inventoryTierRate,
      inventoryTierLabel,
      platformSubtotal,
      uniqueFloorplateRate,
      repeatFloorplateRate,
      uniqueFloorplateCost,
      repeatFloorplateCost,
      floorplateSubtotal,
      monthlyLicense,
      exteriorModelingTotal: exteriorCalc.exteriorModelingTotal,
      orbitConfigurationTotal: orbitCalc.orbitConfigurationTotal,
      implementationTotal
    };
  }, [vexosQuoteType, pricingRules, residentialBuildings, amenityBuildings, residentialUnits, 
      uniqueFloorplates, repeatFloorplates, projectSize, floorplateEnabled, exteriorCalc.exteriorModelingTotal, orbitCalc.orbitConfigurationTotal,
      sfhInventory, sfhExteriorCalc.exteriorModelingTotal, inventoryTiers]);

  // Calculate Visualization totals (NO exterior modeling - that's in VEXOS/exteriorCalc)
  const vizCalc = useMemo(() => {
    // Tiered interior modeling — ONE rate based on combined (residential + amenity) SF, applied to both.
    const interiorModeling = calculateInteriorModeling({
      residentialSqft: interiorSqft,
      amenityEntries,
      tiers: interiorTiers
    });
    const interiorModelingRate = interiorModeling.appliedRate;
    const interiorModelingCost = interiorModeling.residentialModelingCost;
    const amenityDetails = interiorModeling.amenityDetails;
    const amenityModelingCost = interiorModeling.amenityModelingCost;
    const interiorModelingSubtotal = interiorModeling.interiorModelingSubtotal;
    const combinedInteriorSqft = interiorModeling.combinedInteriorSqft;
    const interiorModelingTierLabel = interiorModeling.tierLabel;

    // Flat-tier volume pricing — scan loaded tiers (admin-editable) for qty match.
    function resolveTier(qty, tiers, fallback) {
      if (!qty || qty <= 0 || !Array.isArray(tiers) || tiers.length === 0) return fallback;
      const sorted = [...tiers].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      for (const t of sorted) {
        if (t.active === false) continue;
        const mn = t.min_qty ?? 1;
        const mx = t.max_qty;
        if (qty >= mn && (mx == null || qty <= mx)) return Number(t.rate_per_image) || fallback;
      }
      return fallback;
    }
    const interiorRenderRate = resolveTier(interiorRenders, renderingTiers.interior, getRate("interior rendering", "visualization") || 3500);
    const exteriorRenderRate = resolveTier(exteriorRenders, renderingTiers.exterior, getRate("exterior rendering", "visualization") || 4500);
    // Live pricing-admin sync for aerial. `aerialRate` state still carries per-quote
    // overrides loaded from saved line items; empty/default (6000) means "use admin rate".
    const adminAerialRate = getRate("aerial rendering", "visualization") || 0;
    const effectiveAerialRate = (aerialRate && aerialRate !== 6000) ? aerialRate : (adminAerialRate || 6000);
    const interiorRenderCost = interiorRenders * interiorRenderRate;
    const exteriorRenderCost = exteriorRenders * exteriorRenderRate;
    const aerialRenderCost = aerialRenders * effectiveAerialRate;
    const floorPlanCost = floorPlans * floorPlanRate;
    const renderingSubtotal = interiorRenderCost + exteriorRenderCost + aerialRenderCost + floorPlanCost;

    // VR Tours - Residential (detailed calculation with Customizer)
    let residentialVRBaseCost = 0;
    let residentialVRAdditionalPointsCost = 0;
    let residentialCustomizerTotal = 0;
    let totalResidentialTours = 0;
    let totalResidentialAdditionalPoints = 0;

    const residentialVRDetails = RESIDENTIAL_VR_TOURS.map(config => {
      const tours = Array.isArray(vrResidential[config.key]) ? vrResidential[config.key] : [];
      const customizerData = vrResidentialCustomizer[config.key];
      const rate = getResidentialVrRate(config.key);
      
      // Per-tour breakdown
      const tourBreakdown = tours.map((t, idx) => {
        const addPts = Math.max(0, parseInt(t.additionalPoints) || 0);
        const viewChangeCount = Math.max(0, parseInt(t.viewChangeCount) || 0);
        const tourBaseCost = rate;
        const tourAdditionalCost = addPts * additionalPointRate;
        const finalPoints = config.includedPoints + addPts;
        const displayName = (t.name && t.name.trim()) || `${config.label} Tour ${idx + 1}`;
        return {
          id: t.id,
          index: idx,
          name: t.name || "",
          displayName,
          additionalPoints: addPts,
          viewChangeCount,
          finalPoints,
          baseCost: tourBaseCost,
          additionalCost: tourAdditionalCost,
          totalCost: tourBaseCost + tourAdditionalCost
        };
      });
      
      const baseCost = tourBreakdown.reduce((s, t) => s + t.baseCost, 0);
      const additionalCost = tourBreakdown.reduce((s, t) => s + t.additionalCost, 0);
      const quantity = tours.length;
      const sumAdditionalPoints = tourBreakdown.reduce((s, t) => s + t.additionalPoints, 0);
      
      // Calculate customizer cost for this tour type
      const customizerCalc = calculateCustomizerCost(customizerData);
      residentialCustomizerTotal += customizerCalc.total;
      
      const totalCost = baseCost + additionalCost + customizerCalc.total;
      
      residentialVRBaseCost += baseCost;
      residentialVRAdditionalPointsCost += additionalCost;
      totalResidentialTours += quantity;
      totalResidentialAdditionalPoints += sumAdditionalPoints;

      return {
        ...config,
        quantity,
        tours: tourBreakdown,
        rate,
        baseCost,
        additionalCost,
        totalCost,
        // Maintain backward-compat fields for legacy consumers
        additionalPoints: tourBreakdown[0]?.additionalPoints || 0,
        finalPoints: config.includedPoints + (tourBreakdown[0]?.additionalPoints || 0),
        customizerEnabled: customizerData.enabled,
        customizerCalc
      };
    });

    const residentialVRSubtotal = residentialVRBaseCost + residentialVRAdditionalPointsCost + residentialCustomizerTotal;

    // VR Tours - Amenity (detailed calculation with Customizer)
    let amenityVRBaseCost = 0;
    let amenityVRAdditionalPointsCost = 0;
    let amenityCustomizerTotal = 0;
    let totalAmenityTours = 0;
    let totalAmenityAdditionalPoints = 0;

    const amenityVRDetails = AMENITY_VR_TOURS.map(config => {
      const tours = Array.isArray(vrAmenity[config.key]) ? vrAmenity[config.key] : [];
      const customizerData = vrAmenityCustomizer[config.key];
      const rate = getAmenityVrRate(config.key);
      
      const tourBreakdown = tours.map((t, idx) => {
        const addPts = Math.max(0, parseInt(t.additionalPoints) || 0);
        const viewChangeCount = Math.max(0, parseInt(t.viewChangeCount) || 0);
        const tourBaseCost = rate;
        const tourAdditionalCost = addPts * additionalPointRate;
        const finalPoints = config.includedPoints + addPts;
        const displayName = (t.name && t.name.trim()) || `${config.label} Tour ${idx + 1}`;
        return {
          id: t.id,
          index: idx,
          name: t.name || "",
          displayName,
          additionalPoints: addPts,
          viewChangeCount,
          finalPoints,
          baseCost: tourBaseCost,
          additionalCost: tourAdditionalCost,
          totalCost: tourBaseCost + tourAdditionalCost
        };
      });
      
      const baseCost = tourBreakdown.reduce((s, t) => s + t.baseCost, 0);
      const additionalCost = tourBreakdown.reduce((s, t) => s + t.additionalCost, 0);
      const quantity = tours.length;
      const sumAdditionalPoints = tourBreakdown.reduce((s, t) => s + t.additionalPoints, 0);
      
      // Calculate customizer cost for this tour type
      const customizerCalc = calculateCustomizerCost(customizerData);
      amenityCustomizerTotal += customizerCalc.total;
      
      const totalCost = baseCost + additionalCost + customizerCalc.total;
      
      amenityVRBaseCost += baseCost;
      amenityVRAdditionalPointsCost += additionalCost;
      totalAmenityTours += quantity;
      totalAmenityAdditionalPoints += sumAdditionalPoints;

      return {
        ...config,
        quantity,
        tours: tourBreakdown,
        rate,
        baseCost,
        additionalCost,
        totalCost,
        additionalPoints: tourBreakdown[0]?.additionalPoints || 0,
        finalPoints: config.includedPoints + (tourBreakdown[0]?.additionalPoints || 0),
        customizerEnabled: customizerData.enabled,
        customizerCalc
      };
    });

    const amenityVRSubtotal = amenityVRBaseCost + amenityVRAdditionalPointsCost + amenityCustomizerTotal;
    const totalAdditionalPointsCost = residentialVRAdditionalPointsCost + amenityVRAdditionalPointsCost;
    const totalCustomizerCost = residentialCustomizerTotal + amenityCustomizerTotal;

    // Custom Amenity Tours — priced per-point using the Small-tier per-point rate
    const smallRate = getAmenityVrRate("small");
    const customPerPointRate = smallRate / 4; // Small tier is 4 pts — derived per-point rate
    const customAmenityDetails = (amenityCustomTours || []).map(space => {
      const pts = Math.max(1, parseInt(space.points) || 1);
      const cost = pts * customPerPointRate;
      return { id: space.id, name: space.name || "Custom Space", points: pts, rate: customPerPointRate, cost };
    });
    const customAmenityGrossTotal = customAmenityDetails.reduce((s, d) => s + d.cost, 0);
    const customAmenityApplied = amenityCustomConfirmed ? customAmenityGrossTotal : 0;

    const vrSubtotal = residentialVRSubtotal + amenityVRSubtotal + customAmenityApplied;

    // VR View-Change Duplicates — same floorplan/typology, only the backplate
    // and 1–3 rendered images change. Per-unit rate is determined by the
    // GLOBAL count across both Residential + Amenity tours (volume tiers):
    //   1–5: $400 · 6–10: $350 · 11–20: $300 · 21–50: $250 · 51+: $200
    function viewChangeTierRate(count) {
      if (count <= 0) return 0;
      if (count <= 5) return 400;
      if (count <= 10) return 350;
      if (count <= 20) return 300;
      if (count <= 50) return 250;
      return 200;
    }
    const residentialViewChangeCount = residentialVRDetails.reduce(
      (s, d) => s + d.tours.reduce((t, x) => t + (parseInt(x.viewChangeCount) || 0), 0),
      0,
    );
    const amenityViewChangeCount = amenityVRDetails.reduce(
      (s, d) => s + d.tours.reduce((t, x) => t + (parseInt(x.viewChangeCount) || 0), 0),
      0,
    );
    const totalViewChangeCount = residentialViewChangeCount + amenityViewChangeCount;
    const viewChangeRate = viewChangeTierRate(totalViewChangeCount);
    const viewChangeSubtotal = totalViewChangeCount * viewChangeRate;

    // Animation - FIXED PRICING — AI Motion or Cinematic Film + optional Social Media Pack
    const animationBaseCost = animationEnabled ? getVideoAnimationRate(animationType, animationDuration) : 0;
    const socialMediaPackCost = animationEnabled && socialMediaPack ? SOCIAL_MEDIA_PACK.price : 0;
    const animationCost = animationBaseCost + socialMediaPackCost;
    const animationSubtotal = animationCost;
    const animationShots = animationEnabled ? getAnimationShots(animationType, animationDuration) : 0;

    // Interior Design Direction fee (from interiorDesignDirectionCalc)
    const interiorDesignFee = interiorDesignDirectionCalc.totalFee;

    // Visualization grandTotal — includes Exterior Modeling when VEXOS is OFF (so the rep can charge
    // for exterior assets needed for renderings/aerials/360s without double-billing under VEXOS).
    const exteriorVizContribution = !vexosModuleEnabled ? exteriorCalc.exteriorModelingTotal : 0;
    const grandTotal = interiorModelingSubtotal + renderingSubtotal + vrSubtotal + viewChangeSubtotal + animationSubtotal + interiorDesignFee + exteriorVizContribution;

    return {
      interiorModelingRate, interiorModelingCost, amenityModelingCost, amenityDetails, interiorModelingSubtotal,
      combinedInteriorSqft, interiorModelingTierLabel,
      interiorRenderRate, exteriorRenderRate, interiorRenderCost, exteriorRenderCost,
      aerialRenderCost, aerialRate: effectiveAerialRate, floorPlanCost, renderingSubtotal,
      // VR Residential
      residentialVRDetails,
      residentialVRBaseCost,
      residentialVRAdditionalPointsCost,
      residentialVRSubtotal,
      totalResidentialTours,
      totalResidentialAdditionalPoints,
      residentialCustomizerTotal,
      // VR Amenity
      amenityVRDetails,
      amenityVRBaseCost,
      amenityVRAdditionalPointsCost,
      amenityVRSubtotal,
      totalAmenityTours,
      totalAmenityAdditionalPoints,
      amenityCustomizerTotal,
      // Custom Amenity Tours
      customAmenityDetails,
      customAmenityGrossTotal,
      customAmenityApplied,
      customPerPointRate,
      // VR Combined
      vrSubtotal,
      totalAdditionalPointsCost,
      totalCustomizerCost,
      // VR View-Change Duplicates
      residentialViewChangeCount,
      amenityViewChangeCount,
      totalViewChangeCount,
      viewChangeRate,
      viewChangeSubtotal,
      // Animation
      animationCost, animationBaseCost, socialMediaPackCost, animationSubtotal, animationShots,
      // Interior Design Direction
      interiorDesignFee,
      // Exterior Modeling routed into Visualization (when VEXOS off)
      exteriorVizContribution,
      // Grand Total
      grandTotal
    };
  }, [pricingRules, renderingTiers, interiorSqft, interiorTiers,
      amenityEntries, interiorRenders, exteriorRenders, aerialRenders, aerialRate,
      floorPlans, floorPlanRate, vrResidential, vrAmenity, additionalPointRate, animationEnabled, animationType, animationDuration, socialMediaPack,
      interiorDesignDirectionCalc, vrResidentialCustomizer, vrAmenityCustomizer, customizerPricing,
      amenityCustomTours, amenityCustomConfirmed,
      vexosModuleEnabled, exteriorCalc.exteriorModelingTotal]);

  // Conceptual Exterior Modeling calculations
  const conceptModelingCalc = useMemo(() => {
    if (!conceptModelingEnabled) return { grandTotal: 0, baseCost: 0, modFee: 0, addonTotal: 0, lineItems: [], description: "" };

    let baseCost = 0;
    let modFee = 0;
    let description = "";
    const lineItems = [];

    if (conceptApproach === "lite") {
      baseCost = 2000 * conceptBuildings;
      description = "Adaptation of pre-existing 3D building models with material and proportional adjustments for visual representation.";
      lineItems.push({ label: "Concept Lite — Adapt Existing Model", quantity: conceptBuildings, rate: 2000, subtotal: baseCost });
    } else if (conceptApproach === "plus") {
      const modFees = { light: 1500, medium: 3000, heavy: 6000 };
      const modLabels = { light: "Light Modifications", medium: "Medium Modifications", heavy: "Heavy Modifications" };
      baseCost = 3000 * conceptBuildings;
      modFee = modFees[conceptModLevel] * conceptBuildings;
      description = "Base model adaptation with customized massing and facade adjustments to align with the project vision.";
      lineItems.push({ label: "Concept Plus — Base Model", quantity: conceptBuildings, rate: 3000, subtotal: baseCost });
      lineItems.push({ label: `Concept Plus — ${modLabels[conceptModLevel]}`, quantity: conceptBuildings, rate: modFees[conceptModLevel], subtotal: modFee });
    } else if (conceptApproach === "studio") {
      const studioPrices = { standard: 10000, complex: 15000, landmark: conceptLandmarkPrice };
      const studioLabels = { standard: "Standard", complex: "Complex", landmark: "Landmark / Signature" };
      const unitPrice = studioPrices[conceptComplexity];
      baseCost = unitPrice * conceptBuildings;
      description = "Full conceptual building design developed from scratch through an iterative design process.";
      lineItems.push({ label: `Concept Studio — ${studioLabels[conceptComplexity]}`, quantity: conceptBuildings, rate: unitPrice, subtotal: baseCost });
    }

    // Add-ons
    const siteContextPrices = { none: 0, simple: 1500, medium: 3000, complex: 5000 };
    const siteContextCost = siteContextPrices[conceptSiteContext] || 0;
    const renderOptCost = conceptRenderOptimization ? 2000 : 0;
    const vexosCost = conceptVexosStructuring ? 2500 : 0;
    const addonTotal = siteContextCost + renderOptCost + vexosCost;

    if (siteContextCost > 0) lineItems.push({ label: `Site Context Modeling (${conceptSiteContext})`, quantity: 1, rate: siteContextCost, subtotal: siteContextCost });
    if (renderOptCost > 0) lineItems.push({ label: "Rendering / Animation Optimization", quantity: 1, rate: renderOptCost, subtotal: renderOptCost });
    if (vexosCost > 0) lineItems.push({ label: "VEXOS Structuring", quantity: 1, rate: vexosCost, subtotal: vexosCost });

    const grandTotal = baseCost + modFee + addonTotal;

    return { grandTotal, baseCost, modFee, addonTotal, siteContextCost, renderOptCost, vexosCost, lineItems, description };
  }, [conceptModelingEnabled, conceptApproach, conceptBuildings, conceptModLevel, conceptComplexity, conceptLandmarkPrice,
      conceptSiteContext, conceptRenderOptimization, conceptVexosStructuring]);

  // Marketing Calculator calculations
  const MKTG_PRE_LEASE_TIERS = [
    { id: "t1", label: "below $1,500", cpl: 10 }, { id: "t2", label: "$1,500 - $2,500", cpl: 15 },
    { id: "t3", label: "$2,500 - $4,500", cpl: 25 }, { id: "t4", label: "$4,500+", cpl: 40 }
  ];
  const MKTG_PRE_SALE_TIERS = [
    { id: "t1", label: "below $600K", cpl: 35 }, { id: "t2", label: "$600K - $1.2M", cpl: 60 },
    { id: "t3", label: "$1.2M - $2.5M", cpl: 75 }, { id: "t4", label: "$2.5M+", cpl: 100 }
  ];
  const MKTG_SETUP_FEE = 5500;
  const MKTG_SERVICE_FEE = 3500;
  const MKTG_CONTENT_PACKAGES = [
    { id: "starter", label: "Content Starter", clipsPerMonth: 6, enhancementsBase: 6 },
    { id: "growth", label: "Content Growth", clipsPerMonth: 12, enhancementsBase: 12 },
    { id: "dominance", label: "Content Dominance", clipsPerMonth: 18, enhancementsBase: 20 }
  ];
  const MKTG_RETAINER_DISCOUNT = 0.08;

  const marketingCalc = useMemo(() => {
    if (!marketingEnabled) return { grandTotal: 0, monthlyBudget: 0, monthlyAdSpend: 0, monthlyLeads: 0, totalLeads: 0, lineItems: [], description: "", aiContentTotal: 0, totalClips: 0, totalEnhancements: 0, discount: 0, retainerMonthly: 0, clipsPerMonth: 0, enhancementsPerMonth: 0, hasAiContent: false, rawAiTotal: 0 };
    const tiers = marketingMode === "pre_lease" ? MKTG_PRE_LEASE_TIERS : MKTG_PRE_SALE_TIERS;
    const tier = tiers.find(t => t.id === marketingTier) || tiers[2];
    const rate = marketingConvRate / 100;
    const totalLeads = Math.ceil(marketingUnits / rate);
    const monthlyLeads = Math.ceil(totalLeads / marketingTimeline);
    const monthlyAdSpend = Math.round(monthlyLeads * tier.cpl);
    const monthlyBudget = monthlyAdSpend + MKTG_SERVICE_FEE;

    // AI Content (only when content section is enabled)
    let totalClips = 0, totalEnhancements = 0, clipsPerMonth = 0, enhancementsPerMonth = 0;
    const pkg = MKTG_CONTENT_PACKAGES.find(p => p.id === mktgSelectedPackage);
    if (mktgContentEnabled && mktgContentMode === "package" && pkg) {
      clipsPerMonth = pkg.clipsPerMonth;
      totalClips = clipsPerMonth * marketingTimeline;
      totalEnhancements = pkg.enhancementsBase;
      enhancementsPerMonth = Math.ceil(totalEnhancements / marketingTimeline);
    } else if (mktgContentEnabled && mktgContentMode === "custom") {
      totalClips = mktgAiAnimatedEnabled ? mktgAiAnimatedQty : 0;
      totalEnhancements = mktgAiEnhanceEnabled ? mktgAiEnhanceQty : 0;
      clipsPerMonth = totalClips > 0 ? Math.ceil(totalClips / marketingTimeline) : 0;
      enhancementsPerMonth = totalEnhancements > 0 ? Math.ceil(totalEnhancements / marketingTimeline) : 0;
    }
    const hasAiContent = mktgContentEnabled && (mktgContentMode === "package" || (mktgContentMode === "custom" && (mktgAiAnimatedEnabled || mktgAiEnhanceEnabled)));
    const rawAnimatedTotal = totalClips * 899;
    const rawEnhanceTotal = totalEnhancements * 599;
    const rawAiTotal = rawAnimatedTotal + rawEnhanceTotal;
    const discount = (mktgRetainerEnabled && hasAiContent) ? rawAiTotal * MKTG_RETAINER_DISCOUNT : 0;
    const aiContentTotal = hasAiContent ? rawAiTotal - discount : 0;
    const retainerMonthlyRaw = (clipsPerMonth * 899) + (enhancementsPerMonth * 599);
    const retainerMonthly = mktgRetainerEnabled ? Math.round(retainerMonthlyRaw * (1 - MKTG_RETAINER_DISCOUNT)) : 0;

    const totalCampaignCost = MKTG_SETUP_FEE + (monthlyAdSpend * marketingTimeline) + (MKTG_SERVICE_FEE * marketingTimeline) + aiContentTotal;
    const modeLabel = marketingMode === "pre_lease" ? "Pre-Lease" : "Pre-Sale";
    const desc = marketingMode === "pre_lease"
      ? "Targeted leasing campaign based on the project's rental profile, unit count, lease-up timeline, and estimated lead-to-lease conversion rate."
      : "Targeted pre-sale campaign based on the project's price point, inventory count, sell-out timeline, and estimated lead-to-sale conversion rate.";
    const lineItems = [
      { label: "Setup Fee", quantity: 1, rate: MKTG_SETUP_FEE, subtotal: MKTG_SETUP_FEE },
      { label: `Monthly Service Fee (${marketingTimeline}mo)`, quantity: marketingTimeline, rate: MKTG_SERVICE_FEE, subtotal: MKTG_SERVICE_FEE * marketingTimeline },
      { label: `Monthly Ad Spend (${marketingTimeline}mo)`, quantity: marketingTimeline, rate: monthlyAdSpend, subtotal: monthlyAdSpend * marketingTimeline },
      ...(hasAiContent && totalClips > 0 ? [{ label: `Animated Renders${mktgRetainerEnabled ? " — Retainer" : ""}`, quantity: totalClips, rate: 899, subtotal: rawAnimatedTotal }] : []),
      ...(hasAiContent && totalEnhancements > 0 ? [{ label: `Render Enhancement${mktgRetainerEnabled ? " — Retainer" : ""}`, quantity: totalEnhancements, rate: 599, subtotal: rawEnhanceTotal }] : []),
      ...(discount > 0 ? [{ label: "Retainer Discount (8%)", quantity: 1, rate: -discount, subtotal: -discount }] : []),
    ];
    return { grandTotal: totalCampaignCost, monthlyBudget, monthlyAdSpend, monthlyLeads, totalLeads, lineItems, description: desc, modeLabel, tierLabel: tier.label, cpl: tier.cpl, aiContentTotal, totalClips, totalEnhancements, discount, retainerMonthly, clipsPerMonth, enhancementsPerMonth, hasAiContent, rawAiTotal };
  }, [marketingEnabled, marketingMode, marketingTier, marketingUnits, marketingTimeline, marketingConvRate, mktgContentEnabled, mktgContentMode, mktgSelectedPackage, mktgRetainerEnabled, mktgAiAnimatedEnabled, mktgAiAnimatedQty, mktgAiEnhanceEnabled, mktgAiEnhanceQty]);

  // Combined calculations
  // Collateral suite calc
  const collateralCalc = useMemo(
    () => calculateCollateral(collateralEnabled ? collateralState : null, collateralPrices),
    [collateralEnabled, collateralState, collateralPrices]
  );
  const oneTimeTotal = vexosCalc.implementationTotal + vizCalc.grandTotal + conceptModelingCalc.grandTotal + (marketingEnabled ? MKTG_SETUP_FEE + marketingCalc.aiContentTotal : 0) + collateralCalc.oneTimeTotal;

  // Module-aware subtotals — enabled modules contribute, disabled modules contribute 0.
  // vizCalc.grandTotal already factors in Exterior Modeling when VEXOS is OFF
  // (see exteriorVizContribution inside vizCalc).
  const vexosEnabledTotal = vexosModuleEnabled ? vexosCalc.implementationTotal : 0;
  const vizEnabledTotal = vizModuleEnabled ? vizCalc.grandTotal : 0;
  const aiContentEnabledTotal = aiContentModuleEnabled ? aiContentTotal : 0;
  // Marketing, Conceptual Modeling, and Collateral Suite are also billable modules — include
  // them in the enabled subtotal so Marketing-only / Collateral-only / Conceptual-only proposals
  // can be generated without VEXOS or Visualization.
  const marketingEnabledTotal = (marketingEnabled && marketingCalc.grandTotal > 0)
    ? (MKTG_SETUP_FEE + marketingCalc.grandTotal + (marketingCalc.aiContentTotal || 0))
    : 0;
  const conceptEnabledTotal = (conceptModelingEnabled && conceptModelingCalc.grandTotal > 0)
    ? conceptModelingCalc.grandTotal
    : 0;
  const collateralEnabledTotal = (collateralEnabled && collateralCalc.grandTotal > 0)
    ? collateralCalc.grandTotal
    : 0;

  // ===== VEXOS Lot Explorer™ — unified pricing object =====
  // Per-lot pricing. The whole product flows through ONE normalized object so
  // sidebar, payment plan, PDF section, and reconcile endpoint all read the
  // same numbers — eliminating the subtotal-drift class of bug.
  const lotExplorerCalc = useMemo(() => {
    const BASE_FEE = 7500;
    const lots = Math.max(0, parseInt(lotExplorerLotCount) || 0);
    const rate = Math.max(0, parseFloat(lotExplorerRatePerLot) || 0);
    const lotSubtotal = lots * rate;
    const grandTotal = BASE_FEE + lotSubtotal;
    const features = [];
    if (lotExplorerIncludePoi) features.push("POI Widget");
    if (lotExplorerIncludeCrm) features.push("CRM Integration");
    if (lotExplorerIncludeMedia) features.push("Media Pop-up Support");
    if (lotExplorerIncludeBackend) features.push("Backend Management");
    // Effective timeline = integer weeks (whole numbers only).
    const _ovWeeksRaw = parseFloat(lotExplorerTimelineOverrideWeeks);
    const _ovWeeks = (!isNaN(_ovWeeksRaw) && _ovWeeksRaw > 0) ? Math.max(1, Math.round(_ovWeeksRaw)) : NaN;
    const _defWeeks = Math.max(1, Math.round(parseFloat(lotExplorerTimelineWeeks) || 4));
    const effectiveTimelineWeeks = (lotExplorerTimelineOverrideEnabled && !isNaN(_ovWeeks))
      ? _ovWeeks
      : _defWeeks;
    // Hosting / Management / Support — recurring fee math
    const _hostMonthly = Math.max(0, parseFloat(lotExplorerHostingMonthly) || 0);
    const _hostFree = Math.max(0, parseInt(lotExplorerHostingFreeMonths) || 0);
    const _hostLock = Math.max(0, parseInt(lotExplorerHostingLockMonths) || 0);
    const hostingLockInValue = _hostMonthly * _hostLock;
    const hostingFreeValue = _hostMonthly * _hostFree;
    return {
      baseFee: BASE_FEE,
      lotSubtotal,
      lotCount: lots,
      ratePerLot: rate,
      grandTotal,
      features,
      timelineWeeks: effectiveTimelineWeeks,
      timelineDefaultWeeks: _defWeeks,
      timelineIsOverridden: lotExplorerTimelineOverrideEnabled && !isNaN(_ovWeeks),
      // Hosting / Management / Support
      hostingMonthly: _hostMonthly,
      hostingFreeMonths: _hostFree,
      hostingLockMonths: _hostLock,
      hostingLockInValue,
      hostingFreeValue,
      lineItems: grandTotal > 0
        ? [
            { label: "Base Platform Fee", quantity: 1, rate: BASE_FEE, subtotal: BASE_FEE, unit_type: "flat" },
            ...(lots > 0 ? [{ label: `Interactive Sitemap (${lots} lots)`, quantity: lots, rate, subtotal: lotSubtotal, unit_type: "lot" }] : []),
          ]
        : []
    };
  }, [lotExplorerLotCount, lotExplorerRatePerLot, lotExplorerIncludePoi, lotExplorerIncludeCrm, lotExplorerIncludeMedia, lotExplorerIncludeBackend, lotExplorerTimelineWeeks, lotExplorerTimelineOverrideEnabled, lotExplorerTimelineOverrideWeeks, lotExplorerHostingMonthly, lotExplorerHostingFreeMonths, lotExplorerHostingLockMonths]);
  const lotExplorerEnabledTotal = (lotExplorerModuleEnabled && lotExplorerCalc.grandTotal > 0)
    ? lotExplorerCalc.grandTotal
    : 0;

  const enabledSubtotal = vexosEnabledTotal + vizEnabledTotal + aiContentEnabledTotal
    + marketingEnabledTotal + conceptEnabledTotal + collateralEnabledTotal
    + lotExplorerEnabledTotal;

  // Validate + apply special client discount (clamp 0-100)
  const _safeDiscountPct = Math.max(0, Math.min(100, parseFloat(specialDiscountPct) || 0));
  const discountAmount = (enabledSubtotal * _safeDiscountPct) / 100;
  const enabledSubtotalAfterDiscount = Math.max(0, enabledSubtotal - discountAmount);

  // Per-module discount allocation (proportional to each module's share of the enabled subtotal)
  const _discountVexos = enabledSubtotal > 0 && vexosModuleEnabled
    ? (vexosEnabledTotal / enabledSubtotal) * discountAmount
    : 0;
  const _discountViz = enabledSubtotal > 0 && vizModuleEnabled
    ? (vizEnabledTotal / enabledSubtotal) * discountAmount
    : 0;
  // Concept Modeling is creative-side scope that ships ALONGSIDE the
  // visualization deliverables and follows the same milestone schedule. We
  // therefore roll its (discounted) total into the Visualization payment
  // plan card so "Due at Signing" reflects the union and the payment
  // schedule applies to both. Otherwise the sidebar showed only renderings
  // and the upfront percentage was based on a stale subtotal.
  const _discountConcept = enabledSubtotal > 0 && conceptModelingEnabled
    ? (conceptEnabledTotal / enabledSubtotal) * discountAmount
    : 0;
  const conceptDiscountedTotal = Math.max(0, conceptEnabledTotal - _discountConcept);
  const vexosDiscountedTotal = Math.max(0, vexosEnabledTotal - _discountVexos);
  const vizDiscountedTotal = Math.max(0, vizEnabledTotal - _discountViz);

  // Lot Explorer proportional discount (eligible for global discount per spec)
  const _discountLotExplorer = enabledSubtotal > 0 && lotExplorerModuleEnabled
    ? (lotExplorerEnabledTotal / enabledSubtotal) * discountAmount
    : 0;
  // AI Content Studio — eligible for the global discount AND folded into the
  // Visualization payment plan (per user spec). Discount allocated proportionally
  // like all other modules, and the discounted total joins the visualization
  // milestone math so one unified payment schedule covers Viz + Concept + AI.
  const _discountAiContent = enabledSubtotal > 0 && aiContentModuleEnabled
    ? (aiContentEnabledTotal / enabledSubtotal) * discountAmount
    : 0;
  const aiContentDiscountedTotal = Math.max(0, aiContentEnabledTotal - _discountAiContent);
  const lotExplorerDiscountedTotal = Math.max(0, lotExplorerEnabledTotal - _discountLotExplorer);

  // ===== VEXOS Lot Explorer™ Payment Plan — REQUIRED & FIXED =====
  // 50% Deposit / 25% V1 Delivery / 25% Final Delivery.
  // DO NOT mix with VEXOS or Visualization payment logic.
  const lotExplorerPayment = useMemo(() => {
    const total = lotExplorerDiscountedTotal;
    const deposit = Math.round(total * 0.50);
    const v1 = Math.round(total * 0.25);
    // Force milestones to sum exactly to total to avoid $1 rounding drift
    const finalDelivery = Math.max(0, total - deposit - v1);
    return {
      total,
      deposit,
      v1Delivery: v1,
      finalDelivery,
      milestones: [
        { key: "deposit", percent: 50, label: "Deposit", amount: deposit, note: "Due at signing — initiates project setup, sitemap preparation, backend configuration, and UX deployment." },
        { key: "v1_delivery", percent: 25, label: "V1 Delivery", amount: v1, note: "Interactive sitemap deployed · zoom/pan operational · initial lot interaction · initial backend · initial media pop-up." },
        { key: "final_delivery", percent: 25, label: "Final Delivery", amount: finalDelivery, note: "Finalized lot data · finalized media · availability logic · CRM connection · POI implementation · final QA and deployment." }
      ]
    };
  }, [lotExplorerDiscountedTotal]);

  const hasDiscount = _safeDiscountPct > 0 && discountAmount > 0;
  
  // === NEW VEXOS PAYMENT MODEL ================================================
  // SaaS-style: upfront Platform Setup Fee + recurring Monthly Platform Fee +
  // selectable Contract Term (12/24/36mo). No financing, no remaining-balance
  // math, no upfront percentage. Per product spec:
  //   - Cumulative totals (recurringContractValue, totalContractValue) are
  //     computed INTERNALLY for analytics/backend but MUST NOT be surfaced
  //     to the user in UI / sidebar / PDF.
  //   - Legacy field names (totalVexos, upfrontPayment, monthlyPayment, etc.)
  //     remain populated as aliases so existing callers don't break. They now
  //     represent the SaaS model:
  //       - totalVexos          = vexosCalc.implementationTotal (one-time scope)
  //       - upfrontPayment      = setupFee (the tiered upfront fee only)
  //       - effectivePercent    = 100 (deprecated, no upfront% anymore)
  //       - remainingBalance    = 0 (no financing balance anymore)
  //       - monthlyPayment      = monthlyFee (alias for back-compat)
  //       - platformTermMonths  = contractTermMonths (no +6 extension anymore)
  const vexosPayment = useMemo(() => {
    const totalVexosScope = vexosDiscountedTotal; // full one-time VEXOS scope (post-discount)
    const totalUnits = (Number(residentialUnits) || 0) + (Number(sfhInventory) || 0);
    const setupFee = getVexosPlatformSetupFee(totalUnits);
    const monthlyFee = getVexosMonthlyFee(totalUnits);
    const contractTermMonths = vexosPaymentTermMonths;
    // INTERNAL-ONLY — computed for backend storage/analytics, NEVER rendered.
    const recurringContractValue = monthlyFee * contractTermMonths;
    const totalContractValue = setupFee + recurringContractValue;
    return {
      // NEW canonical fields
      setupFee,
      monthlyFee,
      contractTermMonths,
      dueAtSigning: setupFee,
      recurringMonthly: monthlyFee,
      // Internal-only (never displayed)
      _recurringContractValue: recurringContractValue,
      _totalContractValue: totalContractValue,
      // Legacy back-compat aliases
      totalVexos: totalVexosScope,
      upfrontPayment: setupFee,
      effectivePercent: 100,
      remainingBalance: 0,
      monthlyPayment: monthlyFee,
      platformTermMonths: contractTermMonths,
    };
  }, [vexosDiscountedTotal, vexosPaymentTermMonths, pricingRules, residentialUnits, sfhInventory]);
  
  // Payment bases — each payment module uses its own (discounted) scope total.
  // Visualization Scope rolls Conceptual Modeling AND AI Content Studio INTO the
  // visualization payment plan so the milestone schedule (Due at Signing / V1 Delivery /
  // Final Approval, etc.) applies to all three. This keeps the proposal payment plan
  // unified — AI Motion and AI Render Enhancement share the SAME payment cadence as
  // the visualization deliverables they support.
  const visualizationScopeTotal = vizDiscountedTotal + conceptDiscountedTotal + aiContentDiscountedTotal;
  // (vexosScopeTotal lives inside vexosPayment.totalVexos; totalProjectInvestment = oneTimeTotal)

  // Get active milestones (for Visualization payment plan only)
  const activeMilestones = useMemo(() => {
    if (paymentPreset === "custom") {
      return customMilestones;
    }
    return PAYMENT_PRESETS[paymentPreset]?.milestones || [];
  }, [paymentPreset, customMilestones]);

  // Visualization payment milestones — base: visualizationScopeTotal ONLY
  const vizMilestoneAmounts = useMemo(() => {
    return activeMilestones.map(m => ({
      ...m,
      amount: (visualizationScopeTotal * m.percent) / 100
    }));
  }, [activeMilestones, visualizationScopeTotal]);

  // Auto-derive V1 Delivery week = end of Modeling phase (parsed from week_label)
  const autoV1DeliveryWeek = useMemo(() => {
    const phases = timelineEstimateSnapshot?.phase_summary || [];
    for (const p of phases) {
      const name = (p.name || "").toLowerCase();
      if (name.includes("modeling")) {
        const wl = p.week_label || "";
        const range = wl.match(/(\d+)\s*[-\u2013\u2014]\s*(\d+)/);
        if (range) return parseInt(range[2]);
        const single = wl.match(/(\d+)/);
        if (single) return parseInt(single[1]);
      }
    }
    return null;
  }, [timelineEstimateSnapshot]);

  // Effective V1 week (override takes priority)
  const effectiveV1DeliveryWeek = useMemo(() => {
    const ov = parseFloat(vizV1DeliveryWeekOverride);
    if (!isNaN(ov) && ov > 0) return Math.floor(ov);
    return autoV1DeliveryWeek;
  }, [vizV1DeliveryWeekOverride, autoV1DeliveryWeek]);
  const isV1Overridden = (() => {
    const ov = parseFloat(vizV1DeliveryWeekOverride);
    return !isNaN(ov) && ov > 0;
  })();

  // Total Due Today — under the new VEXOS SaaS model, the ENTIRE VEXOS
  // implementation scope is due at signing (setup fee + exterior modeling +
  // orbit config + floorplate development). There's no longer an upfront-%
  // since there's no financing. The recurring Monthly Platform Fee is shown
  // separately in the sidebar as "Recurring Monthly".
  const totalDueToday = Math.round(
    (vexosModuleEnabled ? (vexosCalc.implementationTotal || 0) : 0)
    + ((vizModuleEnabled || conceptModelingEnabled || aiContentModuleEnabled) && vizMilestoneAmounts.length > 0 ? vizMilestoneAmounts[0].amount : 0)
    + (marketingEnabled && marketingCalc.grandTotal > 0 ? MKTG_SETUP_FEE + (marketingCalc.aiContentTotal || 0) : 0)
    + (collateralEnabled ? collateralCalc.grandTotal : 0)
    + (lotExplorerModuleEnabled ? (lotExplorerPayment.deposit || 0) : 0)
  );

  // Custom milestone handlers
  function addCustomMilestone() {
    setCustomMilestones([...customMilestones, { percent: 0, label: "New Milestone" }]);
  }

  function updateCustomMilestone(index, field, value) {
    const updated = [...customMilestones];
    updated[index] = { ...updated[index], [field]: value };
    setCustomMilestones(updated);
  }

  function removeCustomMilestone(index) {
    setCustomMilestones(customMilestones.filter((_, i) => i !== index));
  }

  const customMilestoneTotal = customMilestones.reduce((sum, m) => sum + (m.percent || 0), 0);

  // Build deliverables for timeline estimation
  const timelineDeliverables = useMemo(() => {
    const items = [];
    // Exterior Modeling
    if (exteriorCalc.exteriorModelingTotal > 0) {
      items.push({ deliverable_type: vexosQuoteType === "sfh" ? "House Model" : "High Rise Model", quantity: exteriorCalc.totalUniqueStructures || residentialBuildings || 1 });
    }
    // Interior Modeling
    if (vizCalc.interiorModelingCost > 0) {
      const modelUnits = Math.max(1, Math.ceil(interiorSqft / 1000));
      items.push({ deliverable_type: "Interior Unit S/M", quantity: modelUnits });
    }
    if (vizCalc.amenityModelingCost > 0) {
      items.push({ deliverable_type: "Interior Amenities", quantity: amenityEntries.length || 1 });
    }
    // Renderings
    if (interiorRenders > 0) items.push({ deliverable_type: "Interior Render", quantity: interiorRenders });
    if (exteriorRenders > 0) items.push({ deliverable_type: "Exterior Render", quantity: exteriorRenders });
    if (aerialRenders > 0) items.push({ deliverable_type: "Exterior Hero Day", quantity: aerialRenders });
    if (floorPlans > 0) items.push({ deliverable_type: "Floor Plans", quantity: floorPlans });
    // VR / 360 Tours
    if (vizCalc.totalResidentialTours > 0) items.push({ deliverable_type: "Interior 360", quantity: vizCalc.totalResidentialTours });
    if (vizCalc.totalAmenityTours > 0) items.push({ deliverable_type: "Interior 360 Amenities", quantity: vizCalc.totalAmenityTours });
    // Animation
    if (animationEnabled) {
      const animLabel = animationType === "ai"
        ? `AI Motion ${animationDuration}sec`
        : `Animation ${animationDuration}sec`;
      items.push({ deliverable_type: animLabel, quantity: 1 });
      if (socialMediaPack) {
        items.push({ deliverable_type: "Social Media Pack", quantity: 1 });
      }
    }
    // VEXOS Platform
    if (vexosCalc.platformSubtotal > 0) items.push({ deliverable_type: "VEXOS Platform Setup", quantity: 1 });
    // Floorplates (map to Floor Plans production)
    if (vexosCalc.floorplateSubtotal > 0) items.push({ deliverable_type: "Floor Plans", quantity: uniqueFloorplates + repeatFloorplates });
    // Concept Modeling
    if (conceptModelingCalc.grandTotal > 0) items.push({ deliverable_type: "House Model", quantity: conceptBuildings || 1 });
    return items;
  }, [exteriorCalc, vizCalc, vexosCalc, conceptModelingCalc, interiorSqft, amenityEntries,
      interiorRenders, exteriorRenders, aerialRenders, floorPlans, animationEnabled, animationType, animationDuration, socialMediaPack,
      vexosQuoteType, residentialBuildings, uniqueFloorplates, repeatFloorplates, conceptBuildings]);

  // Helper to emit a consistent "viz_animation" section shape (used by multiple buildSections paths)
  function buildAnimationSection() {
    const lineItems = animationEnabled ? [{
      id: crypto.randomUUID(),
      label: getAnimationDisplayName(animationType, animationDuration),
      quantity: 1,
      rate: getVideoAnimationRate(animationType, animationDuration),
      subtotal: getVideoAnimationRate(animationType, animationDuration),
      unit_type: "package",
      metadata: {
        animation_type: animationType,
        duration_seconds: animationDuration,
        shot_count: getAnimationShots(animationType, animationDuration)
      }
    }] : [];
    if (animationEnabled && socialMediaPack) {
      lineItems.push({
        id: crypto.randomUUID(),
        label: SOCIAL_MEDIA_PACK.label,
        quantity: 1,
        rate: SOCIAL_MEDIA_PACK.price,
        subtotal: SOCIAL_MEDIA_PACK.price,
        unit_type: "package",
        metadata: { addon: "social_media_pack" }
      });
    }
    return {
      id: crypto.randomUUID(),
      section_type: "viz_animation",
      label: "Video Animation",
      line_items: lineItems,
      subtotal: vizCalc.animationSubtotal,
      metadata: animationEnabled ? {
        animation_type: animationType,
        duration_seconds: animationDuration,
        shot_count: getAnimationShots(animationType, animationDuration),
        modeling_aligned: animationModelingAligned,
        social_media_pack: socialMediaPack,
        includes: getAnimationIncludes(animationType)
      } : null
    };
  }


  // Build VR sections (Residential + Amenity) for saving.
  // Used by BOTH SFH and Building mode flows so VR tours never get dropped
  // from saved quotes (the cause of the "VR shows in sidebar but missing
  // from PDF" bug for SFH-mode quotes).
  const buildVrSections = () => {
    return [
      {
        id: crypto.randomUUID(),
        section_type: "viz_vr_residential",
        label: "Residential VR Tours",
        line_items: vizCalc.residentialVRDetails
          .filter(d => d.quantity > 0)
          .flatMap(d => {
            const items = d.tours.map(t => ({
              id: crypto.randomUUID(),
              label: `VR Tour — ${t.displayName}`,
              quantity: 1,
              rate: t.totalCost,
              subtotal: t.totalCost,
              unit_type: "tour",
              metadata: {
                category: "residential",
                tour_type: d.key,
                tour_id: t.id,
                tour_name: t.name,
                tier_label: d.label,
                included_points: d.includedPoints,
                additional_points: t.additionalPoints,
                final_points: t.finalPoints,
                base_rate: d.rate,
                additional_point_rate: additionalPointRate,
                base_cost: t.baseCost,
                additional_cost: t.additionalCost
              }
            }));
            if (d.customizerEnabled && d.customizerCalc.total > 0) {
              items.push({
                id: crypto.randomUUID(),
                label: `Real-Time Customizer — ${d.label}`,
                quantity: 1,
                rate: d.customizerCalc.total,
                subtotal: d.customizerCalc.total,
                unit_type: "customizer",
                metadata: {
                  category: "customizer",
                  tour_type: d.key,
                  base_fee: d.customizerCalc.baseFee,
                  categories_cost: d.customizerCalc.categoriesCost,
                  category_details: d.customizerCalc.categoryDetails
                }
              });
            }
            return items;
          }),
        subtotal: vizCalc.residentialVRSubtotal,
        metadata: {
          total_tours: vizCalc.totalResidentialTours,
          total_additional_points: vizCalc.totalResidentialAdditionalPoints,
          customizer_total: vizCalc.residentialCustomizerTotal
        }
      },
      {
        id: crypto.randomUUID(),
        section_type: "viz_vr_amenity",
        label: "Amenity VR Tours",
        line_items: [
          ...vizCalc.amenityVRDetails
            .filter(d => d.quantity > 0)
            .flatMap(d => {
              const items = d.tours.map(t => ({
                id: crypto.randomUUID(),
                label: `Amenity VR Tour — ${t.displayName}`,
                quantity: 1,
                rate: t.totalCost,
                subtotal: t.totalCost,
                unit_type: "tour",
                metadata: {
                  category: "amenity",
                  tour_type: d.key,
                  tour_id: t.id,
                  tour_name: t.name,
                  tier_label: d.label,
                  included_points: d.includedPoints,
                  additional_points: t.additionalPoints,
                  final_points: t.finalPoints,
                  base_rate: d.rate,
                  additional_point_rate: additionalPointRate,
                  base_cost: t.baseCost,
                  additional_cost: t.additionalCost
                }
              }));
              if (d.customizerEnabled && d.customizerCalc.total > 0) {
                items.push({
                  id: crypto.randomUUID(),
                  label: `Real-Time Customizer — ${d.label}`,
                  quantity: 1,
                  rate: d.customizerCalc.total,
                  subtotal: d.customizerCalc.total,
                  unit_type: "customizer",
                  metadata: {
                    category: "customizer",
                    tour_type: d.key,
                    base_fee: d.customizerCalc.baseFee,
                    categories_cost: d.customizerCalc.categoriesCost,
                    category_details: d.customizerCalc.categoryDetails
                  }
                });
              }
              return items;
            }),
          ...(amenityCustomConfirmed
            ? (vizCalc.customAmenityDetails || []).map(space => ({
                id: crypto.randomUUID(),
                label: `Amenity VR Tour — ${space.name || 'Custom Space'}`,
                quantity: 1,
                rate: space.cost,
                subtotal: space.cost,
                unit_type: "tour",
                metadata: {
                  category: "amenity",
                  tour_type: "custom",
                  custom_space_id: space.id,
                  custom_name: space.name,
                  included_points: space.points,
                  additional_points: 0,
                  final_points: space.points,
                  base_rate: space.rate,
                  base_cost: space.cost,
                  additional_cost: 0,
                  modeling_confirmed: true
                }
              }))
            : [])
        ],
        subtotal: vizCalc.amenityVRSubtotal + (vizCalc.customAmenityApplied || 0),
        metadata: {
          total_tours: vizCalc.totalAmenityTours,
          total_additional_points: vizCalc.totalAmenityAdditionalPoints,
          customizer_total: vizCalc.amenityCustomizerTotal,
          custom_spaces_count: (vizCalc.customAmenityDetails || []).length,
          custom_spaces_confirmed: amenityCustomConfirmed,
          custom_spaces_total: vizCalc.customAmenityApplied || 0
        }
      },
      // VR View-Change Duplicates — same floorplan typology, backplate +
      // 1–3 re-rendered images. Volume-priced tiers ($400/$350/$300/$250/$200)
      // determined by GLOBAL count across residential + amenity tours.
      ...((vizCalc.viewChangeSubtotal || 0) > 0 ? [{
        id: crypto.randomUUID(),
        section_type: "viz_vr_view_change",
        label: "VR View-Change Duplicates",
        line_items: [
          {
            id: crypto.randomUUID(),
            label: `View-Change Duplicates (${vizCalc.totalViewChangeCount} ${vizCalc.totalViewChangeCount === 1 ? "tour" : "tours"})`,
            quantity: vizCalc.totalViewChangeCount,
            rate: vizCalc.viewChangeRate,
            subtotal: vizCalc.viewChangeSubtotal,
            unit_type: "duplicate",
            metadata: {
              total_view_change_count: vizCalc.totalViewChangeCount,
              residential_count: vizCalc.residentialViewChangeCount,
              amenity_count: vizCalc.amenityViewChangeCount,
              tier_rate: vizCalc.viewChangeRate,
              tier_breakpoints: { "1-5": 400, "6-10": 350, "11-20": 300, "21-50": 250, "51+": 200 },
              description: "Same floorplan/typology — backplate change + 1–3 re-rendered images per tour",
            }
          }
        ],
        subtotal: vizCalc.viewChangeSubtotal,
        metadata: {
          total_view_change_count: vizCalc.totalViewChangeCount,
          tier_rate: vizCalc.viewChangeRate,
        }
      }] : [])
    ];
  }


  // Build collateral section for saving
  const buildCollateralSection = () => {
    if (!collateralEnabled || collateralCalc.grandTotal === 0) return [];
    const lineItems = collateralCalc.items.map(item => ({
      id: crypto.randomUUID(),
      label: item.label, quantity: 1, rate: item.amount, subtotal: item.amount, unit_type: "one-time"
    }));
    if (collateralCalc.aiRetainerMonthly > 0) {
      lineItems.push({
        id: crypto.randomUUID(),
        label: `AI Social Media Content (${collateralCalc.aiRetainerTier} clips/mo)`,
        quantity: collateralCalc.retainerMonths,
        rate: collateralCalc.aiRetainerMonthly,
        subtotal: collateralCalc.aiRetainerTotal,
        unit_type: "monthly_retainer",
      });
    }
    return [{
      id: crypto.randomUUID(),
      section_type: "marketing_collateral",
      label: "Collateral Suite",
      line_items: lineItems,
      subtotal: collateralCalc.grandTotal,
      metadata: {
        infra_toggles: collateralState.toggles,
        billboard_option: collateralState.billboardOption,
        ai_retainer_tier: collateralState.aiRetainerTier,
        ai_enhance_qty: collateralState.aiEnhanceQty,
        retainer_months: collateralState.retainerMonths,
        one_time_total: collateralCalc.oneTimeTotal,
        ai_retainer_total: collateralCalc.aiRetainerTotal,
        ai_retainer_monthly: collateralCalc.aiRetainerMonthly,
      }
    }];
  };


  // Build sections for saving
  const buildMarketingSection = () => {
    if (!marketingEnabled || marketingCalc.grandTotal === 0) return [];
    return [{
      id: crypto.randomUUID(),
      section_type: "marketing_strategy",
      label: "Marketing Strategy & Lead Generation",
      line_items: marketingCalc.lineItems.map(item => ({
        id: crypto.randomUUID(), label: item.label, quantity: item.quantity, rate: item.rate, subtotal: item.subtotal
      })),
      subtotal: marketingCalc.grandTotal,
      metadata: {
        mode: marketingMode,
        mode_label: marketingCalc.modeLabel,
        tier_label: marketingCalc.tierLabel,
        cpl: marketingCalc.cpl,
        units: marketingUnits,
        timeline: marketingTimeline,
        conversion_rate: marketingConvRate,
        monthly_leads: marketingCalc.monthlyLeads,
        total_leads: marketingCalc.totalLeads,
        monthly_budget: marketingCalc.monthlyBudget,
        monthly_ad_spend: marketingCalc.monthlyAdSpend,
        ai_animated_enabled: mktgAiAnimatedEnabled,
        ai_animated_qty: mktgAiAnimatedQty,
        ai_enhance_enabled: mktgAiEnhanceEnabled,
        ai_enhance_qty: mktgAiEnhanceQty,
        ai_content_total: marketingCalc.aiContentTotal,
        content_mode: mktgContentMode,
        content_enabled: mktgContentEnabled,
        selected_package: mktgContentMode === "package" ? mktgSelectedPackage : null,
        retainer_enabled: mktgRetainerEnabled,
        retainer_discount: marketingCalc.discount,
        retainer_monthly: marketingCalc.retainerMonthly,
        client_description: marketingCalc.description
      }
    }];
  };

  function buildSections() {
    const replicationRate = getReplicationRate();
    
    // SFH Mode - Simplified sections with Exterior Modeling
    if (vexosQuoteType === "sfh") {
      // Use the tiered Platform Setup Fee from vexosCalc (driven by total units).
      const sfhPlatformFee = vexosCalc.baseSetup;
      return [
        {
          id: crypto.randomUUID(),
          section_type: "vexos_platform",
          label: "VEXOS Platform (SFH)",
          line_items: [
            { id: crypto.randomUUID(), label: "SFH Platform Fee", quantity: 1, rate: sfhPlatformFee, subtotal: sfhPlatformFee }
          ],
          subtotal: sfhPlatformFee,
          metadata: {
            vexos_quote_type: "sfh",
            license_term: vexosPaymentTermMonths,
            vexos_upfront_percent: vexosPayment.effectivePercent,
            vexos_upfront_mode: vexosUpfrontMode,
            vexos_upfront_fixed: vexosUpfrontFixed,
            vexos_payment_term_months: vexosPaymentTermMonths,
            vexos_platform_term_months: vexosPayment.platformTermMonths
          }
        },
        // Exterior Modeling - Same structure as Building mode (includes Site
        // Environment + Reality Capture as line items so they appear in PDFs
        // and roll into the VEXOS Implementation subtotal).
        {
          id: crypto.randomUUID(),
          section_type: "vexos_exterior_modeling",
          label: "Exterior Modeling",
          line_items: [
            ...exteriorStructures
              .filter(s => s.uniqueCount > 0 || s.repeatCount > 0)
              .map(s => {
                const details = exteriorCalc.structureDetails.find(d => d.id === s.id);
                return {
                  id: s.id,
                  label: s.name || "Structure",
                  quantity: s.uniqueCount + s.repeatCount,
                  rate: details?.baseCost || 0,
                  subtotal: details?.totalCost || 0,
                  unit_type: "structure",
                  metadata: {
                    complexity: s.complexity,
                    unique_count: s.uniqueCount,
                    repeat_count: s.repeatCount,
                    base_cost: details?.baseCost || 0,
                    replication_cost: details?.replicationCost || 0
                  }
                };
              }),
            // Site environment line item — included so PDF and subtotal match the UI.
            ...(exteriorCalc.siteEnvironmentCost > 0 ? [{
              id: crypto.randomUUID(),
              label: `Site Environment — ${siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'Standard'}`,
              quantity: 1,
              rate: exteriorCalc.siteEnvironmentCost,
              subtotal: exteriorCalc.siteEnvironmentCost,
              unit_type: "site",
              metadata: {
                item_type: "site_environment",
                environment_type: siteEnvironment
              }
            }] : []),
            // Reality Capture line item
            ...(exteriorCalc.realityCaptureCost > 0 ? [{
              id: crypto.randomUUID(),
              label: "Reality Capture & Environment Reconstruction",
              description: "Drone capture, photogrammetry, terrain reconstruction",
              quantity: 1,
              rate: realityCaptureRate,
              subtotal: realityCaptureRate,
              metadata: {
                item_type: "reality_capture"
              }
            }] : [])
          ],
          // Use exteriorModelingTotal (= structures + site env + reality capture)
          // so the section subtotal matches the calculator UI exactly.
          subtotal: exteriorCalc.exteriorModelingTotal,
          metadata: {
            site_environment: siteEnvironment,
            site_environment_cost: exteriorCalc.siteEnvironmentCost,
            reality_capture_enabled: includeRealityCapture,
            reality_capture_rate: realityCaptureRate,
            total_unique_structures: exteriorCalc.totalUniqueStructures,
            total_replicated: exteriorCalc.totalReplicatedPlacements,
            // Persist FULL exterior_structures array (incl. empty/zero rows + names)
            // so users don't lose typed data when a row hasn't been quantified yet.
            exterior_structures: exteriorStructures,
          }
        },
        // Orbit Configuration Section (only include if there are orbits configured)
        ...(orbitCalc.orbitConfigurationTotal > 0 ? [{
          id: crypto.randomUUID(),
          section_type: "vexos_orbit_configuration",
          label: "Orbit Configuration",
          line_items: [
            // Suborbit line item (internal keys keep the retired name)
            ...(orbitCalc.masterOrbitCost > 0 ? [{
              id: crypto.randomUUID(),
              label: "Suborbit",
              description: "Contextual overview orbit showcasing the project within its surroundings",
              quantity: 1,
              rate: masterOrbitPrice,
              subtotal: orbitCalc.masterOrbitCost,
              unit_type: "orbit",
              metadata: { item_type: "master_orbit", orbit_price: masterOrbitPrice }
            }] : []),
            // General Area Orbit line item
            ...(orbitCalc.generalAreaOrbitCost > 0 ? [{
              id: crypto.randomUUID(),
              label: "General Area Orbit",
              description: "Neighborhood and area context orbit showcasing local surroundings",
              quantity: 1,
              rate: generalAreaOrbitPrice,
              subtotal: orbitCalc.generalAreaOrbitCost,
              unit_type: "orbit",
              metadata: { item_type: "general_area_orbit", orbit_price: generalAreaOrbitPrice }
            }] : []),
            // Additional orbit line items
            ...orbitCalc.additionalOrbitDetails.map(orbit => ({
              id: orbit.id,
              label: orbit.type === "custom" 
                ? `Additional Orbit – Custom: '${orbit.customName || 'Unnamed'}'`
                : `Additional Orbit – ${orbit.typeLabel}`,
              description: ADDITIONAL_ORBIT_TYPES.find(t => t.value === orbit.type)?.description || "",
              quantity: orbit.quantity,
              rate: orbit.unitPrice,
              subtotal: orbit.subtotal,
              unit_type: "orbit",
              metadata: { item_type: "additional_orbit", orbit_type: orbit.type, custom_name: orbit.customName, unit_price: orbit.unitPrice }
            }))
          ],
          subtotal: orbitCalc.orbitConfigurationTotal,
          metadata: {
            master_orbit_enabled: includeMasterOrbit,
            master_orbit_price: masterOrbitPrice,
            general_area_orbit_enabled: includeGeneralAreaOrbit,
            general_area_orbit_price: generalAreaOrbitPrice,
            additional_orbit_price: additionalOrbitPrice,
            total_additional_orbit_count: orbitCalc.totalAdditionalOrbitCount
          }
        }] : []),
        {
          id: crypto.randomUUID(),
          section_type: "vexos_payment",
          label: "VEXOS Platform Investment",
          line_items: [
            { id: crypto.randomUUID(), label: "Upfront Platform Setup Fee", quantity: 1, rate: vexosPayment.setupFee, subtotal: vexosPayment.setupFee, unit_type: "one_time_setup" },
            { id: crypto.randomUUID(), label: "Monthly VEXOS Platform Fee", quantity: 1, rate: vexosPayment.monthlyFee, subtotal: vexosPayment.monthlyFee, unit_type: "monthly_recurring", metadata: { term_months: vexosPayment.contractTermMonths } }
          ],
          // Section subtotal is intentionally 0 — `vexos_payment` is a metadata-only
          // section (not summed into VEXOS scope; see `_VEXOS_SECTION_TYPES` gating
          // in calculators.py L2061). The actual VEXOS scope total lives on
          // `vexos_platform` + `vexos_exterior_modeling` + `vexos_orbit_configuration`
          // + `vexos_floorplates`.
          subtotal: 0,
          metadata: {
            // New SaaS-model fields
            setup_fee: vexosPayment.setupFee,
            monthly_fee: vexosPayment.monthlyFee,
            contract_term_months: vexosPayment.contractTermMonths,
            due_at_signing: vexosPayment.dueAtSigning,
            recurring_monthly: vexosPayment.recurringMonthly,
            // Internal-only — for analytics/backend, never rendered
            _recurring_contract_value: vexosPayment._recurringContractValue,
            _total_contract_value: vexosPayment._totalContractValue,
            // Legacy back-compat
            upfront_payment: vexosPayment.setupFee,
            monthly_payment: vexosPayment.monthlyFee,
            payment_term_months: vexosPayment.contractTermMonths,
            platform_term_months: vexosPayment.contractTermMonths,
            vexos_quote_type: "sfh"
          }
        },
        // Include visualization sections even in SFH mode
        {
          id: crypto.randomUUID(),
          section_type: "viz_interior_modeling",
          label: "Interior Modeling",
          line_items: [
            ...(vizCalc.interiorModelingCost > 0 ? [{ id: crypto.randomUUID(), label: "Interior Modeling", quantity: interiorSqft, rate: vizCalc.interiorModelingRate, subtotal: vizCalc.interiorModelingCost, unit_type: "sqft", metadata: { sqft: interiorSqft, combined_interior_sqft: vizCalc.combinedInteriorSqft, tier_label: vizCalc.interiorModelingTierLabel } }] : []),
            ...(vizCalc.amenityModelingCost > 0 ? vizCalc.amenityDetails.map(a => ({ id: crypto.randomUUID(), label: `${a.name || "Amenity"} Interior${a.structureFee > 0 ? " + Structure" : ""}`, quantity: 1, rate: a.totalCost, subtotal: a.totalCost, unit_type: "amenity_sqft", metadata: { sqft: a.sqft, complexity: a.complexity, modelingType: a.modelingType, structureSize: a.structureSize, sharedGroupId: a.sharedGroupId } })) : [])
          ],
          subtotal: vizCalc.interiorModelingSubtotal,
          // Persist raw UI state in metadata so empty/zero-cost amenities survive a
          // round-trip. Hydration reads from metadata.amenity_entries first.
          metadata: {
            interior_sqft: interiorSqft,
            amenity_entries: amenityEntries,
          }
        },
        {
          id: crypto.randomUUID(),
          section_type: "viz_rendering",
          label: "Renderings",
          line_items: [
            ...(vizCalc.interiorRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Interior Renderings", quantity: interiorRenders, rate: vizCalc.interiorRenderRate, subtotal: vizCalc.interiorRenderCost, unit_type: "image" }] : []),
            ...(vizCalc.exteriorRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Exterior Renderings", quantity: exteriorRenders, rate: vizCalc.exteriorRenderRate, subtotal: vizCalc.exteriorRenderCost, unit_type: "image" }] : []),
            ...(vizCalc.aerialRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Aerial Renderings", quantity: aerialRenders, rate: vizCalc.aerialRate, subtotal: vizCalc.aerialRenderCost, unit_type: "image" }] : []),
            ...(vizCalc.floorPlanCost > 0 ? [{ id: crypto.randomUUID(), label: "Floor Plans", quantity: floorPlans, rate: floorPlanRate, subtotal: vizCalc.floorPlanCost, unit_type: "image" }] : [])
          ],
          subtotal: vizCalc.renderingSubtotal
        },
        // VR sections — included in SFH mode too so tours never get dropped
        // from saved quotes (root cause of "VR shown in sidebar but missing
        // from PDF" bug for SFH-mode quotes).
        ...buildVrSections(),
        buildAnimationSection(),
        // Conceptual Exterior Modeling section (SFH mode)
        ...(conceptModelingCalc.grandTotal > 0 ? [{
          id: crypto.randomUUID(),
          section_type: "concept_exterior_modeling",
          label: "Conceptual Exterior Modeling",
          line_items: conceptModelingCalc.lineItems.map(item => ({
            id: crypto.randomUUID(),
            label: item.label,
            quantity: item.quantity,
            rate: item.rate,
            subtotal: item.subtotal,
            unit_type: item.quantity > 1 ? "building" : "addon"
          })),
          subtotal: conceptModelingCalc.grandTotal,
          metadata: {
            approach: conceptApproach,
            buildings: conceptBuildings,
            modification_level: conceptApproach === "plus" ? conceptModLevel : null,
            complexity_level: conceptApproach === "studio" ? conceptComplexity : null,
            site_context: conceptSiteContext,
            render_optimization: conceptRenderOptimization,
            vexos_structuring: conceptVexosStructuring,
            client_description: conceptModelingCalc.description
          }
        }] : []),
        ...buildMarketingSection(),
        ...buildCollateralSection()
      ];
    }
    
    // Building Mode - Full sections (existing logic)
    return [
      {
        id: crypto.randomUUID(),
        section_type: "vexos_platform",
        label: "VEXOS Platform Configuration",
        line_items: [
          { id: crypto.randomUUID(), label: "Platform Setup", quantity: 1, rate: vexosCalc.baseSetup, subtotal: vexosCalc.baseSetup },
          ...(vexosCalc.additionalBuildingsCost > 0 ? [{ id: crypto.randomUUID(), label: "Additional Buildings", quantity: residentialBuildings - 1, rate: getRate("additional residential", "vexos"), subtotal: vexosCalc.additionalBuildingsCost, unit_type: "building" }] : []),
          ...(vexosCalc.amenityCost > 0 ? [{ id: crypto.randomUUID(), label: "Amenity Buildings", quantity: amenityBuildings, rate: getRate("amenity building", "vexos"), subtotal: vexosCalc.amenityCost, unit_type: "building" }] : []),
          { id: crypto.randomUUID(), label: "Floors Covered", quantity: 0, rate: 0, subtotal: 0, metadata: { floors_covered: floorsCovered, coverage_notes: coverageNotes, planning_only: true } },
          { id: crypto.randomUUID(), label: "Inventory Programming", quantity: residentialUnits, rate: vexosCalc.effectiveUnitRate || getRate("residential unit", "vexos"), subtotal: vexosCalc.unitsCost, unit_type: "unit", metadata: { tiered_pricing: true } }
        ],
        subtotal: vexosCalc.platformSubtotal,
        metadata: {
          vexos_quote_type: "building",
          license_term: vexosPaymentTermMonths,
          vexos_upfront_percent: vexosUpfrontPercent,
          vexos_payment_term_months: vexosPaymentTermMonths,
          floorplate_enabled: floorplateEnabled,
          // Persist raw residential buildings count so it survives single-building
          // quotes (which don't emit an "Additional Buildings" line item).
          residential_buildings: residentialBuildings
        }
      },
      // Exterior Modeling Section (VEXOS)
      {
        id: crypto.randomUUID(),
        section_type: "vexos_exterior_modeling",
        label: "Exterior Modeling",
        line_items: [
          // Structure line items
          ...exteriorCalc.structureDetails.filter(s => s.uniqueCount > 0 || s.repeatCount > 0).map(s => ({
            id: s.id,
            label: s.name || `${complexityTiers.find(t => t.value === s.complexity)?.label || 'Medium'} Structure`,
            quantity: s.uniqueCount,
            rate: s.tierRate,
            subtotal: s.totalCost,
            unit_type: "structure",
            metadata: {
              item_type: "structure",
              structure_name: s.name,
              complexity_tier: s.complexity,
              tier_rate: s.tierRate,
              unique_count: s.uniqueCount,
              repeat_count: s.repeatCount,
              replication_rate: replicationRate,
              primary_cost: s.primaryCost,
              replication_cost: s.replicationCost,
              notes: s.notes
            }
          })),
          // Site environment line item
          ...(exteriorCalc.siteEnvironmentCost > 0 ? [{
            id: crypto.randomUUID(),
            label: `Site Environment — ${siteEnvironments.find(e => e.value === siteEnvironment)?.label || 'Standard'}`,
            quantity: 1,
            rate: exteriorCalc.siteEnvironmentCost,
            subtotal: exteriorCalc.siteEnvironmentCost,
            unit_type: "site",
            metadata: {
              item_type: "site_environment",
              environment_type: siteEnvironment
            }
          }] : []),
          // Reality capture line item
          ...(exteriorCalc.realityCaptureCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Reality Capture & Environment Reconstruction",
            description: "Drone capture, photogrammetry, terrain reconstruction",
            quantity: 1,
            rate: realityCaptureRate,
            subtotal: realityCaptureRate,
            metadata: {
              item_type: "reality_capture"
            }
          }] : [])
        ],
        subtotal: exteriorCalc.exteriorModelingTotal,
        metadata: {
          total_unique_structures: exteriorCalc.totalUniqueStructures,
          total_replicated_placements: exteriorCalc.totalReplicatedPlacements,
          primary_structure_subtotal: exteriorCalc.primaryStructureSubtotal,
          replication_subtotal: exteriorCalc.replicationSubtotal,
          site_environment: siteEnvironment,
          site_environment_cost: exteriorCalc.siteEnvironmentCost,
          reality_capture_enabled: includeRealityCapture,
          reality_capture_rate: realityCaptureRate,
          // Persist FULL exterior_structures array so empty/zero rows + names survive reload
          exterior_structures: exteriorStructures,
        }
      },
      // Orbit Configuration Section (only include if there are orbits configured)
      ...(orbitCalc.orbitConfigurationTotal > 0 ? [{
        id: crypto.randomUUID(),
        section_type: "vexos_orbit_configuration",
        label: "Orbit Configuration",
        line_items: [
          // Suborbit line item (internal keys keep the retired name)
          ...(orbitCalc.masterOrbitCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "Suborbit",
            description: "Contextual overview orbit showcasing the project within its surroundings",
            quantity: 1,
            rate: masterOrbitPrice,
            subtotal: orbitCalc.masterOrbitCost,
            unit_type: "orbit",
            metadata: { item_type: "master_orbit", orbit_price: masterOrbitPrice }
          }] : []),
          // General Area Orbit line item
          ...(orbitCalc.generalAreaOrbitCost > 0 ? [{
            id: crypto.randomUUID(),
            label: "General Area Orbit",
            description: "Neighborhood and area context orbit showcasing local surroundings",
            quantity: 1,
            rate: generalAreaOrbitPrice,
            subtotal: orbitCalc.generalAreaOrbitCost,
            unit_type: "orbit",
            metadata: { item_type: "general_area_orbit", orbit_price: generalAreaOrbitPrice }
          }] : []),
          // Additional orbit line items
          ...orbitCalc.additionalOrbitDetails.map(orbit => ({
            id: orbit.id,
            label: orbit.type === "custom" 
              ? `Additional Orbit – Custom: '${orbit.customName || 'Unnamed'}'`
              : `Additional Orbit – ${orbit.typeLabel}`,
            description: ADDITIONAL_ORBIT_TYPES.find(t => t.value === orbit.type)?.description || "",
            quantity: orbit.quantity,
            rate: orbit.unitPrice,
            subtotal: orbit.subtotal,
            unit_type: "orbit",
            metadata: { item_type: "additional_orbit", orbit_type: orbit.type, custom_name: orbit.customName, unit_price: orbit.unitPrice }
          }))
        ],
        subtotal: orbitCalc.orbitConfigurationTotal,
        metadata: {
          master_orbit_enabled: includeMasterOrbit,
          master_orbit_price: masterOrbitPrice,
          general_area_orbit_enabled: includeGeneralAreaOrbit,
          general_area_orbit_price: generalAreaOrbitPrice,
          additional_orbit_price: additionalOrbitPrice,
          total_additional_orbit_count: orbitCalc.totalAdditionalOrbitCount
        }
      }] : []),
      ...(floorplateEnabled ? [{
        id: crypto.randomUUID(),
        section_type: "vexos_floorplates",
        label: "Floorplate Development",
        line_items: [
          { id: crypto.randomUUID(), label: "Unique Floorplates", quantity: uniqueFloorplates, rate: vexosCalc.uniqueFloorplateRate, subtotal: vexosCalc.uniqueFloorplateCost, unit_type: "floorplate" },
          { id: crypto.randomUUID(), label: "Repeat Floorplates", quantity: repeatFloorplates, rate: vexosCalc.repeatFloorplateRate, subtotal: vexosCalc.repeatFloorplateCost, unit_type: "floorplate" }
        ],
        subtotal: vexosCalc.floorplateSubtotal,
        metadata: { floors_covered: floorsCovered, total_floorplates: uniqueFloorplates + repeatFloorplates, coverage_status: coverage.status, floorplate_enabled: true }
      }] : []),
      {
        id: crypto.randomUUID(),
        section_type: "vexos_payment",
        label: "VEXOS Platform Investment",
        line_items: [
          { id: crypto.randomUUID(), label: "Upfront Platform Setup Fee", quantity: 1, rate: vexosPayment.setupFee, subtotal: vexosPayment.setupFee, unit_type: "one_time_setup" },
          { id: crypto.randomUUID(), label: "Monthly VEXOS Platform Fee", quantity: 1, rate: vexosPayment.monthlyFee, subtotal: vexosPayment.monthlyFee, unit_type: "monthly_recurring", metadata: { term_months: vexosPayment.contractTermMonths } }
        ],
        // Metadata-only section — subtotal=0 so it's never double-counted into VEXOS scope.
        subtotal: 0,
        metadata: {
          setup_fee: vexosPayment.setupFee,
          monthly_fee: vexosPayment.monthlyFee,
          contract_term_months: vexosPayment.contractTermMonths,
          due_at_signing: vexosPayment.dueAtSigning,
          recurring_monthly: vexosPayment.recurringMonthly,
          _recurring_contract_value: vexosPayment._recurringContractValue,
          _total_contract_value: vexosPayment._totalContractValue,
          upfront_payment: vexosPayment.setupFee,
          monthly_payment: vexosPayment.monthlyFee,
          payment_term_months: vexosPayment.contractTermMonths,
          platform_term_months: vexosPayment.contractTermMonths
        }
      },
      {
        id: crypto.randomUUID(),
        section_type: "viz_interior_modeling",
        label: "Interior Modeling",
        line_items: [
          ...(vizCalc.interiorModelingCost > 0 ? [{ id: crypto.randomUUID(), label: "Interior Modeling", quantity: interiorSqft, rate: vizCalc.interiorModelingRate, subtotal: vizCalc.interiorModelingCost, unit_type: "sqft", metadata: { sqft: interiorSqft, combined_interior_sqft: vizCalc.combinedInteriorSqft, tier_label: vizCalc.interiorModelingTierLabel } }] : []),
          ...(vizCalc.amenityModelingCost > 0 ? vizCalc.amenityDetails.map(a => ({ id: crypto.randomUUID(), label: `${a.name || "Amenity"} Interior${a.structureFee > 0 ? " + Structure" : ""}`, quantity: 1, rate: a.totalCost, subtotal: a.totalCost, unit_type: "amenity_sqft", metadata: { sqft: a.sqft, complexity: a.complexity, modelingType: a.modelingType } })) : [])
        ],
        subtotal: vizCalc.interiorModelingSubtotal,
        // Persist raw UI state in metadata so empty/zero-cost amenities survive a round-trip.
        metadata: {
          interior_sqft: interiorSqft,
          amenity_entries: amenityEntries,
        }
      },
      {
        id: crypto.randomUUID(),
        section_type: "viz_rendering",
        label: "Renderings",
        line_items: [
          ...(vizCalc.interiorRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Interior Renderings", quantity: interiorRenders, rate: vizCalc.interiorRenderRate, subtotal: vizCalc.interiorRenderCost, unit_type: "image" }] : []),
          ...(vizCalc.exteriorRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Exterior Renderings", quantity: exteriorRenders, rate: vizCalc.exteriorRenderRate, subtotal: vizCalc.exteriorRenderCost, unit_type: "image" }] : []),
          ...(vizCalc.aerialRenderCost > 0 ? [{ id: crypto.randomUUID(), label: "Aerial Renderings", quantity: aerialRenders, rate: vizCalc.aerialRate, subtotal: vizCalc.aerialRenderCost, unit_type: "image" }] : []),
          ...(vizCalc.floorPlanCost > 0 ? [{ id: crypto.randomUUID(), label: "3D Floor Plans", quantity: floorPlans, rate: floorPlanRate, subtotal: vizCalc.floorPlanCost, unit_type: "plan" }] : [])
        ],
        subtotal: vizCalc.renderingSubtotal
      },
      ...buildVrSections(),
      buildAnimationSection(),
      // Interior Design Direction section (only if fee > 0)
      ...(interiorDesignDirectionCalc.totalFee > 0 ? [{
        id: crypto.randomUUID(),
        section_type: "viz_interior_design_direction",
        label: "Interior Design Direction",
        line_items: interiorDesignDirectionCalc.spaces.map(space => ({
          id: space.id,
          label: space.name,
          quantity: 1,
          rate: space.fee,
          subtotal: space.fee,
          unit_type: "space",
          metadata: {
            completeness_level: space.completeness_level,
            level_label: space.levelLabel,
            notes: space.notes
          }
        })),
        subtotal: interiorDesignDirectionCalc.totalFee,
        metadata: {
          auto_detected_spaces: autoDetectedInteriorSpaces,
          overridden_spaces: interiorDesignSpacesOverride,
          total_spaces: effectiveInteriorSpaceCount,
          spaces: interiorDesignSpaces.map(s => ({
            name: s.name,
            completeness_level: s.completeness_level,
            fee: interiorDesignDirectionCalc.spaces.find(sp => sp.id === s.id)?.fee || 0,
            notes: s.notes
          }))
        }
      }] : []),
      // Conceptual Exterior Modeling section (only if enabled)
      ...(conceptModelingCalc.grandTotal > 0 ? [{
        id: crypto.randomUUID(),
        section_type: "concept_exterior_modeling",
        label: "Conceptual Exterior Modeling",
        line_items: conceptModelingCalc.lineItems.map(item => ({
          id: crypto.randomUUID(),
          label: item.label,
          quantity: item.quantity,
          rate: item.rate,
          subtotal: item.subtotal,
          unit_type: item.quantity > 1 ? "building" : "addon"
        })),
        subtotal: conceptModelingCalc.grandTotal,
        metadata: {
          approach: conceptApproach,
          buildings: conceptBuildings,
          modification_level: conceptApproach === "plus" ? conceptModLevel : null,
          complexity_level: conceptApproach === "studio" ? conceptComplexity : null,
          site_context: conceptSiteContext,
          render_optimization: conceptRenderOptimization,
          vexos_structuring: conceptVexosStructuring,
          client_description: conceptModelingCalc.description
        }
      }] : []),
      ...buildMarketingSection(),
      ...buildCollateralSection()
    ];
  }

  async function handleSave({ silent = false } = {}) {
    if (!projectName.trim()) {
      if (!silent) toast.error("Project name required");
      return false;
    }
    if (!clientName.trim()) {
      if (!silent) toast.error("Client name required");
      return false;
    }
    if (!salesperson) {
      if (!silent) toast.error("Select a salesperson");
      return false;
    }

    if (!silent) setSaving(true);
    if (silent) setAutoSaveStatus("saving");
    let payload = null;
    try {
      // PERSIST EVERYTHING. Do NOT filter sections by module-enabled state at save time —
      // that previously caused data loss when a user toggled Visualization/VEXOS OFF.
      // Module enable state lives in payment_plan.module_state and is honored by the
      // backend PDF generator + frontend totals (vexosEnabledTotal / vizEnabledTotal etc.)
      // so we keep ALL section data intact.
      const _allSections = buildSections();
      let _filteredSections = _allSections;
      // When VEXOS is OFF but Viz is ON and exterior modeling data exists, ALSO surface
      // the exterior line items under Visualization (so they appear in the PDF + contribute
      // to the viz total). This is additive — original vexos_exterior_modeling section is
      // still preserved above for round-trip data safety.
      if (!vexosModuleEnabled && vizModuleEnabled && exteriorCalc.exteriorModelingTotal > 0) {
        const _vexosExt = _allSections.find(s => s.section_type === "vexos_exterior_modeling");
        if (_vexosExt) {
          _filteredSections = [..._filteredSections, {
            ..._vexosExt,
            id: crypto.randomUUID(),
            section_type: "viz_exterior_modeling",
            label: "Exterior Modeling",
          }];
        }
      }
      // AI Content Studio — emit when enabled & has at least one selected deliverable.
      if (aiContentModuleEnabled && aiContentTotal > 0) {
        // Build line items honoring motion-variant single-select semantics.
        const _aiLines = aiContentItems
          .filter(it => {
            if (it.isMotionVariant) return !!it.selectedVariantId;
            return (it.qty || 0) > 0;
          })
          .map(it => {
            if (it.isMotionVariant) {
              const v = (it.variants || []).find(x => x.id === it.selectedVariantId);
              const _rate = v ? v.rate : 0;
              return {
                label: `${it.label} — ${v ? v.label : ""}`.trim(),
                quantity: 1,
                rate: _rate,
                subtotal: _rate,
                unit_type: "package",
                metadata: { description: it.description, motion_variant_id: it.selectedVariantId },
              };
            }
            return {
              label: it.label,
              quantity: it.qty,
              rate: it.rate,
              subtotal: (it.qty || 0) * (it.rate || 0),
              unit_type: "package",
              metadata: { description: it.description },
            };
          });
        if (_aiLines.length > 0) {
          _filteredSections.push({
            id: crypto.randomUUID(),
            section_type: "ai_content_studio",
            label: "AI Content Studio",
            line_items: _aiLines,
            subtotal: aiContentTotal
          });
        }
      }
      // VEXOS Lot Explorer™ — emit a dedicated `interactive_2d_sitemap` section
      // with per-lot line item + payment plan metadata so the backend renders
      // the dedicated PDF section with its own 50/25/25 schedule.
      if (lotExplorerModuleEnabled && lotExplorerCalc.grandTotal > 0) {
        _filteredSections.push({
          id: crypto.randomUUID(),
          section_type: "interactive_2d_sitemap",
          label: "VEXOS Lot Explorer™",
          line_items: lotExplorerCalc.lineItems.map(li => ({ ...li, id: crypto.randomUUID() })),
          subtotal: lotExplorerCalc.grandTotal,
          metadata: {
            lot_count: lotExplorerCalc.lotCount,
            rate_per_lot: lotExplorerCalc.ratePerLot,
            base_fee: lotExplorerCalc.baseFee,
            features: lotExplorerCalc.features,
            include_poi: lotExplorerIncludePoi,
            include_crm: lotExplorerIncludeCrm,
            include_media: lotExplorerIncludeMedia,
            include_backend: lotExplorerIncludeBackend,
            timeline_weeks: lotExplorerCalc.timelineWeeks,
            timeline_default_weeks: lotExplorerCalc.timelineDefaultWeeks,
            timeline_is_overridden: lotExplorerCalc.timelineIsOverridden,
            hosting_monthly: lotExplorerCalc.hostingMonthly,
            hosting_free_months: lotExplorerCalc.hostingFreeMonths,
            hosting_lock_months: lotExplorerCalc.hostingLockMonths,
            hosting_lock_in_value: lotExplorerCalc.hostingLockInValue,
            payment_plan: {
              type: "lot_explorer_50_25_25",
              total: lotExplorerPayment.total,
              deposit: lotExplorerPayment.deposit,
              v1_delivery: lotExplorerPayment.v1Delivery,
              final_delivery: lotExplorerPayment.finalDelivery,
              milestones: lotExplorerPayment.milestones
            }
          }
        });
      }
      payload = {
        project_name: projectName,
        client_name: clientName,
        salesperson,
        // Project Intake metadata — pure documentation fields persisted alongside
        // the quote so they flow into the proposal PDF and PM handoff. No pricing impact.
        project_metadata: {
          lot_sq_ft: lotSqFt === "" ? null : Number(lotSqFt),
          unique_floorplan_count: uniqueFloorplanCount === "" ? null : Number(uniqueFloorplanCount),
          avg_floorplan_sq_ft: avgFloorplanSqFt === "" ? null : Number(avgFloorplanSqFt),
          unit_count: unitCountIntake === "" ? null : Number(unitCountIntake),
          development_type: developmentType || null,
        },
        quote_type: "combined",
        sections: _filteredSections,
        payment_plan: {
          preset: paymentPreset,
          milestones: paymentPreset === "custom" ? customMilestones : PAYMENT_PRESETS[paymentPreset].milestones,
          one_time_total: oneTimeTotal,
          visualization_scope_total: visualizationScopeTotal,
          vexos_payment: {
            // NEW canonical SaaS-model fields
            setup_fee: vexosPayment.setupFee,
            monthly_fee: vexosPayment.monthlyFee,
            contract_term_months: vexosPayment.contractTermMonths,
            due_at_signing: vexosPayment.dueAtSigning,
            recurring_monthly: vexosPayment.recurringMonthly,
            // Internal-only — analytics/backend, never rendered to user
            _recurring_contract_value: vexosPayment._recurringContractValue,
            _total_contract_value: vexosPayment._totalContractValue,
            // Legacy back-compat (kept so older PDF code paths still resolve)
            total: vexosPayment.totalVexos,
            upfront_payment: vexosPayment.setupFee,
            monthly_payment: vexosPayment.monthlyFee,
            payment_term_months: vexosPayment.contractTermMonths,
            platform_term_months: vexosPayment.contractTermMonths,
            upfront_percent: 100,
          },
          lot_explorer_payment: lotExplorerModuleEnabled && lotExplorerCalc.grandTotal > 0 ? {
            type: "lot_explorer_50_25_25",
            total: lotExplorerPayment.total,
            deposit: lotExplorerPayment.deposit,
            v1_delivery: lotExplorerPayment.v1Delivery,
            final_delivery: lotExplorerPayment.finalDelivery,
            lot_count: lotExplorerCalc.lotCount,
            rate_per_lot: lotExplorerCalc.ratePerLot,
            base_fee: lotExplorerCalc.baseFee,
            features: lotExplorerCalc.features,
            hosting_monthly: lotExplorerCalc.hostingMonthly,
            hosting_free_months: lotExplorerCalc.hostingFreeMonths,
            hosting_lock_months: lotExplorerCalc.hostingLockMonths,
            hosting_lock_in_value: lotExplorerCalc.hostingLockInValue
          } : null,
          total_due_today: totalDueToday,
          viz_v1_delivery_week_override: (vizV1DeliveryWeekOverride !== "" && Number(vizV1DeliveryWeekOverride) > 0)
            ? Number(vizV1DeliveryWeekOverride)
            : null,
          module_state: {
            vexos_enabled: vexosModuleEnabled,
            viz_enabled: vizModuleEnabled,
            ai_content_enabled: aiContentModuleEnabled,
            lot_explorer_enabled: lotExplorerModuleEnabled,
            // Newly persisted toggles — were previously missing from module_state,
            // which caused the backend reconcile + PDF generator to count
            // saved concept/marketing/collateral sections even when the user
            // toggled those modules OFF. Result: false-positive "PDF Mismatch"
            // banner AND silent inclusion of disabled scope in the generated PDF.
            concept_modeling_enabled: conceptModelingEnabled,
            marketing_enabled: marketingEnabled,
            collateral_enabled: collateralEnabled
          },
          special_discount_pct: _safeDiscountPct,
          discount_amount: discountAmount,
          enabled_subtotal: enabledSubtotal,
          enabled_subtotal_after_discount: enabledSubtotalAfterDiscount
        },
        total_one_time: enabledSubtotalAfterDiscount,
        grand_total: enabledSubtotalAfterDiscount,
        timeline_override_weeks: (timelineOverrideEnabled && parseFloat(timelineOverrideWeeks) > 0)
          ? parseFloat(timelineOverrideWeeks)
          : null,
        notes
      };

      if (isNew) {
        const res = await apiClient.post("/calculators/quotes", payload);
        if (!silent) toast.success("Quote created");
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(prev => prev === "saved" ? "idle" : prev), 2000);
        // For new quotes the URL has /new; navigate to the assigned id so subsequent
        // autosaves hit PUT /quotes/:id and don't keep creating new records.
        navigate(`/sales/calculators/combined/${res.data.id}`, { replace: true });
        return true;
      } else {
        await apiClient.put(`/calculators/quotes/${quoteId}`, payload);
        if (!silent) toast.success("Quote saved");
        setAutoSaveStatus("saved");
        setTimeout(() => setAutoSaveStatus(prev => prev === "saved" ? "idle" : prev), 2000);
        // Re-run pre-flight reconciliation after every successful save so the
        // sidebar badge reflects the freshly-persisted subtotal.
        refreshReconcile();
        return true;
      }
    } catch (err) {
      if (!silent) toast.error("Failed to save");
      setAutoSaveStatus("error");
      // Local draft fallback — cache last good payload so user doesn't lose work
      // on reload even if the autosave round-trip fails.
      try {
        if (typeof window !== "undefined" && window.localStorage && quoteId && quoteId !== "new") {
          window.localStorage.setItem(`trp_quote_draft_${quoteId}`, JSON.stringify({ ts: Date.now(), payload }));
        }
      } catch (_) { /* ignore quota errors */ }
      return false;
    } finally {
      if (!silent) setSaving(false);
    }
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }

  // Keep a ref to handleSave so the autosave effect can invoke the latest closure
  // without re-registering its long dependency list every render.
  handleSaveRef.current = handleSave;

  // Flush any pending autosave immediately. Returns a promise that resolves when the
  // in-flight save completes (or immediately if none is queued).
  const flushPendingQuoteSave = async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (autoSavePendingRef.current) {
      autoSavePendingRef.current = false;
      if (handleSaveRef.current) {
        await handleSaveRef.current({ silent: true });
      }
    }
  };

  // Debounced autosave — schedule a silent save 1500ms after any quote-input change.
  // We DON'T autosave brand-new quotes (they need an explicit Save first to obtain an id);
  // once the URL has a real quoteId, autosave takes over.
  // Effect intentionally has minimal deps — it tracks data via the broader render loop
  // since `loading`/`isNew` gate it correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (loading || isNew) return;
    // Mark dirty + reset debounce
    autoSavePendingRef.current = true;
    setAutoSaveStatus(prev => (prev === "saved" ? "idle" : prev));
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      if (!autoSavePendingRef.current) return;
      autoSavePendingRef.current = false;
      if (handleSaveRef.current) {
        handleSaveRef.current({ silent: true });
      }
    }, 1500);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [
    // Project metadata
    projectName, clientName, salesperson, notes,
    // Module toggles
    vexosModuleEnabled, vizModuleEnabled, aiContentModuleEnabled,
    marketingEnabled, collateralEnabled, conceptModelingEnabled, animationEnabled, includeRealityCapture,
    lotExplorerModuleEnabled,
    // VEXOS Lot Explorer™
    lotExplorerLotCount, lotExplorerRatePerLot,
    lotExplorerIncludePoi, lotExplorerIncludeCrm, lotExplorerIncludeMedia, lotExplorerIncludeBackend,
    lotExplorerTimelineWeeks, lotExplorerTimelineOverrideEnabled, lotExplorerTimelineOverrideWeeks,
    lotExplorerHostingMonthly, lotExplorerHostingFreeMonths, lotExplorerHostingLockMonths,
    // VEXOS scope
    vexosQuoteType, projectSize, floorplateEnabled, uniqueFloorplates, repeatFloorplates,
    sfhInventory, includeMasterOrbit, includeGeneralAreaOrbit, additionalOrbits,
    exteriorStructures, siteEnvironment,
    // Visualization scope
    interiorSqft, amenityEntries, interiorRenders, exteriorRenders, aerialRenders, floorPlans,
    vrResidential, vrAmenity, animationType, animationDuration, socialMediaPack,
    // Marketing
    marketingMode, marketingTier, marketingUnits, marketingTimeline, marketingConvRate,
    mktgContentEnabled, mktgContentMode, mktgSelectedPackage, mktgRetainerEnabled,
    mktgAiAnimatedEnabled, mktgAiAnimatedQty, mktgAiEnhanceEnabled, mktgAiEnhanceQty,
    // Conceptual modeling
    conceptApproach, conceptBuildings, conceptModLevel, conceptComplexity, conceptLandmarkPrice,
    conceptSiteContext, conceptRenderOptimization, conceptVexosStructuring,
    // Collateral
    collateralState,
    // AI Content Studio
    aiContentItems,
    // Pricing/payment
    paymentPreset, customMilestones, vexosUpfrontMode, vexosUpfrontPercent, vexosUpfrontFixed,
    vexosPaymentTermMonths, specialDiscountPct,
    // Timeline overrides
    timelineOverrideEnabled, timelineOverrideWeeks, vizV1DeliveryWeekOverride,
  ]);

  // Save-on-navigation: flush any pending autosave before unload.
  useEffect(() => {
    const handler = (e) => {
      if (autoSavePendingRef.current) {
        // Best-effort sync flush — browsers limit work in beforeunload, so we just
        // warn the user. The autosave timer also triggers within 1.5s of typing so
        // most navigations leave nothing pending.
        e.preventDefault();
        e.returnValue = "";
        // Fire-and-forget save; navigator.sendBeacon would be ideal but we need
        // auth headers — fall back to the regular save which usually completes.
        if (handleSaveRef.current) handleSaveRef.current({ silent: true });
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  async function handleGenerateProposal() {
    if (!quoteId || isNew) {
      toast.error("Please save the quote first");
      return;
    }

    // Flush pending autosave so the proposal pulls from the latest persisted state.
    await flushPendingQuoteSave();

    setGeneratingProposal(true);
    try {
      const res = await apiClient.post(`/calculators/quotes/${quoteId}/generate-proposal`, {
        format: "html",
        presentation_mode: presentationMode
      });
      setProposalHtml(res.data.html);
      toast.success("Proposal generated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate proposal");
    } finally {
      setGeneratingProposal(false);
    }
  }

  function handlePrintProposal() {
    const printWindow = window.open('', '_blank');
    // Inject blank-page guard + orphan-header guard.
    // Runs after layout settles, then triggers print with clean pagination.
    const cleanupScript = `
      <script>
        (function() {
          function hasMeaningfulPageContent(el) {
            if (!el) return false;
            try {
              const text = (el.textContent || '').trim();
              if (text.length === 0) return false;
              const rect = el.getBoundingClientRect();
              if (rect.width <= 0 || rect.height < 20) return false;
              const style = window.getComputedStyle(el);
              if (style.display === 'none' || style.visibility === 'hidden') return false;
              const footerStripped = text
                .replace(/Proposal for .*/gi, '')
                .replace(/Page \\\\d+ of \\\\d+/gi, '')
                .trim();
              return footerStripped.length > 0;
            } catch (_e) { return true; }
          }
          function pruneEmptyPages() {
            const candidates = document.querySelectorAll('.section, .page, .pdf-page, .overview-section, [data-pdf-section]');
            let removed = 0;
            candidates.forEach(function(el) {
              if (!hasMeaningfulPageContent(el)) {
                el.style.pageBreakBefore = 'auto';
                el.style.breakBefore = 'auto';
                el.style.display = 'none';
                removed++;
              }
            });
            return removed;
          }
          function reinforcePagination() {
            // For every section with a forced page-break-before, ensure the
            // title + intro + first content card stay on the same page by
            // wrapping them in a same-page group via inline break hints.
            const sections = document.querySelectorAll('.section, .overview-section');
            sections.forEach(function(sec) {
              const title = sec.querySelector('.section-title, .narrative-page-title');
              if (!title) return;
              // Collect all elements between title and the first .section-card / .subsection / .pi-block / .summary
              let node = title;
              while (node && node.nextElementSibling) {
                node.style.breakAfter = 'avoid-page';
                node.style.pageBreakAfter = 'avoid';
                if (node.classList && (
                  node.classList.contains('section-card') ||
                  node.classList.contains('subsection') ||
                  node.classList.contains('pi-block') ||
                  node.classList.contains('summary') ||
                  node.classList.contains('payment-plan'))) {
                  break;
                }
                node = node.nextElementSibling;
              }
            });
          }
          window.addEventListener('load', function() {
            setTimeout(function() {
              try { reinforcePagination(); } catch (_e) {}
              try { pruneEmptyPages(); } catch (_e) {}
              setTimeout(function() { window.print(); }, 100);
            }, 200);
          });
        })();
      <\/script>
    `;
    let html = proposalHtml || '';
    if (html.includes('</body>')) {
      html = html.replace('</body>', cleanupScript + '</body>');
    } else {
      html = html + cleanupScript;
    }
    printWindow.document.write(html);
    printWindow.document.close();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Summary Line Item Component
  // Inline editable slider value badge — click to type, Enter/blur to commit, Esc to cancel.
  // Clamps to [min, max] and respects integer-only mode for whole-number values.
  const EditableSliderBadge = ({ value, min, max, onChange, suffix = "", integer = true, decimals = 1, testId, step = 1, formatter }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value));
    useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);
    const snap = (n) => {
      if (step <= 1) return n;
      // Snap to nearest `step` interval, anchored at `min`.
      return min + Math.round((n - min) / step) * step;
    };
    const commit = () => {
      const raw = String(draft).trim().replace(/[^\d.\-]/g, "");
      let n = integer ? parseInt(raw, 10) : parseFloat(raw);
      if (Number.isNaN(n)) { setEditing(false); setDraft(String(value)); return; }
      if (n < min) n = min;
      if (n > max) n = max;
      if (!integer) n = Math.round(n * 10 ** decimals) / 10 ** decimals;
      n = snap(n);
      onChange(n);
      setEditing(false);
    };
    if (editing) {
      return (
        <input
          type="text"
          inputMode={integer ? "numeric" : "decimal"}
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") { setDraft(String(value)); setEditing(false); }
          }}
          onFocus={(e) => e.target.select()}
          className="text-xs font-semibold bg-muted px-2 py-0.5 rounded w-16 text-right outline-none ring-2 ring-orange-400/50 tabular-nums"
          data-testid={testId ? `${testId}-input` : undefined}
        />
      );
    }
    return (
      <span
        className="text-xs font-semibold bg-muted px-2 py-0.5 rounded cursor-pointer hover:bg-muted/70 transition-colors tabular-nums"
        onClick={() => setEditing(true)}
        title="Click to edit"
        data-testid={testId}
      >
        {formatter ? formatter(value) : `${value}${suffix}`}
      </span>
    );
  };

  const SummaryLineItem = ({ label, value, indent = false, muted = true }) => (
    <div className={`flex justify-between ${indent ? 'pl-4' : ''} ${presentationMode ? 'py-1' : 'py-0.5'}`}>
      <span className={muted ? "text-muted-foreground" : "font-medium"}>{label}</span>
      <span className={muted ? "text-muted-foreground tabular-nums" : "font-medium tabular-nums"}>{value}</span>
    </div>
  );

  // Expandable summary section with line item details
  const SummarySection = ({ title, total, items, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const hasItems = items && items.length > 0;
    
    return (
      <div className="border-b border-white/[0.06] last:border-b-0">
        <button
          onClick={() => hasItems && setIsOpen(!isOpen)}
          className={`flex items-center justify-between w-full py-3 px-0.5 -mx-0.5 rounded-lg transition-colors duration-200 ${hasItems ? 'hover:bg-white/[0.02] cursor-pointer' : ''}`}
          data-testid={`summary-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-2.5">
            {hasItems ? (
              <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            ) : (
              <div className="w-3.5" />
            )}
            <span className="text-[13px] text-white/75 tracking-tight">{title}</span>
          </div>
          <span className="text-[13px] text-white font-medium tabular-nums">${total.toLocaleString()}</span>
        </button>
        
        {isOpen && hasItems && (
          <div className="pl-6 pb-3 space-y-1.5">
            {items.map((item, idx) => {
              // Premium client-facing sqft formatting
              let qtyDetail = "";
              if (item.unit_type === "sqft" && item.quantity > 0 && item.rate > 0) {
                const rateFmt = item.rate < 10 ? item.rate.toFixed(2) : Math.round(item.rate).toLocaleString();
                qtyDetail = `${item.quantity.toLocaleString()} SF @ $${rateFmt}/SF`;
              } else if (item.unit_type === "amenity_sqft") {
                const sf = (item.metadata && item.metadata.sqft) || 0;
                if (sf > 0 && item.rate > 0) {
                  const eff = item.rate / sf;
                  const rateFmt = eff < 10 ? eff.toFixed(2) : Math.round(eff).toLocaleString();
                  qtyDetail = `${Math.round(sf).toLocaleString()} SF @ $${rateFmt}/SF`;
                }
              } else if (item.quantity > 0) {
                qtyDetail = `${item.quantity} × $${item.rate.toLocaleString()}`;
              }
              return (
                <div key={idx} className="flex justify-between text-[12px] py-0.5">
                  <span className={`${item.isCustomizer ? 'text-[#8FA79B]' : 'text-white/45'}`}>
                    {qtyDetail ? `${item.label} · ${qtyDetail}` : item.label}
                  </span>
                  <span className={`tabular-nums ${item.isCustomizer ? 'text-[#8FA79B]' : 'text-white/60'}`}>${item.subtotal.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Build line items for summary sections
  const platformItems = [
    { label: "Base Platform", quantity: 1, rate: vexosCalc.baseSetup, subtotal: vexosCalc.baseSetup },
    ...(vexosCalc.additionalBuildingsCost > 0 ? [{ label: "Additional Buildings", quantity: residentialBuildings - 1, rate: getRate("additional residential", "vexos"), subtotal: vexosCalc.additionalBuildingsCost }] : []),
    ...(vexosCalc.amenityCost > 0 ? [{ label: "Amenity Buildings", quantity: amenityBuildings, rate: getRate("amenity building", "vexos"), subtotal: vexosCalc.amenityCost }] : []),
    // Use tier-aware effective rate (vexosCalc.effectiveUnitRate) so the
    // sidebar label like "45 × $100" matches the actual tiered subtotal,
    // not the legacy flat-rate $25 from getRate(). Falls back to legacy rate
    // for ultra-old pricing rule sets that don't surface effectiveUnitRate.
    { label: "Inventory Programming", quantity: residentialUnits, rate: vexosCalc.effectiveUnitRate || getRate("residential unit", "vexos"), subtotal: vexosCalc.unitsCost }
  ].filter(item => item.subtotal > 0);

  const exteriorItems = [
    ...exteriorCalc.structureDetails.filter(s => s.uniqueCount > 0).map(s => ({
      label: s.name || `${complexityTiers.find(t => t.value === s.complexity)?.label} Structure`,
      quantity: s.uniqueCount,
      rate: s.tierRate,
      subtotal: s.primaryCost
    })),
    ...(exteriorCalc.replicationSubtotal > 0 ? [{
      label: "Repeated Placements",
      quantity: exteriorCalc.totalReplicatedPlacements,
      rate: getReplicationRate(),
      subtotal: exteriorCalc.replicationSubtotal
    }] : []),
    ...(exteriorCalc.siteEnvironmentCost > 0 ? [{
      label: `Site Environment (${siteEnvironments.find(e => e.value === siteEnvironment)?.label})`,
      quantity: 1,
      rate: exteriorCalc.siteEnvironmentCost,
      subtotal: exteriorCalc.siteEnvironmentCost
    }] : []),
    ...(exteriorCalc.realityCaptureCost > 0 ? [{
      label: "Reality Capture",
      quantity: 1,
      rate: realityCaptureRate,
      subtotal: exteriorCalc.realityCaptureCost
    }] : [])
  ];

  const floorplateItems = [
    { label: "Unique Floorplates", quantity: uniqueFloorplates, rate: vexosCalc.uniqueFloorplateRate, subtotal: vexosCalc.uniqueFloorplateCost },
    { label: "Repeat Floorplates", quantity: repeatFloorplates, rate: vexosCalc.repeatFloorplateRate, subtotal: vexosCalc.repeatFloorplateCost }
  ].filter(item => item.subtotal > 0);

  const interiorModelingItems = [
    ...(vizCalc.interiorModelingCost > 0 ? [{ label: "Interior Modeling", quantity: interiorSqft, rate: vizCalc.interiorModelingRate, subtotal: vizCalc.interiorModelingCost, unit_type: "sqft", metadata: { sqft: interiorSqft } }] : []),
    ...(vizCalc.amenityModelingCost > 0 ? vizCalc.amenityDetails.map(a => ({ label: `${a.name || "Amenity"} Interior`, quantity: 1, rate: a.totalCost, subtotal: a.totalCost, unit_type: "amenity_sqft", metadata: { sqft: a.sqft } })) : [])
  ];

  const renderingItems = [
    ...(vizCalc.interiorRenderCost > 0 ? [{ label: "Interior Renderings", quantity: interiorRenders, rate: vizCalc.interiorRenderRate, subtotal: vizCalc.interiorRenderCost }] : []),
    ...(vizCalc.exteriorRenderCost > 0 ? [{ label: "Exterior Renderings", quantity: exteriorRenders, rate: vizCalc.exteriorRenderRate, subtotal: vizCalc.exteriorRenderCost }] : []),
    ...(vizCalc.aerialRenderCost > 0 ? [{ label: "Aerial Renderings", quantity: aerialRenders, rate: vizCalc.aerialRate, subtotal: vizCalc.aerialRenderCost }] : []),
    ...(vizCalc.floorPlanCost > 0 ? [{ label: "3D Floor Plans", quantity: floorPlans, rate: floorPlanRate, subtotal: vizCalc.floorPlanCost }] : [])
  ];

  const vrItems = [
    ...vizCalc.residentialVRDetails.filter(d => d.quantity > 0).flatMap(d => {
      const items = [{
        label: `VR Tour — ${d.label}`,
        quantity: d.quantity,
        rate: d.rate,
        subtotal: d.baseCost + d.additionalCost
      }];
      // Add customizer line items if enabled
      if (d.customizerEnabled && d.customizerCalc.total > 0) {
        items.push({
          label: `  ↳ Real-Time Customizer`,
          quantity: 0,
          rate: 0,
          subtotal: d.customizerCalc.total,
          isCustomizer: true
        });
      }
      return items;
    }),
    ...vizCalc.amenityVRDetails.filter(d => d.quantity > 0).flatMap(d => {
      const items = [{
        label: `Amenity VR — ${d.label}`,
        quantity: d.quantity,
        rate: d.rate,
        subtotal: d.baseCost + d.additionalCost
      }];
      // Add customizer line items if enabled
      if (d.customizerEnabled && d.customizerCalc.total > 0) {
        items.push({
          label: `  ↳ Real-Time Customizer`,
          quantity: 0,
          rate: 0,
          subtotal: d.customizerCalc.total,
          isCustomizer: true
        });
      }
      return items;
    })
  ];

  // Interior Design Direction items for summary
  const interiorDesignDirectionItems = interiorDesignDirectionCalc.spaces
    .filter(s => s.fee > 0)
    .map(s => ({
      label: `${s.name} (${s.levelLabel})`,
      quantity: 1,
      rate: s.fee,
      subtotal: s.fee
    }));

  // Build VR experience data with quantities for presentation mode (point-based, not residence-typed)
  const vrExperienceData = [];
  const _resStudioCount = (vrResidential.studio || []).length;
  const _res1bedCount = (vrResidential["1bed"] || []).length;
  const _res2bedCount = (vrResidential["2bed"] || []).length;
  const _res3bedCount = (vrResidential["3bed"] || []).length;
  if (_resStudioCount > 0) vrExperienceData.push({ quantity: _resStudioCount, label: "4-Point Residence Tour" });
  if (_res1bedCount > 0) vrExperienceData.push({ quantity: _res1bedCount, label: "6-Point Residence Tour" });
  if (_res2bedCount > 0) vrExperienceData.push({ quantity: _res2bedCount, label: "8-Point Residence Tour" });
  if (_res3bedCount > 0) vrExperienceData.push({ quantity: _res3bedCount, label: "10-Point Residence Tour" });
  // Amenity experiences (sum all amenity quantities)
  const totalAmenityVR = (vrAmenity.small || []).length + (vrAmenity.medium || []).length + (vrAmenity.large || []).length + (vrAmenity.xl || []).length;
  if (totalAmenityVR > 0) {
    vrExperienceData.push({ quantity: totalAmenityVR, label: "Amenity Spatial Experience" });
  }

  // Premium Presentation Mode Summary Component - Apple Keynote Style
  const PresentationSummary = () => {
    // Payment milestone labels for presentation mode
    const getPresentationMilestoneLabel = (idx, total) => {
      if (total === 2) {
        return idx === 0 ? "Initial Payment (Project Initiation)" : "Final Payment (Implementation Milestone)";
      }
      if (idx === 0) return "Initial Payment (Project Initiation)";
      if (idx === total - 1) return "Final Payment (Delivery)";
      return `Progress Payment ${idx}`;
    };

    // Capability item component with subtle check
    const CapabilityItem = ({ children }) => (
      <li className="flex items-start gap-3 py-[6px]">
        <span className="w-4 h-4 mt-0.5 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <Check className="w-2.5 h-2.5 text-zinc-400" />
        </span>
        <span className="text-[14px] text-zinc-100">{children}</span>
      </li>
    );

    // Experience item with quantity
    const ExperienceItem = ({ quantity, label }) => (
      <li className="flex items-start gap-3 py-[6px]">
        <span className="w-4 h-4 mt-0.5 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <Check className="w-2.5 h-2.5 text-zinc-400" />
        </span>
        <span className="text-[14px]">
          <span className="text-zinc-500 font-medium">{quantity}×</span>
          <span className="text-zinc-100 ml-1.5">{label}</span>
        </span>
      </li>
    );

    return (
      <div className="space-y-12 pb-2">
        {/* Section 1: VEXOS Platform Implementation */}
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Monitor className="w-4 h-4 text-blue-400" />
            </div>
            <h3 className="text-[16px] font-light text-white tracking-tight">VEXOS Platform Implementation</h3>
          </div>
          
          <p className="text-[14px] text-zinc-400 leading-relaxed">
            Deployment of the VEXOS interactive sales platform, creating a fully navigable digital representation of the development.
          </p>
          
          <div className="space-y-1 pt-1">
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Capabilities Included</p>
            <ul className="space-y-0">
              <CapabilityItem>Interactive 3D building environment</CapabilityItem>
              <CapabilityItem>Complete residential inventory system</CapabilityItem>
              <CapabilityItem>Floorplate visualization engine</CapabilityItem>
              <CapabilityItem>Real-time unit exploration</CapabilityItem>
              {amenityBuildings > 0 && (
                <CapabilityItem>Amenity space integration</CapabilityItem>
              )}
              <CapabilityItem>Sales-ready digital twin of the property</CapabilityItem>
            </ul>
          </div>
          
          <div className="pt-7">
            <div className="flex justify-between items-baseline">
              <span className="text-[14px] text-zinc-400">Platform Implementation Investment</span>
              <span className="text-[20px] font-light text-white tabular-nums">${vexosCalc.implementationTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Section 2: Immersive Residence Experiences */}
        {vizCalc.vrSubtotal > 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-violet-400" />
              </div>
              <h3 className="text-[16px] font-light text-white tracking-tight">Immersive Residence Experiences</h3>
            </div>
            
            <p className="text-[14px] text-zinc-400 leading-relaxed">
              Fully navigable digital residences and amenity spaces that allow buyers to explore the architecture spatially before construction.
            </p>
            
            {vrExperienceData.length > 0 && (
              <div className="space-y-1 pt-1">
                <ul className="space-y-0">
                  {vrExperienceData.map((exp, idx) => (
                    <ExperienceItem key={idx} quantity={exp.quantity} label={exp.label} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Section 3: Photorealistic Architectural Visuals */}
        {vizCalc.renderingSubtotal > 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Image className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-[16px] font-light text-white tracking-tight">Photorealistic Architectural Visuals</h3>
            </div>
            
            <p className="text-[14px] text-zinc-400 leading-relaxed">
              High-end photorealistic imagery used across marketing, broker presentations, and digital campaigns to showcase the architecture and lifestyle of the development.
            </p>
            
            <div className="space-y-1 pt-1">
              <ul className="space-y-0">
                {interiorRenders > 0 && (
                  <ExperienceItem quantity={interiorRenders} label="Interior Architectural Visual" />
                )}
                {exteriorRenders > 0 && (
                  <ExperienceItem quantity={exteriorRenders} label="Exterior Architectural Visual" />
                )}
                {aerialRenders > 0 && (
                  <ExperienceItem quantity={aerialRenders} label="Aerial Development View" />
                )}
                {amenityEntries.length > 0 && vizCalc.amenityModelingCost > 0 && vizCalc.amenityDetails.map((a, i) => (
                  <ExperienceItem key={i} quantity={1} label={`${a.name || "Amenity"} Interior (${a.sqft.toLocaleString()} sqft)`} />
                ))}
                {floorPlans > 0 && (
                  <ExperienceItem quantity={floorPlans} label="3D Floor Plan" />
                )}
              </ul>
            </div>
          </div>
        )}

        {/* Section 4: Video Animation — AI Motion or Cinematic Film */}
        {vizCalc.animationSubtotal > 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                <Video className="w-4 h-4 text-rose-400" />
              </div>
              <h3 className="text-[16px] font-light text-white tracking-tight">
                {animationType === "ai" ? "AI Motion" : "Cinematic Project Films"}
              </h3>
            </div>

            <p className="text-[14px] text-zinc-400 leading-relaxed">
              {animationType === "ai"
                ? "AI-driven motion built from existing render assets. Optimized for paid ads, social, and rapid marketing."
                : "High-end cinematic animation designed to communicate the architectural vision and spatial experience of the development."}
            </p>

            <div className="space-y-1 pt-1">
              <ul className="space-y-0">
                <ExperienceItem
                  quantity={1}
                  label={getAnimationDisplayName(animationType, animationDuration)}
                />
                {socialMediaPack && (
                  <ExperienceItem quantity={1} label={SOCIAL_MEDIA_PACK.label} />
                )}
              </ul>
              <p className="text-xs text-zinc-500 mt-2 pl-6">
                {animationDuration}s • {getAnimationShots(animationType, animationDuration)} shots
                {animationType === "cinematic" ? " • 3s per shot" : ""}
                {socialMediaPack ? " • incl. Social Media Pack" : ""}
              </p>
            </div>
          </div>
        )}

        {/* Section 5: Interior Design Development (only shows if fee > 0) */}
        {interiorDesignDirectionCalc.totalFee > 0 && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Paintbrush className="w-4 h-4 text-amber-400" />
              </div>
              <h3 className="text-[16px] font-light text-white tracking-tight">Interior Design Development</h3>
            </div>
            
            <p className="text-[14px] text-zinc-400 leading-relaxed">
              Design interpretation and conceptual development for spaces where a complete interior design package is not yet defined.
            </p>
            
            <div className="space-y-1 pt-1">
              <ul className="space-y-0">
                {interiorDesignDirectionCalc.spaces.filter(s => s.fee > 0).map((space, idx) => (
                  <ExperienceItem key={idx} quantity={1} label={`${space.name} — Design Development`} />
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Visualization Investment Total - shows if ANY visualization exists */}
        {vizCalc.grandTotal > 0 && (
          <div className="pt-7 border-t border-zinc-800/50">
            <div className="flex justify-between items-baseline">
              <span className="text-[14px] text-zinc-400">Immersive Visualization Investment</span>
              <span className="text-[20px] font-light text-white tabular-nums">${vizCalc.grandTotal.toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* Section 5: VEXOS Platform License — only shown when VEXOS module is enabled */}
        {vexosModuleEnabled && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Shield className="w-4 h-4 text-emerald-400" />
            </div>
            <h3 className="text-[16px] font-light text-white tracking-tight">VEXOS Platform License</h3>
          </div>

          <p className="text-[14px] text-zinc-400 leading-relaxed">
            The VEXOS platform operates under a software license that activates once the system is delivered and live.
          </p>

          <div className="space-y-1 pt-1">
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">License Includes</p>
            <ul className="space-y-0">
              <CapabilityItem>Platform hosting</CapabilityItem>
              <CapabilityItem>Performance monitoring</CapabilityItem>
              <CapabilityItem>Platform updates</CapabilityItem>
              <CapabilityItem>Technical support</CapabilityItem>
              <CapabilityItem>Continuous availability for the sales team</CapabilityItem>
            </ul>
          </div>

          <div className="pt-7 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-[14px] text-zinc-400">VEXOS Platform Setup</span>
              <span className="text-[20px] font-light text-white tabular-nums">${vexosPayment.setupFee.toLocaleString()}</span>
            </div>
            <div className="space-y-2 pt-2">
              <div className="flex justify-between items-center text-[13px]">
                <span className="text-zinc-500">Monthly Platform Fee</span>
                <span className="text-emerald-400 font-semibold tabular-nums">${vexosPayment.monthlyFee.toLocaleString()}<span className="text-[11px] font-normal text-zinc-500"> / month</span></span>
              </div>
              <div className="flex justify-between items-center text-[13px]">
                <span className="text-zinc-500">Platform Agreement</span>
                <span className="text-zinc-300 tabular-nums">{vexosPayment.contractTermMonths} months</span>
              </div>
            </div>
          </div>

          <p className="text-[12px] text-zinc-500 italic">
            Recurring monthly platform fee includes hosting, maintenance, support, and continued VEXOS platform access.
          </p>
        </div>
        )}

        {/* Section 4: Investment Overview */}
        <div className="space-y-6 pt-8 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center">
              <Zap className="w-4 h-4 text-zinc-300" />
            </div>
            <h3 className="text-[16px] font-light text-white tracking-tight">Investment Overview</h3>
          </div>
          
          <div className="space-y-0">
            <div className="flex justify-between items-baseline py-4">
              <span className="text-[15px] text-zinc-300">Total Project Investment</span>
              <span className="text-[18px] font-light text-white tabular-nums">${oneTimeTotal.toLocaleString()}</span>
            </div>

            {vexosModuleEnabled && vexosPayment.totalVexos > 0 && (
            <div className="flex justify-between items-baseline py-4 border-t border-zinc-800/50">
              <span className="text-[15px] text-zinc-300">VEXOS Monthly Platform Fee</span>
              <span className="text-[18px] font-light text-emerald-400 tabular-nums">${vexosPayment.monthlyFee.toLocaleString()}<span className="text-[13px] font-normal text-zinc-500"> / month</span></span>
            </div>
            )}
          </div>
          
          {/* Payment Schedule */}
          <div className="pt-4">
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-4">Payment Schedule</p>
            <div className="space-y-3">
              {vizMilestoneAmounts.map((milestone, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-[14px] text-zinc-400">
                    {getPresentationMilestoneLabel(idx, vizMilestoneAmounts.length)}
                  </span>
                  <span className="text-[15px] font-semibold text-white tabular-nums">${milestone.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <p className="text-[12px] text-zinc-500 italic mt-5">
              Monthly license billing begins after platform launch.
            </p>
          </div>
        </div>

        {/* Grand Total - Premium Dark Block */}
        <div className="pt-4">
          <TimelineEstimateWidget
            deliverables={timelineDeliverables}
            proposalTotal={oneTimeTotal}
            variant="dark"
            overrideEnabledControlled={timelineOverrideEnabled}
            overrideWeeksControlled={timelineOverrideWeeks}
            onOverrideChange={({ enabled, weeks }) => {
              setTimelineOverrideEnabled(enabled);
              setTimelineOverrideWeeks(weeks);
            }}
            onEstimateChange={setTimelineEstimateSnapshot}
            data-testid="combined-timeline-widget-presentation"
          />
        </div>
        <div className="bg-zinc-900 -mx-6 px-6 py-8 rounded-xl mt-6 border border-zinc-800">
          <div className="flex justify-between items-center">
            <span className="text-[15px] text-zinc-400">Total Due Today</span>
            <span className="text-[28px] font-light text-white tabular-nums tracking-tight">${totalDueToday.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/sales/calculators")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-emerald-500" />
                <h1 className="text-xl font-light">Combined Quote</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {isNew ? "New Quote" : projectName || "Untitled"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Autosave status indicator — builds trust that data is being persisted. */}
            {!isNew && (
              <span
                data-testid="autosave-status"
                className={`text-xs tabular-nums tracking-tight transition-opacity duration-300 ${
                  autoSaveStatus === "idle" ? "opacity-0" : "opacity-100"
                } ${autoSaveStatus === "error" ? "text-red-500" : "text-muted-foreground"}`}
              >
                {autoSaveStatus === "saving" && "Saving…"}
                {autoSaveStatus === "saved" && "Saved"}
                {autoSaveStatus === "error" && "Save failed"}
              </span>
            )}
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-quote-btn">
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Panel - Inputs */}
          <div className="col-span-2 space-y-6">
            {/* VEXOS Platform (consolidated with Project Intake at top) */}
            <Collapsible open={expandedSections.vexos} onOpenChange={() => toggleSection("vexos")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        Project &amp; VEXOS Setup
                        {vexosQuoteType === "sfh" && (
                          <Badge variant="outline" className="ml-2 text-xs font-normal">SFH</Badge>
                        )}
                        {vexosQuoteType === "both" && (
                          <Badge variant="outline" className="ml-2 text-xs font-normal">SFH + Building</Badge>
                        )}
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <Badge className={`bg-blue-500/10 text-blue-500 border-blue-500/20 ${vexosModuleEnabled ? '' : 'opacity-40'}`}>${(vexosModuleEnabled ? vexosCalc.implementationTotal : 0).toLocaleString()}</Badge>
                        <Switch
                          checked={vexosModuleEnabled}
                          onCheckedChange={(checked) => {
                            setVexosModuleEnabled(checked);
                            if (checked) setExpandedSections(prev => ({ ...prev, vexos: true }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="toggle-module-vexos"
                          aria-label="Toggle VEXOS Platform"
                        />
                        {expandedSections.vexos ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Project Intake — consolidated into the first part of the calculator.
                        These four fields drive proposal metadata + project-name header,
                        and live at the top of the VEXOS block so the entire "what kind
                        of project is this" intake happens in a single place. */}
                    <div className="space-y-4 pb-5 border-b border-border/60" data-testid="project-intake-block">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Project Information</Label>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Project Name</Label>
                          <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g., One River Point" data-testid="project-name-input" />
                        </div>
                        <div className="space-y-2">
                          <Label>Client</Label>
                          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g., Related Group" data-testid="client-name-input" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Salesperson</Label>
                          <Select value={salesperson} onValueChange={setSalesperson}>
                            <SelectTrigger data-testid="salesperson-select"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {SALESPEOPLE.map(name => (<SelectItem key={name} value={name}>{name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Project Scale</Label>
                          <Select value={projectSize} onValueChange={setProjectSize}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PROJECT_SIZES.map(size => (<SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Property & Site Intake — pure documentation fields.
                          Numbers feed the proposal PDF and PM handoff but never drive pricing. */}
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <Label>Development Type</Label>
                          <Select value={developmentType} onValueChange={setDevelopmentType}>
                            <SelectTrigger data-testid="development-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="condo">Condo project</SelectItem>
                              <SelectItem value="townhome">Townhome community</SelectItem>
                              <SelectItem value="mixed_use">Mixed-use project</SelectItem>
                              <SelectItem value="master_plan">Master plan community</SelectItem>
                              <SelectItem value="lot_subdivision">Lot subdivision</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Lot Size <span className="text-xs text-muted-foreground font-normal">(sq ft)</span></Label>
                          <Input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={lotSqFt}
                            onChange={(e) => setLotSqFt(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="e.g., 45,000"
                            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid="lot-sqft-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Total Units</Label>
                          <Input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={unitCountIntake}
                            onChange={(e) => setUnitCountIntake(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="e.g., 240"
                            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid="unit-count-intake-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unique Floor Plans</Label>
                          <Input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={uniqueFloorplanCount}
                            onChange={(e) => setUniqueFloorplanCount(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="e.g., 8"
                            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid="unique-floorplan-count-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Avg Floor Plan <span className="text-xs text-muted-foreground font-normal">(sq ft)</span></Label>
                          <Input
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={avgFloorplanSqFt}
                            onChange={(e) => setAvgFloorplanSqFt(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="e.g., 1,250"
                            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            data-testid="avg-floorplan-sqft-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Quote Type Toggle */}
                    <div className="space-y-3">
                      <Label className="text-xs text-muted-foreground">Quote Type</Label>
                      <div className="flex gap-2">
                        {VEXOS_QUOTE_TYPES.map(type => (
                          <button
                            key={type.value}
                            onClick={() => setVexosQuoteType(type.value)}
                            className={`flex-1 p-3 rounded-lg border-2 transition-all text-left ${
                              vexosQuoteType === type.value
                                ? "border-blue-500 bg-blue-500/10"
                                : "border-border hover:border-muted-foreground/50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {type.value === "building" ? (
                                <Building2 className={`w-4 h-4 ${vexosQuoteType === type.value ? "text-blue-500" : "text-muted-foreground"}`} />
                              ) : type.value === "both" ? (
                                <Layers className={`w-4 h-4 ${vexosQuoteType === type.value ? "text-blue-500" : "text-muted-foreground"}`} />
                              ) : (
                                <Home className={`w-4 h-4 ${vexosQuoteType === type.value ? "text-blue-500" : "text-muted-foreground"}`} />
                              )}
                              <span className={`font-medium text-sm ${vexosQuoteType === type.value ? "text-blue-500" : ""}`}>
                                {type.label}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* SFH Mode - License Term + Exterior Modeling */}
                    {vexosQuoteType === "sfh" && (
                      <div className="space-y-6">
                        <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Home className="w-5 h-5 text-blue-500" />
                              <span className="font-medium">Single Family Home</span>
                            </div>
                            <Badge className="bg-blue-500/10 text-blue-500">
                              ${vexosCalc.baseSetup.toLocaleString()} base
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Streamlined VEXOS implementation for single-family residential projects.
                          </p>
                        </div>

                        {/* Exterior Modeling Section - Reused from Building mode */}
                        <Separator />
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                              <Mountain className="w-4 h-4 text-emerald-500" />
                              Exterior Modeling
                            </h4>
                            {exteriorCalc.exteriorModelingTotal > 0 && (
                              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                ${exteriorCalc.exteriorModelingTotal.toLocaleString()}
                              </Badge>
                            )}
                          </div>

                          {/* Structure Rows */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs text-muted-foreground">Primary Structures</Label>
                              <Button variant="outline" size="sm" onClick={addStructure} className="h-7 gap-1 text-xs">
                                <Plus className="w-3 h-3" /> Add
                              </Button>
                            </div>

                            {exteriorStructures.map((structure, index) => {
                              const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                              return (
                                <div 
                                  key={structure.id} 
                                  className={`p-3 rounded-lg border transition-all ${
                                    (structure.uniqueCount > 0 || structure.repeatCount > 0) 
                                      ? 'border-emerald-500/30 bg-emerald-500/5' 
                                      : 'border-border'
                                  }`}
                                >
                                  <div className="grid grid-cols-12 gap-3 items-start">
                                    <div className="col-span-4">
                                      <Input
                                        value={structure.name}
                                        onChange={(e) => updateStructure(structure.id, "name", e.target.value)}
                                        placeholder="Structure name"
                                        className="h-8 text-sm"
                                      />
                                    </div>
                                    <div className="col-span-2">
                                      <Select 
                                        value={structure.complexity} 
                                        onValueChange={(v) => updateStructure(structure.id, "complexity", v)}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {complexityTiers.map(tier => (
                                            <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="flex items-center gap-1">
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}>
                                          <Minus className="w-3 h-3" />
                                        </Button>
                                        <Input type="number" value={structure.uniqueCount} onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}>
                                          <Plus className="w-3 h-3" />
                                        </Button>
                                      </div>
                                      <p className="text-xs text-muted-foreground text-center mt-1">Unique</p>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="flex items-center gap-1">
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}>
                                          <Minus className="w-3 h-3" />
                                        </Button>
                                        <Input type="number" value={structure.repeatCount} onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}>
                                          <Plus className="w-3 h-3" />
                                        </Button>
                                      </div>
                                      <p className="text-xs text-muted-foreground text-center mt-1">Repeated</p>
                                    </div>
                                    <div className="col-span-2 flex items-center gap-1">
                                      <div className="flex-1 h-8 px-2 rounded bg-muted/50 flex items-center justify-end text-sm font-medium text-emerald-600">
                                        ${(details?.totalCost || 0).toLocaleString()}
                                      </div>
                                      {exteriorStructures.length > 1 && (
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeStructure(structure.id)}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Site Environment & Reality Capture */}
                          <div className="grid grid-cols-2 gap-4 pt-2">
                            <div className="space-y-1">
                              <Label className="text-xs flex items-center gap-1">
                                <Trees className="w-3 h-3" /> Site Environment
                              </Label>
                              <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {siteEnvironments.map(env => (
                                    <SelectItem key={env.value} value={env.value}>
                                      {env.label} {env.value !== "none" && `($${getSiteRate(env.value).toLocaleString()})`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Reality Capture</Label>
                              <div className="flex items-center gap-2">
                                <Switch checked={includeRealityCapture} onCheckedChange={setIncludeRealityCapture} />
                                {includeRealityCapture && (
                                  <Input type="number" value={realityCaptureRate} onChange={(e) => setRealityCaptureRate(parseInt(e.target.value) || 8500)} className="h-9 w-24" />
                                )}
                                <span className="text-xs text-muted-foreground">{includeRealityCapture ? "Enabled" : "Off"}</span>
                              </div>
                            </div>
                          </div>

                          {/* Exterior Modeling Summary */}
                          {exteriorCalc.exteriorModelingTotal > 0 && (
                            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 grid grid-cols-4 gap-4 text-xs">
                              <div>
                                <p className="text-muted-foreground">Unique Structures</p>
                                <p className="font-medium">{exteriorCalc.totalUniqueStructures}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Replicated</p>
                                <p className="font-medium">{exteriorCalc.totalReplicatedPlacements}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Site</p>
                                <p className="font-medium">{siteEnvironments.find(e => e.value === siteEnvironment)?.label}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">Total</p>
                                <p className="font-medium text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Orbit Configuration Section - SFH Mode */}
                        <div className="space-y-4 pt-4 border-t">
                          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                            <Orbit className="w-4 h-4 text-purple-500" />
                            Orbit Configuration
                          </h4>
                          
                          {/* Included Orbit Info */}
                          <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-xs">
                            <p className="flex items-center gap-2">
                              <Info className="w-3.5 h-3.5 text-blue-500" />
                              <span>Each residential building includes one standard building orbit.</span>
                            </p>
                          </div>
                          
                          {/* Suborbit Toggle */}
                          <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium text-sm">Suborbit</span>
                                <Badge variant="outline" className="text-xs">Optional</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Wider contextual orbit for overall site and surroundings</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {includeMasterOrbit && <span className="text-sm text-purple-600 font-medium">${masterOrbitPrice.toLocaleString()}</span>}
                              <Switch checked={includeMasterOrbit} onCheckedChange={setIncludeMasterOrbit} />
                            </div>
                          </div>
                          
                          {/* General Area Orbit Toggle */}
                          <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium text-sm">General Area Orbit</span>
                                <Badge variant="outline" className="text-xs">Optional</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">Neighborhood and area context orbit showcasing local surroundings</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {includeGeneralAreaOrbit && <span className="text-sm text-purple-600 font-medium">${generalAreaOrbitPrice.toLocaleString()}</span>}
                              <Switch checked={includeGeneralAreaOrbit} onCheckedChange={setIncludeGeneralAreaOrbit} />
                            </div>
                          </div>
                          
                          {/* Additional Orbits */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium text-sm">Additional Orbits</span>
                              </div>
                              <Button variant="outline" size="sm" onClick={addAdditionalOrbit} className="gap-1 h-7 text-xs">
                                <Plus className="w-3 h-3" /> Add Orbit
                              </Button>
                            </div>
                            
                            {additionalOrbits.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-3 border rounded-lg border-dashed">
                                No additional orbits. Add for PH spotlight, amenity, or custom orbits.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {additionalOrbits.map((orbit, idx) => {
                                  const details = orbitCalc.additionalOrbitDetails.find(o => o.id === orbit.id);
                                  return (
                                    <div key={orbit.id} className="p-3 rounded-lg border-2 border-purple-500/20 bg-purple-500/5">
                                      <div className="grid grid-cols-12 gap-2 items-center">
                                        <div className="col-span-4">
                                          <Select value={orbit.type} onValueChange={(v) => updateAdditionalOrbit(orbit.id, "type", v)}>
                                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {ADDITIONAL_ORBIT_TYPES.map(t => (
                                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="col-span-3">
                                          <Input
                                            value={orbit.customName}
                                            onChange={(e) => updateAdditionalOrbit(orbit.id, "customName", e.target.value)}
                                            placeholder={orbit.type === "custom" ? "Name *" : "Optional"}
                                            className="h-8 text-xs"
                                            disabled={orbit.type !== "custom"}
                                          />
                                        </div>
                                        <div className="col-span-2 flex items-center gap-1">
                                          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateAdditionalOrbit(orbit.id, "quantity", Math.max(1, orbit.quantity - 1))}><Minus className="w-3 h-3" /></Button>
                                          <span className="text-xs font-medium w-6 text-center">{orbit.quantity}</span>
                                          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateAdditionalOrbit(orbit.id, "quantity", orbit.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                                        </div>
                                        <div className="col-span-2 text-right">
                                          <span className="text-sm font-medium text-purple-600">${(details?.subtotal || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="col-span-1">
                                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeAdditionalOrbit(orbit.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          
                          {/* Orbit Configuration Summary */}
                          {orbitCalc.orbitConfigurationTotal > 0 && (
                            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20 space-y-2">
                              {orbitCalc.masterOrbitCost > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Suborbit</span>
                                  <span className="text-purple-600">${orbitCalc.masterOrbitCost.toLocaleString()}</span>
                                </div>
                              )}
                              {orbitCalc.generalAreaOrbitCost > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">General Area Orbit</span>
                                  <span className="text-purple-600">${orbitCalc.generalAreaOrbitCost.toLocaleString()}</span>
                                </div>
                              )}
                              {orbitCalc.additionalOrbitsTotal > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-muted-foreground">Additional Orbits ({orbitCalc.totalAdditionalOrbitCount})</span>
                                  <span className="text-purple-600">${orbitCalc.additionalOrbitsTotal.toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center pt-1 border-t">
                                <span className="text-xs font-medium">Orbit Configuration Total</span>
                                <span className="font-semibold text-purple-600">${orbitCalc.orbitConfigurationTotal.toLocaleString()}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <Separator />

                        {/* SFH Summary */}
                        <div className="p-4 rounded-lg border bg-card">
                          <h4 className="font-medium text-sm mb-3">SFH Implementation Summary</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Platform Fee</span>
                              <span className="font-medium">${vexosCalc.baseSetup.toLocaleString()}</span>
                            </div>
                            {exteriorCalc.exteriorModelingTotal > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Exterior Modeling</span>
                                <span className="font-medium text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</span>
                              </div>
                            )}
                            {orbitCalc.orbitConfigurationTotal > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Orbit Configuration</span>
                                <span className="font-medium text-purple-600">${orbitCalc.orbitConfigurationTotal.toLocaleString()}</span>
                              </div>
                            )}
                            <Separator className="my-2" />
                            <div className="flex justify-between">
                              <span className="font-medium">Implementation Total</span>
                              <span className="font-bold text-blue-500">${vexosCalc.implementationTotal.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Building Mode - Full Configuration */}
                    {vexosQuoteType === "building" && (
                      <>
                        <div className="space-y-4">
                          <h4 className="font-medium text-sm text-muted-foreground">Platform Configuration</h4>
                          <div className="grid grid-cols-4 gap-4">
                            <div className="space-y-1"><Label className="text-xs">Res. Buildings</Label><Input type="number" value={residentialBuildings} onChange={(e) => setResidentialBuildings(Math.max(1, parseInt(e.target.value) || 1))} /></div>
                            <div className="space-y-1"><Label className="text-xs">Amenity Bldgs</Label><Input type="number" value={amenityBuildings} onChange={(e) => setAmenityBuildings(parseInt(e.target.value) || 0)} /></div>
                            <div className="space-y-1"><Label className="text-xs">Floors Covered</Label><Input type="number" value={floorsCovered} onChange={(e) => setFloorsCovered(Math.max(1, parseInt(e.target.value) || 1))} /></div>
                            <div className="space-y-1"><Label className="text-xs">Inventory</Label><Input type="number" value={residentialUnits} onChange={(e) => setResidentialUnits(Math.max(1, parseInt(e.target.value) || 1))} data-testid="inventory-input" /><p className="text-xs text-muted-foreground">${vexosCalc.effectiveUnitRate}/unit</p></div>
                          </div>
                          {residentialUnits > 0 && (
                            <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-3 py-2" data-testid="inventory-tier-breakdown">
                              <span className="text-xs text-muted-foreground">Tier: <span className="font-medium text-foreground/80">{vexosCalc.inventoryTierLabel}</span> @ ${vexosCalc.inventoryTierRate}/unit</span>
                              <span className="text-xs font-semibold">{residentialUnits.toLocaleString()} x ${vexosCalc.inventoryTierRate} = ${vexosCalc.unitsCost?.toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm text-muted-foreground">Floorplate Development</h4>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-muted-foreground">Include</Label>
                              <Switch checked={floorplateEnabled} onCheckedChange={setFloorplateEnabled} data-testid="floorplate-toggle" />
                            </div>
                          </div>
                          {floorplateEnabled && (
                          <>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1"><Label className="text-xs">Unique</Label><Input type="number" value={uniqueFloorplates} onChange={(e) => setUniqueFloorplates(parseInt(e.target.value) || 0)} data-testid="floorplate-unique-input" /><p className="text-xs text-muted-foreground">${vexosCalc.uniqueFloorplateRate.toLocaleString()} ea</p></div>
                            <div className="space-y-1"><Label className="text-xs">Repeat</Label><Input type="number" value={repeatFloorplates} onChange={(e) => setRepeatFloorplates(parseInt(e.target.value) || 0)} data-testid="floorplate-repeat-input" /><p className="text-xs text-muted-foreground">${vexosCalc.repeatFloorplateRate.toLocaleString()} ea</p></div>
                          </div>
                          <div className={`p-3 rounded-lg border text-sm ${coverage.statusType === "success" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                {coverage.statusType === "success" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}
                                <span className="text-muted-foreground">Floors: {floorsCovered}</span>
                                <span className="text-muted-foreground">Covered: {coverage.totalCovered}</span>
                              </div>
                              <span className={coverage.statusType === "success" ? "text-emerald-600" : "text-amber-600"}>{coverage.status}</span>
                            </div>
                          </div>
                          </>
                          )}
                        </div>

                        {/* Exterior Modeling Section */}
                        <Separator />
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                              <Mountain className="w-4 h-4 text-emerald-500" />
                              Exterior Modeling
                            </h4>
                            {exteriorCalc.exteriorModelingTotal > 0 && (
                              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                ${exteriorCalc.exteriorModelingTotal.toLocaleString()}
                              </Badge>
                            )}
                          </div>

                      {/* Structure Rows */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">Primary Structures</Label>
                          <Button variant="outline" size="sm" onClick={addStructure} className="h-7 gap-1 text-xs">
                            <Plus className="w-3 h-3" /> Add
                          </Button>
                        </div>

                        {exteriorStructures.map((structure, index) => {
                          const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                          return (
                            <div 
                              key={structure.id} 
                              className={`p-3 rounded-lg border transition-all ${
                                (structure.uniqueCount > 0 || structure.repeatCount > 0) 
                                  ? 'border-emerald-500/30 bg-emerald-500/5' 
                                  : 'border-border'
                              }`}
                            >
                              <div className="grid grid-cols-12 gap-3 items-start">
                                <div className="col-span-4">
                                  <Input
                                    value={structure.name}
                                    onChange={(e) => updateStructure(structure.id, "name", e.target.value)}
                                    placeholder="Structure name"
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Select 
                                    value={structure.complexity} 
                                    onValueChange={(v) => updateStructure(structure.id, "complexity", v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COMPLEXITY_TIERS.map(tier => (
                                        <SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2">
                                  <div className="flex items-center gap-1">
                                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}>
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <Input type="number" value={structure.uniqueCount} onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}>
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground text-center mt-1">Unique</p>
                                </div>
                                <div className="col-span-2">
                                  <div className="flex items-center gap-1">
                                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}>
                                      <Minus className="w-3 h-3" />
                                    </Button>
                                    <Input type="number" value={structure.repeatCount} onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}>
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  <p className="text-xs text-muted-foreground text-center mt-1">Repeated</p>
                                </div>
                                <div className="col-span-2 flex items-center gap-1">
                                  <div className="flex-1 h-8 px-2 rounded bg-muted/50 flex items-center justify-end text-sm font-medium text-emerald-600">
                                    ${(details?.totalCost || 0).toLocaleString()}
                                  </div>
                                  {exteriorStructures.length > 1 && (
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeStructure(structure.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Site Environment & Reality Capture */}
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-1">
                          <Label className="text-xs flex items-center gap-1">
                            <Trees className="w-3 h-3" /> Site Environment
                          </Label>
                          <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SITE_ENVIRONMENTS.map(env => (
                                <SelectItem key={env.value} value={env.value}>
                                  {env.label} {env.value !== "none" && `($${getSiteRate(env.value).toLocaleString()})`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Reality Capture</Label>
                          <div className="flex items-center gap-2">
                            <Switch checked={includeRealityCapture} onCheckedChange={setIncludeRealityCapture} />
                            {includeRealityCapture && (
                              <Input type="number" value={realityCaptureRate} onChange={(e) => setRealityCaptureRate(parseInt(e.target.value) || 8500)} className="h-9 w-24" />
                            )}
                            <span className="text-xs text-muted-foreground">{includeRealityCapture ? "Enabled" : "Off"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Exterior Modeling Summary */}
                      {exteriorCalc.exteriorModelingTotal > 0 && (
                        <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 grid grid-cols-4 gap-4 text-xs">
                          <div>
                            <p className="text-muted-foreground">Unique Structures</p>
                            <p className="font-medium">{exteriorCalc.totalUniqueStructures}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Replicated</p>
                            <p className="font-medium">{exteriorCalc.totalReplicatedPlacements}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Site</p>
                            <p className="font-medium">{SITE_ENVIRONMENTS.find(e => e.value === siteEnvironment)?.label}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Total</p>
                            <p className="font-medium text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Orbit Configuration Section - Building Mode */}
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                          <Orbit className="w-4 h-4 text-purple-500" />
                          Orbit Configuration
                        </h4>
                        {orbitCalc.orbitConfigurationTotal > 0 && (
                          <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/20">
                            ${orbitCalc.orbitConfigurationTotal.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Included Orbit Info */}
                      <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-xs">
                        <p className="flex items-center gap-2">
                          <Info className="w-3.5 h-3.5 text-blue-500" />
                          <span>Each residential building includes one standard building orbit.</span>
                        </p>
                      </div>
                      
                      {/* Suborbit Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium text-sm">Suborbit</span>
                            <Badge variant="outline" className="text-xs">Optional</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Wider contextual orbit showcasing the overall site, surroundings, and project location</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {includeMasterOrbit && <span className="text-sm text-purple-600 font-medium">${masterOrbitPrice.toLocaleString()}</span>}
                          <Switch checked={includeMasterOrbit} onCheckedChange={setIncludeMasterOrbit} data-testid="master-orbit-toggle-building" />
                        </div>
                      </div>
                      
                      {/* General Area Orbit Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium text-sm">General Area Orbit</span>
                            <Badge variant="outline" className="text-xs">Optional</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Neighborhood and area context orbit showcasing local surroundings</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {includeGeneralAreaOrbit && <span className="text-sm text-purple-600 font-medium">${generalAreaOrbitPrice.toLocaleString()}</span>}
                          <Switch checked={includeGeneralAreaOrbit} onCheckedChange={setIncludeGeneralAreaOrbit} data-testid="general-area-orbit-toggle-building" />
                        </div>
                      </div>
                      
                      {/* Additional Orbits */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium text-sm">Additional Orbits</span>
                            <span className="text-xs text-muted-foreground">(${additionalOrbitPrice.toLocaleString()} each)</span>
                          </div>
                          <Button variant="outline" size="sm" onClick={addAdditionalOrbit} className="h-7 gap-1 text-xs">
                            <Plus className="w-3 h-3" /> Add Orbit
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Add specialty orbits for the same building: PH spotlight, amenity levels, or custom experiences.
                        </p>
                        
                        {additionalOrbits.length === 0 ? (
                          <div className="text-center py-4 text-muted-foreground text-xs border rounded-lg border-dashed">
                            No additional orbits added. Click "Add Orbit" to create specialty orbit experiences.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {additionalOrbits.map((orbit, idx) => {
                              const details = orbitCalc.additionalOrbitDetails.find(o => o.id === orbit.id);
                              return (
                                <div key={orbit.id} className="p-3 rounded-lg border-2 border-purple-500/20 bg-purple-500/5">
                                  <div className="grid grid-cols-12 gap-2 items-center">
                                    <div className="col-span-4">
                                      <Select value={orbit.type} onValueChange={(v) => updateAdditionalOrbit(orbit.id, "type", v)}>
                                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {ADDITIONAL_ORBIT_TYPES.map(t => (
                                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-3">
                                      <Input
                                        value={orbit.customName}
                                        onChange={(e) => updateAdditionalOrbit(orbit.id, "customName", e.target.value)}
                                        placeholder={orbit.type === "custom" ? "Name *" : "Optional"}
                                        className="h-8 text-xs"
                                        disabled={orbit.type !== "custom"}
                                      />
                                    </div>
                                    <div className="col-span-2 flex items-center gap-1">
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateAdditionalOrbit(orbit.id, "quantity", Math.max(1, orbit.quantity - 1))}><Minus className="w-3 h-3" /></Button>
                                      <span className="text-xs font-medium w-6 text-center">{orbit.quantity}</span>
                                      <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateAdditionalOrbit(orbit.id, "quantity", orbit.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                                    </div>
                                    <div className="col-span-2 text-right">
                                      <span className="text-sm font-medium text-purple-600">${(details?.subtotal || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="col-span-1">
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeAdditionalOrbit(orbit.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      {/* Orbit Configuration Summary */}
                      {orbitCalc.orbitConfigurationTotal > 0 && (
                        <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20 space-y-2">
                          <h4 className="font-medium text-xs flex items-center gap-2">Orbit Configuration Summary</h4>
                          <div className="space-y-1 text-xs">
                            {orbitCalc.masterOrbitCost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Suborbit</span>
                                <span className="font-medium">${orbitCalc.masterOrbitCost.toLocaleString()}</span>
                              </div>
                            )}
                            {orbitCalc.generalAreaOrbitCost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">General Area Orbit</span>
                                <span className="font-medium">${orbitCalc.generalAreaOrbitCost.toLocaleString()}</span>
                              </div>
                            )}
                            {orbitCalc.additionalOrbitDetails.map(orbit => (
                              <div key={orbit.id} className="flex justify-between">
                                <span className="text-muted-foreground">
                                  {orbit.type === "custom" ? `Custom: ${orbit.customName || 'Unnamed'}` : orbit.typeLabel} (x{orbit.quantity})
                                </span>
                                <span className="font-medium">${orbit.subtotal.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                          <div className="pt-2 border-t flex justify-between">
                            <span className="text-xs text-muted-foreground">Total</span>
                            <span className="font-semibold text-purple-600">${orbitCalc.orbitConfigurationTotal.toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                      </>
                    )}

                    {/* Both Mode - SFH + Building sub-tabs */}
                    {vexosQuoteType === "both" && (
                      <div className="space-y-6">
                        {/* Sub-tab toggle */}
                        <div className="flex gap-1 p-1 rounded-lg bg-muted/50" data-testid="both-mode-tabs">
                          <button
                            data-testid="both-sfh-tab"
                            onClick={() => setBothActiveTab("sfh")}
                            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                              bothActiveTab === "sfh" ? "bg-background shadow-sm text-blue-500" : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <Home className="w-4 h-4" /> SFH Homes
                            {vexosCalc.sfhSubtotal > 0 && <span className="text-xs opacity-70">${vexosCalc.sfhSubtotal?.toLocaleString()}</span>}
                          </button>
                          <button
                            data-testid="both-building-tab"
                            onClick={() => setBothActiveTab("building")}
                            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                              bothActiveTab === "building" ? "bg-background shadow-sm text-blue-500" : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <Building2 className="w-4 h-4" /> Building
                            {(vexosCalc.platformSubtotal + vexosCalc.floorplateSubtotal + vexosCalc.exteriorModelingTotal) > 0 && 
                              <span className="text-xs opacity-70">${(vexosCalc.platformSubtotal + vexosCalc.floorplateSubtotal + vexosCalc.exteriorModelingTotal).toLocaleString()}</span>
                            }
                          </button>
                        </div>

                        {/* SFH Sub-tab */}
                        {bothActiveTab === "sfh" && (
                          <div className="space-y-6">
                            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Home className="w-5 h-5 text-blue-500" />
                                  <span className="font-medium">Single Family Homes</span>
                                </div>
                                <Badge className="bg-blue-500/10 text-blue-500">Included in Platform Setup</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">VEXOS platform for single-family residential homes within the development.</p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs">SFH Inventory (homes)</Label>
                              <Input type="number" value={sfhInventory} onChange={(e) => setSfhInventory(Math.max(1, parseInt(e.target.value) || 1))} data-testid="sfh-inventory-input" />
                              {sfhInventory > 0 && (
                                <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-3 py-2">
                                  <span className="text-xs text-muted-foreground">Tier: <span className="font-medium text-foreground/80">{vexosCalc.sfhInventoryLabel}</span> @ ${vexosCalc.sfhInventoryRate}/unit</span>
                                  <span className="text-xs font-semibold">{sfhInventory.toLocaleString()} x ${vexosCalc.sfhInventoryRate} = ${vexosCalc.sfhUnitsCost?.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                            <Separator />
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Mountain className="w-4 h-4 text-emerald-500" />Exterior Modeling (SFH)</h4>
                                {sfhExteriorCalc.exteriorModelingTotal > 0 && <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">${sfhExteriorCalc.exteriorModelingTotal.toLocaleString()}</Badge>}
                              </div>
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs text-muted-foreground">Home Structures</Label>
                                  <Button variant="outline" size="sm" onClick={addSfhStructure} className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add</Button>
                                </div>
                                {sfhExteriorStructures.map((structure) => {
                                  const details = sfhExteriorCalc.structureDetails.find(d => d.id === structure.id);
                                  return (
                                    <div key={structure.id} className={`p-3 rounded-lg border transition-all ${(structure.uniqueCount > 0 || structure.repeatCount > 0) ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'}`}>
                                      <div className="grid grid-cols-12 gap-3 items-start">
                                        <div className="col-span-4"><Input value={structure.name} onChange={(e) => updateSfhStructure(structure.id, "name", e.target.value)} placeholder="Home name" className="h-8 text-sm" /></div>
                                        <div className="col-span-2">
                                          <Select value={structure.complexity} onValueChange={(v) => updateSfhStructure(structure.id, "complexity", v)}>
                                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>{complexityTiers.map(tier => (<SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>))}</SelectContent>
                                          </Select>
                                        </div>
                                        <div className="col-span-2">
                                          <div className="flex items-center gap-1">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateSfhStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}><Minus className="w-3 h-3" /></Button>
                                            <Input type="number" value={structure.uniqueCount} onChange={(e) => updateSfhStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateSfhStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}><Plus className="w-3 h-3" /></Button>
                                          </div>
                                          <p className="text-xs text-muted-foreground text-center mt-1">Unique</p>
                                        </div>
                                        <div className="col-span-2">
                                          <div className="flex items-center gap-1">
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateSfhStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}><Minus className="w-3 h-3" /></Button>
                                            <Input type="number" value={structure.repeatCount} onChange={(e) => updateSfhStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateSfhStructure(structure.id, "repeatCount", structure.repeatCount + 1)}><Plus className="w-3 h-3" /></Button>
                                          </div>
                                          <p className="text-xs text-muted-foreground text-center mt-1">Repeated</p>
                                        </div>
                                        <div className="col-span-2 flex items-center gap-1">
                                          <div className="flex-1 h-8 px-2 rounded bg-muted/50 flex items-center justify-end text-sm font-medium text-emerald-600">${(details?.totalCost || 0).toLocaleString()}</div>
                                          {sfhExteriorStructures.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeSfhStructure(structure.id)}><Trash2 className="w-3 h-3" /></Button>}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Building Sub-tab */}
                        {bothActiveTab === "building" && (
                          <div className="space-y-6">
                            <div className="space-y-4">
                              <h4 className="font-medium text-sm text-muted-foreground">Building Configuration</h4>
                              <div className="grid grid-cols-4 gap-4">
                                <div className="space-y-1"><Label className="text-xs">Res. Buildings</Label><Input type="number" value={residentialBuildings} onChange={(e) => setResidentialBuildings(Math.max(1, parseInt(e.target.value) || 1))} /></div>
                                <div className="space-y-1"><Label className="text-xs">Amenity Bldgs</Label><Input type="number" value={amenityBuildings} onChange={(e) => setAmenityBuildings(parseInt(e.target.value) || 0)} /></div>
                                <div className="space-y-1"><Label className="text-xs">Floors Covered</Label><Input type="number" value={floorsCovered} onChange={(e) => setFloorsCovered(Math.max(1, parseInt(e.target.value) || 1))} /></div>
                                <div className="space-y-1"><Label className="text-xs">Inventory</Label><Input type="number" value={residentialUnits} onChange={(e) => setResidentialUnits(Math.max(1, parseInt(e.target.value) || 1))} /><p className="text-xs text-muted-foreground">${vexosCalc.effectiveUnitRate}/unit</p></div>
                              </div>
                              {residentialUnits > 0 && (
                                <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-3 py-2">
                                  <span className="text-xs text-muted-foreground">Tier: <span className="font-medium text-foreground/80">{vexosCalc.inventoryTierLabel}</span> @ ${vexosCalc.inventoryTierRate}/unit</span>
                                  <span className="text-xs font-semibold">{residentialUnits.toLocaleString()} x ${vexosCalc.inventoryTierRate} = ${vexosCalc.unitsCost?.toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm text-muted-foreground">Floorplate Development</h4>
                                <div className="flex items-center gap-2"><Label className="text-xs text-muted-foreground">Include</Label><Switch checked={floorplateEnabled} onCheckedChange={setFloorplateEnabled} /></div>
                              </div>
                              {floorplateEnabled && (
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-1"><Label className="text-xs">Unique</Label><Input type="number" value={uniqueFloorplates} onChange={(e) => setUniqueFloorplates(parseInt(e.target.value) || 0)} /><p className="text-xs text-muted-foreground">${vexosCalc.uniqueFloorplateRate?.toLocaleString()} ea</p></div>
                                  <div className="space-y-1"><Label className="text-xs">Repeat</Label><Input type="number" value={repeatFloorplates} onChange={(e) => setRepeatFloorplates(parseInt(e.target.value) || 0)} /><p className="text-xs text-muted-foreground">${vexosCalc.repeatFloorplateRate?.toLocaleString()} ea</p></div>
                                </div>
                              )}
                            </div>
                            <Separator />
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Mountain className="w-4 h-4 text-emerald-500" />Exterior Modeling (Building)</h4>
                                {exteriorCalc.exteriorModelingTotal > 0 && <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</Badge>}
                              </div>
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs text-muted-foreground">Primary Structures</Label>
                                  <Button variant="outline" size="sm" onClick={addStructure} className="h-7 gap-1 text-xs"><Plus className="w-3 h-3" /> Add</Button>
                                </div>
                                {exteriorStructures.map((structure) => {
                                  const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                                  return (
                                    <div key={structure.id} className={`p-3 rounded-lg border transition-all ${(structure.uniqueCount > 0 || structure.repeatCount > 0) ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'}`}>
                                      <div className="grid grid-cols-12 gap-3 items-start">
                                        <div className="col-span-4"><Input value={structure.name} onChange={(e) => updateStructure(structure.id, "name", e.target.value)} placeholder="Structure name" className="h-8 text-sm" /></div>
                                        <div className="col-span-2"><Select value={structure.complexity} onValueChange={(v) => updateStructure(structure.id, "complexity", v)}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{complexityTiers.map(tier => (<SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>))}</SelectContent></Select></div>
                                        <div className="col-span-2"><div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}><Minus className="w-3 h-3" /></Button><Input type="number" value={structure.uniqueCount} onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}><Plus className="w-3 h-3" /></Button></div><p className="text-xs text-muted-foreground text-center mt-1">Unique</p></div>
                                        <div className="col-span-2"><div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}><Minus className="w-3 h-3" /></Button><Input type="number" value={structure.repeatCount} onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}><Plus className="w-3 h-3" /></Button></div><p className="text-xs text-muted-foreground text-center mt-1">Repeated</p></div>
                                        <div className="col-span-2 flex items-center gap-1"><div className="flex-1 h-8 px-2 rounded bg-muted/50 flex items-center justify-end text-sm font-medium text-emerald-600">${(details?.totalCost || 0).toLocaleString()}</div>{exteriorStructures.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeStructure(structure.id)}><Trash2 className="w-3 h-3" /></Button>}</div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Site for Both */}
                        <Separator />
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1"><Trees className="w-3 h-3" /> Site Environment</Label>
                            <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>{siteEnvironments.map(env => (<SelectItem key={env.value} value={env.value}>{env.label}{env.value !== "none" && ` ($${getSiteRate(env.value).toLocaleString()})`}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Visualization */}
            <Collapsible open={expandedSections.visualization} onOpenChange={() => toggleSection("visualization")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Palette className="w-5 h-5 text-purple-500" />
                        Visualization
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        <Badge className={`bg-purple-500/10 text-purple-500 border-purple-500/20 ${vizModuleEnabled ? '' : 'opacity-40'}`}>${((vizModuleEnabled ? vizCalc.grandTotal : 0) + (conceptModelingEnabled ? conceptModelingCalc.grandTotal : 0)).toLocaleString()}</Badge>
                        <Switch
                          checked={vizModuleEnabled}
                          onCheckedChange={(checked) => {
                            setVizModuleEnabled(checked);
                            if (checked) setExpandedSections(prev => ({ ...prev, visualization: true }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="toggle-module-visualization"
                          aria-label="Toggle Visualization"
                        />
                        {expandedSections.visualization ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {/* Exterior Modeling — surfaces here only when VEXOS is OFF (state shared with VEXOS;
                        toggling VEXOS on relocates these inputs back inside the VEXOS card without losing data). */}
                    {!vexosModuleEnabled ? (
                      <div className="space-y-4 p-4 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04]" data-testid="viz-exterior-modeling-block">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Mountain className="w-4 h-4 text-emerald-500" />Exterior Modeling</h4>
                          {exteriorCalc.exteriorModelingTotal > 0 && <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20" data-testid="viz-exterior-modeling-total">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</Badge>}
                        </div>
                        <p className="text-[11px] text-muted-foreground -mt-2">Required when renderings, aerials, animations, or 360° tours need exterior assets. Pricing pulls from the same Pricing Admin tiers used by VEXOS.</p>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Primary Structures</Label>
                            <Button variant="outline" size="sm" onClick={addStructure} className="h-7 gap-1 text-xs" data-testid="viz-add-structure-btn"><Plus className="w-3 h-3" /> Add</Button>
                          </div>
                          {exteriorStructures.map((structure) => {
                            const details = exteriorCalc.structureDetails.find(d => d.id === structure.id);
                            return (
                              <div key={structure.id} className={`p-3 rounded-lg border transition-all ${(structure.uniqueCount > 0 || structure.repeatCount > 0) ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border'}`} data-testid={`viz-structure-${structure.id}`}>
                                <div className="grid grid-cols-12 gap-3 items-start">
                                  <div className="col-span-4"><Input value={structure.name} onChange={(e) => updateStructure(structure.id, "name", e.target.value)} placeholder="Structure name" className="h-8 text-sm" /></div>
                                  <div className="col-span-2"><Select value={structure.complexity} onValueChange={(v) => updateStructure(structure.id, "complexity", v)}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{complexityTiers.map(tier => (<SelectItem key={tier.value} value={tier.value}>{tier.label}</SelectItem>))}</SelectContent></Select></div>
                                  <div className="col-span-2"><div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", Math.max(0, structure.uniqueCount - 1))}><Minus className="w-3 h-3" /></Button><Input type="number" value={structure.uniqueCount} onChange={(e) => updateStructure(structure.id, "uniqueCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "uniqueCount", structure.uniqueCount + 1)}><Plus className="w-3 h-3" /></Button></div><p className="text-xs text-muted-foreground text-center mt-1">Unique</p></div>
                                  <div className="col-span-2"><div className="flex items-center gap-1"><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", Math.max(0, structure.repeatCount - 1))}><Minus className="w-3 h-3" /></Button><Input type="number" value={structure.repeatCount} onChange={(e) => updateStructure(structure.id, "repeatCount", Math.max(0, parseInt(e.target.value) || 0))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" /><Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateStructure(structure.id, "repeatCount", structure.repeatCount + 1)}><Plus className="w-3 h-3" /></Button></div><p className="text-xs text-muted-foreground text-center mt-1">Repeated</p></div>
                                  <div className="col-span-2 flex items-center gap-1"><div className="flex-1 h-8 px-2 rounded bg-muted/50 flex items-center justify-end text-sm font-medium text-emerald-600">${(details?.totalCost || 0).toLocaleString()}</div>{exteriorStructures.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeStructure(structure.id)}><Trash2 className="w-3 h-3" /></Button>}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-1">
                          <div className="space-y-1">
                            <Label className="text-xs flex items-center gap-1"><Trees className="w-3 h-3" /> Site Environment</Label>
                            <Select value={siteEnvironment} onValueChange={setSiteEnvironment}>
                              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                              <SelectContent>{siteEnvironments.map(env => (<SelectItem key={env.value} value={env.value}>{env.label}{env.value !== "none" && ` ($${getSiteRate(env.value).toLocaleString()})`}</SelectItem>))}</SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Reality Capture</Label>
                            <div className="flex items-center gap-2 h-9">
                              <Switch checked={includeRealityCapture} onCheckedChange={setIncludeRealityCapture} data-testid="viz-reality-capture-toggle" />
                              <span className="text-xs text-muted-foreground">{includeRealityCapture ? `On · $${realityCaptureRate.toLocaleString()}` : "Off"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Exterior Modeling Summary — mirrors VEXOS layout exactly */}
                        {exteriorCalc.exteriorModelingTotal > 0 && (
                          <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 grid grid-cols-4 gap-4 text-xs" data-testid="viz-exterior-summary">
                            <div>
                              <p className="text-muted-foreground">Unique Structures</p>
                              <p className="font-medium">{exteriorCalc.totalUniqueStructures}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Replicated</p>
                              <p className="font-medium">{exteriorCalc.totalReplicatedPlacements}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Site</p>
                              <p className="font-medium">{siteEnvironments.find(e => e.value === siteEnvironment)?.label}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Total</p>
                              <p className="font-medium text-emerald-600">${exteriorCalc.exteriorModelingTotal.toLocaleString()}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      exteriorCalc.exteriorModelingTotal > 0 && (
                        <p className="text-[11px] text-muted-foreground italic px-1" data-testid="viz-exterior-vexos-note">Exterior modeling is covered under the VEXOS Platform scope.</p>
                      )
                    )}

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Box className="w-4 h-4" />Interior Modeling</h4>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">{vizCalc.combinedInteriorSqft.toLocaleString()} SF total</span>
                          <Badge variant="outline" className="text-[10px]" data-testid="interior-modeling-tier-badge">${vizCalc.interiorModelingRate.toFixed(2)}/SF · {vizCalc.interiorModelingTierLabel}</Badge>
                        </div>
                      </div>
                      <p className="text-[11px] text-muted-foreground -mt-2">Interior Modeling Rate (based on total combined interior modeled SF). Applies to both residential and amenity interiors.</p>
                      <div className="space-y-1">
                        <Label className="text-xs">Residential Interior (sqft)</Label>
                        <Input type="number" value={interiorSqft} onChange={(e) => setInteriorSqft(parseInt(e.target.value) || 0)} data-testid="interior-sqft-input" />
                        <p className="text-xs text-muted-foreground">${vizCalc.interiorModelingRate.toFixed(2)}/sqft (tiered)</p>
                      </div>
                      
                      {/* Amenity Interior Modeling */}
                      <div className="pt-2">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5" /> Amenity Interiors
                            {vizCalc.amenityModelingCost > 0 && <Badge variant="outline" className="ml-1 text-xs">${vizCalc.amenityModelingCost.toLocaleString()}</Badge>}
                          </Label>
                          <Button variant="outline" size="sm" onClick={addAmenity} className="h-7 gap-1 text-xs" data-testid="add-amenity-btn">
                            <Plus className="w-3 h-3" /> Add Amenity
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">${vizCalc.interiorModelingRate.toFixed(2)}/sqft (tiered — same rate as residential interior)</p>
                        {amenityEntries.length === 0 && (
                          <div className="text-center py-4 text-xs text-muted-foreground/60 border border-dashed rounded-lg">No amenity spaces added</div>
                        )}
                        <div className="space-y-2">
                          {amenityEntries.map((amenity) => {
                            const detail = vizCalc.amenityDetails?.find(d => d.id === amenity.id);
                            return (
                              <div key={amenity.id} className={`p-3 rounded-lg border transition-all ${detail?.totalCost > 0 ? 'border-purple-500/30 bg-purple-500/5' : 'border-border'}`} data-testid={`amenity-entry-${amenity.id}`}>
                                <div className="grid grid-cols-12 gap-2 items-center">
                                  <div className="col-span-3">
                                    <Input value={amenity.name} onChange={(e) => updateAmenity(amenity.id, "name", e.target.value)} placeholder="Amenity name" className="h-8 text-sm" />
                                  </div>
                                  <div className="col-span-2">
                                    <Input type="number" value={amenity.sqft} onChange={(e) => updateAmenity(amenity.id, "sqft", parseInt(e.target.value) || 0)} placeholder="sqft" className="h-8 text-sm" />
                                  </div>
                                  <div className="col-span-2">
                                    <Select value={amenity.complexity} onValueChange={(v) => updateAmenity(amenity.id, "complexity", v)}>
                                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="standard">Standard</SelectItem>
                                        <SelectItem value="premium">Premium</SelectItem>
                                        <SelectItem value="signature">Signature</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-2">
                                    <Select value={amenity.modelingType} onValueChange={(v) => updateAmenity(amenity.id, "modelingType", v)}>
                                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="interior_existing_structure">Interior Only</SelectItem>
                                        <SelectItem value="standalone_structure">Standalone</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="col-span-2 flex items-center gap-1">
                                    <span className="text-sm font-medium text-purple-600 flex-1 text-right">${(detail?.totalCost || 0).toLocaleString()}</span>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeAmenity(amenity.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                {amenity.modelingType === "standalone_structure" && (
                                  <div className="grid grid-cols-12 gap-2 mt-2 items-center">
                                    <div className="col-span-3">
                                      <Select value={amenity.structureSize} onValueChange={(v) => updateAmenity(amenity.id, "structureSize", v)}>
                                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Structure size" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="small">Small ($1,000)</SelectItem>
                                          <SelectItem value="medium">Medium ($2,000)</SelectItem>
                                          <SelectItem value="large">Large ($3,500)</SelectItem>
                                          <SelectItem value="xl">XL ($5,000)</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-4">
                                      <Input value={amenity.sharedGroupId} onChange={(e) => updateAmenity(amenity.id, "sharedGroupId", e.target.value)} placeholder="Shared group (optional)" className="h-7 text-xs" />
                                    </div>
                                    <div className="col-span-5">
                                      {detail?.structureCharged && <span className="text-xs text-emerald-600">+${detail.structureFee.toLocaleString()} structure fee</span>}
                                      {!detail?.structureCharged && amenity.sharedGroupId && <span className="text-xs text-muted-foreground">Structure shared</span>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Image className="w-4 h-4" />Renderings</h4>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Interior</Label>
                          <Input type="number" value={interiorRenders} onChange={(e) => setInteriorRenders(parseInt(e.target.value) || 0)} data-testid="interior-renders-input" />
                          {interiorRenders > 0 && (
                            <p className="text-[10px] text-muted-foreground">${vizCalc.interiorRenderRate?.toLocaleString()}/img · ${vizCalc.interiorRenderCost?.toLocaleString()}</p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Exterior</Label>
                          <Input type="number" value={exteriorRenders} onChange={(e) => setExteriorRenders(parseInt(e.target.value) || 0)} data-testid="exterior-renders-input" />
                          {exteriorRenders > 0 && (
                            <p className="text-[10px] text-muted-foreground">${vizCalc.exteriorRenderRate?.toLocaleString()}/img · ${vizCalc.exteriorRenderCost?.toLocaleString()}</p>
                          )}
                        </div>
                        <div className="space-y-1"><Label className="text-xs">Aerial</Label><Input type="number" value={aerialRenders} onChange={(e) => setAerialRenders(parseInt(e.target.value) || 0)} /></div>
                        <div className="space-y-1"><Label className="text-xs">Floor Plans</Label><Input type="number" value={floorPlans} onChange={(e) => setFloorPlans(parseInt(e.target.value) || 0)} /></div>
                      </div>
                      {(interiorRenders > 0 || exteriorRenders > 0) && (
                        <p className="text-[11px] italic text-muted-foreground -mt-1">Volume pricing is applied as a flat tier based on total image quantity.</p>
                      )}
                    </div>
                    <Separator />
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2"><Eye className="w-4 h-4" />VR / 360° Tours</h4>
                      
                      {/* Residential VR Tours */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">Residential Tours</span>
                          {vizCalc.residentialVRSubtotal > 0 && (
                            <Badge variant="outline" className="ml-auto">
                              ${vizCalc.residentialVRSubtotal.toLocaleString()}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {RESIDENTIAL_VR_TOURS.map(config => {
                            const tours = vrResidential[config.key] || [];
                            const customizerData = vrResidentialCustomizer[config.key];
                            const details = vizCalc.residentialVRDetails.find(d => d.key === config.key);
                            return (
                              <VRTourCard
                                key={config.key}
                                config={config}
                                quantity={tours.length}
                                setQuantity={(v) => vrTierSetCount(setVrResidential, config.key, v)}
                                additionalPoints={0}
                                setAdditionalPoints={() => {}}
                                hideAdditionalPoints={true}
                                rate={details?.rate || 0}
                                totalCost={(details?.baseCost || 0) + (details?.additionalCost || 0)}
                                customizerEnabled={customizerData.enabled}
                                setCustomizerEnabled={(v) => updateResidentialCustomizer(config.key, "enabled", v)}
                                customizerCategories={customizerData.categories}
                                setCustomizerCategories={(v) => updateResidentialCustomizer(config.key, "categories", v)}
                                customizerPricing={customizerPricing}
                                customizerCalc={details?.customizerCalc || { total: 0, categoryDetails: [] }}
                              />
                            );
                          })}
                        </div>

                        {/* Per-Tour Editable List — Residential */}
                        {vizCalc.residentialVRDetails.some(d => d.quantity > 0) && (
                          <div className="mt-3 p-3 rounded-lg border border-border/70 bg-background/40 space-y-3" data-testid="residential-tour-list-panel">
                            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                              <Eye className="w-3.5 h-3.5" />
                              <span>Name Each Tour</span>
                            </div>
                            {vizCalc.residentialVRDetails
                              .filter(d => d.quantity > 0)
                              .map(d => (
                                <div key={d.key} className="space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-foreground/80">{d.label}</span>
                                    <span className="text-[10px] text-muted-foreground">{d.quantity} tour{d.quantity !== 1 ? 's' : ''} · ${d.rate.toLocaleString()} base</span>
                                  </div>
                                  <div className="space-y-1.5">
                                    {d.tours.map((t, idx) => (
                                      <VRTourNameRow
                                        key={t.id}
                                        tour={t}
                                        idx={idx}
                                        tierKey={d.key}
                                        tierLabel={d.label}
                                        includedBase={d.includedPoints}
                                        category="residential"
                                        rate={d.rate}
                                        additionalPointRate={additionalPointRate}
                                        viewChangeRate={vizCalc.viewChangeRate}
                                        onUpdate={(field, value) => vrTourUpdate(setVrResidential, d.key, idx, field, value)}
                                        onRemove={() => vrTourRemoveAt(setVrResidential, d.key, idx)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Amenity VR Tours */}
                      <div className="space-y-3 pt-3 border-t">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">Amenity Tours</span>
                          {(vizCalc.amenityVRSubtotal + (vizCalc.customAmenityApplied || 0)) > 0 && (
                            <Badge variant="outline" className="ml-auto">
                              ${(vizCalc.amenityVRSubtotal + (vizCalc.customAmenityApplied || 0)).toLocaleString()}
                            </Badge>
                          )}
                        </div>

                        {/* Build Custom Tour — flexible per-space configurator */}
                        <div
                          data-testid="amenity-custom-tour-card"
                          className={`p-4 rounded-xl border-2 transition-all ${amenityCustomTours.length > 0 && amenityCustomConfirmed ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-dashed border-indigo-400/40 bg-indigo-500/[0.03] hover:border-indigo-400/60'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-400/20 flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-4 h-4 text-indigo-500" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold">Build Custom Tour</h4>
                                <p className="text-[11px] text-muted-foreground/80 leading-snug mt-0.5">
                                  Create a custom 360° experience by selecting individual spaces
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              onClick={() => setAmenityCustomExpanded(!amenityCustomExpanded)}
                              data-testid="amenity-custom-toggle-btn"
                            >
                              {amenityCustomExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          </div>

                          {amenityCustomExpanded && (
                            <div className="mt-4 space-y-3">
                              {/* Per-point rate hint */}
                              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                <Info className="w-3 h-3" />
                                Priced at ${Math.round(vizCalc.customPerPointRate || 0).toLocaleString()} / point (derived from Small tier admin rate)
                              </div>

                              {/* Space list */}
                              <div className="space-y-2">
                                {amenityCustomTours.length === 0 ? (
                                  <div className="text-xs text-muted-foreground italic py-2">
                                    No custom spaces yet. Add your first space below.
                                  </div>
                                ) : (
                                  amenityCustomTours.map((space, idx) => {
                                    const pts = Math.max(1, parseInt(space.points) || 1);
                                    const spaceCost = pts * (vizCalc.customPerPointRate || 0);
                                    return (
                                      <div
                                        key={space.id}
                                        data-testid={`amenity-custom-space-row-${idx}`}
                                        className={`p-3 rounded-lg bg-background/60 border border-border/70 ${idx > 0 ? 'border-t' : ''}`}
                                      >
                                        <div className="flex items-center gap-2 mb-2">
                                          <Input
                                            value={space.name}
                                            onChange={(e) => {
                                              const updated = [...amenityCustomTours];
                                              updated[idx] = { ...updated[idx], name: e.target.value };
                                              setAmenityCustomTours(updated);
                                              setAmenityCustomConfirmed(false);
                                            }}
                                            placeholder="e.g. Lobby, Gym, Pool Deck"
                                            className="h-8 text-sm"
                                            data-testid={`amenity-custom-space-name-${idx}`}
                                          />
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-muted-foreground hover:text-rose-500"
                                            onClick={() => {
                                              setAmenityCustomTours(amenityCustomTours.filter((_, i) => i !== idx));
                                              setAmenityCustomConfirmed(false);
                                            }}
                                            data-testid={`amenity-custom-space-remove-${idx}`}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="flex items-center gap-2">
                                            <Label className="text-[11px] text-muted-foreground">Points</Label>
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              className="h-7 w-7"
                                              onClick={() => {
                                                const updated = [...amenityCustomTours];
                                                updated[idx] = { ...updated[idx], points: Math.max(1, pts - 1) };
                                                setAmenityCustomTours(updated);
                                                setAmenityCustomConfirmed(false);
                                              }}
                                              data-testid={`amenity-custom-space-points-minus-${idx}`}
                                            >
                                              <Minus className="w-3 h-3" />
                                            </Button>
                                            <span className="w-7 text-center text-sm font-medium">{pts}</span>
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              className="h-7 w-7"
                                              onClick={() => {
                                                const updated = [...amenityCustomTours];
                                                updated[idx] = { ...updated[idx], points: pts + 1 };
                                                setAmenityCustomTours(updated);
                                                setAmenityCustomConfirmed(false);
                                              }}
                                              data-testid={`amenity-custom-space-points-plus-${idx}`}
                                            >
                                              <Plus className="w-3 h-3" />
                                            </Button>
                                          </div>
                                          <span className="text-sm font-semibold" data-testid={`amenity-custom-space-cost-${idx}`}>
                                            {amenityCustomConfirmed ? `$${Math.round(spaceCost).toLocaleString()}` : <span className="text-muted-foreground/70 italic">${Math.round(spaceCost).toLocaleString()} (pending)</span>}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full h-8 text-xs"
                                onClick={() => {
                                  setAmenityCustomTours([
                                    ...amenityCustomTours,
                                    { id: crypto.randomUUID(), name: "", points: 1 }
                                  ]);
                                  setAmenityCustomConfirmed(false);
                                }}
                                data-testid="amenity-custom-add-space-btn"
                              >
                                <Plus className="w-3 h-3 mr-1.5" /> Add Space
                              </Button>

                              {/* Confirmation checkbox + validation */}
                              {amenityCustomTours.length > 0 && (
                                <div className="pt-2 border-t border-border/60">
                                  <label className="flex items-start gap-2 cursor-pointer" data-testid="amenity-custom-confirm-label">
                                    <Checkbox
                                      checked={amenityCustomConfirmed}
                                      onCheckedChange={(v) => setAmenityCustomConfirmed(!!v)}
                                      className="mt-0.5"
                                      data-testid="amenity-custom-confirm-checkbox"
                                    />
                                    <span className="text-xs text-foreground/90 leading-snug">
                                      I confirm all selected spaces are included in the modeling scope above
                                    </span>
                                  </label>
                                  {!amenityCustomConfirmed && (
                                    <div
                                      className="mt-2 text-[11px] text-amber-600 flex items-center gap-1.5"
                                      data-testid="amenity-custom-validation-msg"
                                    >
                                      <AlertTriangle className="w-3 h-3" />
                                      Please confirm modeling coverage before proceeding
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {AMENITY_VR_TOURS.map(config => {
                            const tours = vrAmenity[config.key] || [];
                            const customizerData = vrAmenityCustomizer[config.key];
                            const details = vizCalc.amenityVRDetails.find(d => d.key === config.key);
                            return (
                              <VRTourCard
                                key={config.key}
                                config={config}
                                quantity={tours.length}
                                setQuantity={(v) => vrTierSetCount(setVrAmenity, config.key, v)}
                                additionalPoints={0}
                                setAdditionalPoints={() => {}}
                                hideAdditionalPoints={true}
                                rate={details?.rate || 0}
                                totalCost={(details?.baseCost || 0) + (details?.additionalCost || 0)}
                                customizerEnabled={customizerData.enabled}
                                setCustomizerEnabled={(v) => updateAmenityCustomizer(config.key, "enabled", v)}
                                customizerCategories={customizerData.categories}
                                setCustomizerCategories={(v) => updateAmenityCustomizer(config.key, "categories", v)}
                                customizerPricing={customizerPricing}
                                customizerCalc={details?.customizerCalc || { total: 0, categoryDetails: [] }}
                              />
                            );
                          })}
                        </div>

                        {/* Per-Tour Editable List — Amenity */}
                        {vizCalc.amenityVRDetails.some(d => d.quantity > 0) && (
                          <div className="mt-3 p-3 rounded-lg border border-border/70 bg-background/40 space-y-3" data-testid="amenity-tour-list-panel">
                            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                              <Eye className="w-3.5 h-3.5" />
                              <span>Name Each Tour</span>
                            </div>
                            {vizCalc.amenityVRDetails
                              .filter(d => d.quantity > 0)
                              .map(d => (
                                <div key={d.key} className="space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-foreground/80">{d.label}</span>
                                    <span className="text-[10px] text-muted-foreground">{d.quantity} tour{d.quantity !== 1 ? 's' : ''} · ${d.rate.toLocaleString()} base</span>
                                  </div>
                                  <div className="space-y-1.5">
                                    {d.tours.map((t, idx) => (
                                      <VRTourNameRow
                                        key={t.id}
                                        tour={t}
                                        idx={idx}
                                        tierKey={d.key}
                                        tierLabel={d.label}
                                        includedBase={d.includedPoints}
                                        category="amenity"
                                        rate={d.rate}
                                        additionalPointRate={additionalPointRate}
                                        viewChangeRate={vizCalc.viewChangeRate}
                                        onUpdate={(field, value) => vrTourUpdate(setVrAmenity, d.key, idx, field, value)}
                                        onRemove={() => vrTourRemoveAt(setVrAmenity, d.key, idx)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Additional Points Info */}
                      {vizCalc.totalAdditionalPointsCost > 0 && (
                        <div className="p-3 rounded-lg bg-muted/50 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              Additional navigation points ({vizCalc.totalResidentialAdditionalPoints + vizCalc.totalAmenityAdditionalPoints} × ${additionalPointRate})
                            </span>
                          </div>
                          <span className="font-medium">${vizCalc.totalAdditionalPointsCost.toLocaleString()}</span>
                        </div>
                      )}
                      
                      {/* Real-Time Customizer Total */}
                      {vizCalc.totalCustomizerCost > 0 && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-medium text-amber-600">
                              Real-Time Customizer Total
                            </span>
                          </div>
                          <span className="font-semibold text-amber-600">${vizCalc.totalCustomizerCost.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <Separator />
                    {/* Video Animation — AI Motion + Cinematic Film */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                          <Video className="w-4 h-4" />
                          Video Animation
                        </h4>
                        {animationEnabled && vizCalc.animationSubtotal > 0 && (
                          <span className="text-sm font-semibold text-rose-500" data-testid="combined-animation-subtotal">
                            ${vizCalc.animationSubtotal.toLocaleString()}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground">
                        Choose a motion product. One selection at a time.
                      </p>

                      <VideoAnimationPicker
                        selectedType={animationEnabled ? animationType : null}
                        selectedDuration={animationEnabled ? animationDuration : null}
                        socialMediaPack={socialMediaPack}
                        rateResolver={getVideoAnimationRate}
                        onSelect={(type, duration) => {
                          if (!type) {
                            setAnimationEnabled(false);
                            setAnimationModelingAligned(null);
                            setSocialMediaPack(false);
                          } else {
                            setAnimationEnabled(true);
                            setAnimationType(type);
                            setAnimationDuration(duration);
                          }
                        }}
                        onToggleSocialPack={setSocialMediaPack}
                        compact
                        testIdPrefix="combined-animation"
                      />

                      {/* Modeling Alignment — kept minimal, only when a video is selected */}
                      {animationEnabled && (
                        <div className="space-y-2 pt-2 border-t border-white/[0.06]">
                          <Label className="text-xs">Modeling Alignment</Label>
                          <p className="text-xs text-muted-foreground">
                            Are all spaces in this video accounted for in the modeling scope?
                          </p>

                          <div className="grid grid-cols-2 gap-1.5">
                            <button
                              onClick={() => setAnimationModelingAligned(true)}
                              className={`px-3 py-2 rounded-lg border text-left transition-all duration-200 text-xs ${
                                animationModelingAligned === true
                                  ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500"
                                  : "border-border hover:border-muted-foreground/50"
                              }`}
                              data-testid="combined-modeling-aligned-yes"
                            >
                              <div className="flex items-center gap-1.5">
                                {animationModelingAligned === true ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                ) : (
                                  <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                                )}
                                <span className={`font-medium ${animationModelingAligned === true ? "text-emerald-700" : "text-foreground"}`}>
                                  Yes — modeled
                                </span>
                              </div>
                            </button>

                            <button
                              onClick={() => setAnimationModelingAligned(false)}
                              className={`px-3 py-2 rounded-lg border text-left transition-all duration-200 text-xs ${
                                animationModelingAligned === false
                                  ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500"
                                  : "border-border hover:border-muted-foreground/50"
                              }`}
                              data-testid="combined-modeling-aligned-no"
                            >
                              <div className="flex items-center gap-1.5">
                                {animationModelingAligned === false ? (
                                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                                )}
                                <span className={`font-medium ${animationModelingAligned === false ? "text-amber-700" : "text-foreground"}`}>
                                  Not yet
                                </span>
                              </div>
                            </button>
                          </div>

                          {animationModelingAligned === false && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-amber-700">
                                  Video animation requires all spaces to be fully modeled. Update modeling scope before proceeding.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Interior Design Direction - Only shows when interior visualization scope exists */}
                    {hasInteriorVisualization && (
                      <>
                        <Separator />
                        <div className="space-y-5">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                              <Paintbrush className="w-4 h-4" />
                              Interior Design Direction
                            </h4>
                            {interiorDesignDirectionCalc.totalFee > 0 && (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                                +${interiorDesignDirectionCalc.totalFee.toLocaleString()}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Intro Helper Text */}
                          <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              This section helps determine whether TRP is replicating an existing interior design or creating design direction conceptually. Additional fees apply when interior design information is incomplete.
                            </p>
                          </div>
                          
                          {/* Auto-detected Space Count */}
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium">Spaces Requiring Interior Design Review</p>
                                <p className="text-xs text-muted-foreground">Based on interior renders and tours currently selected</p>
                              </div>
                              <Badge variant="secondary" className="text-base px-3">
                                {autoDetectedInteriorSpaces}
                              </Badge>
                            </div>
                            
                            {/* Manual Override */}
                            <div className="flex items-center gap-3">
                              <Label className="text-xs whitespace-nowrap">Manual Override:</Label>
                              <Input 
                                type="number" 
                                min="0"
                                value={interiorDesignSpacesOverride ?? ""} 
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setInteriorDesignSpacesOverride(val === "" ? null : parseInt(val) || 0);
                                }}
                                placeholder={autoDetectedInteriorSpaces.toString()}
                                className="h-8 w-24"
                              />
                              {interiorDesignSpacesOverride !== null && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => setInteriorDesignSpacesOverride(null)}
                                  className="h-8 px-2 text-xs"
                                >
                                  Reset
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {/* Per-Space Design Status */}
                          {effectiveInteriorSpaceCount > 0 && (
                            <div className="space-y-3">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Per-Space Design Status</p>
                              <div className="space-y-2">
                                {interiorDesignSpaces.map((space, idx) => {
                                  const spaceCalc = interiorDesignDirectionCalc.spaces.find(s => s.id === space.id);
                                  return (
                                    <div key={space.id} className="p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                                      <div className="flex items-start gap-3">
                                        {/* Space Name */}
                                        <div className="flex-1 min-w-0">
                                          <Input
                                            value={space.name}
                                            onChange={(e) => updateInteriorDesignSpace(space.id, "name", e.target.value)}
                                            placeholder={`Space ${idx + 1}`}
                                            className="h-8 text-sm font-medium"
                                          />
                                        </div>
                                        
                                        {/* Completeness Level Selector */}
                                        <div className="w-48">
                                          <Select 
                                            value={space.completeness_level} 
                                            onValueChange={(v) => updateInteriorDesignSpace(space.id, "completeness_level", v)}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {DESIGN_COMPLETENESS_LEVELS.map(level => (
                                                <SelectItem key={level.value} value={level.value}>
                                                  <span className="flex items-center gap-2">
                                                    {level.shortLabel}
                                                    <span className={`text-xs ${level.fee === 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                                      {level.fee === 0 ? '($0)' : `(+$${level.fee.toLocaleString()})`}
                                                    </span>
                                                  </span>
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        
                                        {/* Fee Display */}
                                        <div className={`w-20 text-right font-semibold tabular-nums ${spaceCalc?.fee > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                          ${spaceCalc?.fee?.toLocaleString() || '0'}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Sales Guidance Collapsible */}
                          <Collapsible open={showDesignGuidance} onOpenChange={setShowDesignGuidance}>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="w-full justify-between h-9 text-xs text-muted-foreground hover:text-foreground">
                                <span className="flex items-center gap-2">
                                  <HelpCircle className="w-3.5 h-3.5" />
                                  How to Assess Interior Design Completeness
                                </span>
                                {showDesignGuidance ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="mt-3 space-y-3">
                                {DESIGN_COMPLETENESS_LEVELS.map(level => (
                                  <div key={level.value} className="p-4 rounded-lg border bg-muted/30">
                                    <div className="flex items-start justify-between mb-2">
                                      <h5 className="font-medium text-sm">{level.label}</h5>
                                      <Badge variant="outline" className={level.fee === 0 ? 'text-green-600 border-green-600/30' : 'text-amber-600 border-amber-600/30'}>
                                        {level.fee === 0 ? '$0' : `+$${level.fee.toLocaleString()}/space`}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-3">{level.description}</p>
                                    
                                    <div className="grid grid-cols-2 gap-4 text-xs">
                                      <div>
                                        <p className="font-medium text-muted-foreground mb-1.5 uppercase tracking-wide text-[10px]">Ask the client about:</p>
                                        <ul className="space-y-0.5">
                                          {level.questions.slice(0, 4).map((q, i) => (
                                            <li key={i} className="text-muted-foreground flex items-start gap-1.5">
                                              <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-400" />
                                              {q}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <p className="font-medium text-muted-foreground mb-1.5 uppercase tracking-wide text-[10px]">TRP Impact:</p>
                                        <p className="text-muted-foreground">{level.trpImpact}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                          
                          {/* Total Interior Design Direction Fee */}
                          {interiorDesignDirectionCalc.totalFee > 0 && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                              <span className="text-sm font-medium">Total Interior Design Direction</span>
                              <span className="text-lg font-bold text-amber-600 tabular-nums">
                                ${interiorDesignDirectionCalc.totalFee.toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                                  {/* Conceptual Exterior Modeling — consolidated into the Visualization menu.
                    Acts as a sub-section with its own Collapsible toggle so users can
                    expand/collapse it independently of the parent Viz collapsible. */}
                <div className="-mx-6 px-6 pt-6 border-t border-border/60" data-testid="viz-concept-modeling-section">
                  <Collapsible open={expandedSections.conceptModeling} onOpenChange={() => toggleSection("conceptModeling")}>
                    <CollapsibleTrigger asChild>
                      <div className="cursor-pointer hover:bg-muted/30 transition-colors -mx-6 px-6 py-3 rounded-md">
                        <div className="flex items-center justify-between">
                          <h4 className="text-base font-semibold flex items-center gap-2">
                            <Mountain className="w-4 h-4 text-sky-500" />
                            Conceptual Exterior Modeling
                          </h4>
                          <div className="flex items-center gap-3">
                            {conceptModelingEnabled && conceptModelingCalc.grandTotal > 0 && (
                              <Badge className="bg-sky-500/10 text-sky-500 border-sky-500/20">${conceptModelingCalc.grandTotal.toLocaleString()}</Badge>
                            )}
                            <Switch
                              checked={conceptModelingEnabled}
                              onCheckedChange={(checked) => {
                                setConceptModelingEnabled(checked);
                                if (checked) setExpandedSections(prev => ({ ...prev, conceptModeling: true }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid="concept-modeling-toggle"
                            />
                            {expandedSections.conceptModeling ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-6 pt-4 pb-2">
                        {!conceptModelingEnabled ? (
                          <p className="text-sm text-muted-foreground">
                            Enable to add conceptual exterior modeling scope — adapt, modify, or fully create building concepts.
                          </p>
                        ) : (
                          <div className="space-y-6">
                            {/* Modeling Approach Selection — 3 selectable cards */}
                            <div className="space-y-2">
                              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Modeling Approach</Label>
                              <div className="grid grid-cols-3 gap-3">
                                {[
                                  { key: "lite", label: "Concept Lite", subtitle: "Adapt Existing Model", desc: "Use a pre-existing 3D model and adapt materials and proportions.", color: "sky", icon: "◇" },
                                  { key: "plus", label: "Concept Plus", subtitle: "Modify Existing Model", desc: "Start from a base model and adjust massing and facade.", color: "blue", icon: "◈", badge: "Default" },
                                  { key: "studio", label: "Concept Studio", subtitle: "Fully Custom Creation", desc: "Create a building concept from scratch.", color: "indigo", icon: "◆" }
                                ].map(opt => (
                                  <button
                                    key={opt.key}
                                    onClick={() => setConceptApproach(opt.key)}
                                    className={`
                                      relative text-left p-4 rounded-xl border-2 transition-all duration-200
                                      ${conceptApproach === opt.key
                                        ? `border-${opt.color}-500 bg-${opt.color}-50 ring-1 ring-${opt.color}-500 shadow-sm`
                                        : 'border-border hover:border-muted-foreground/40 hover:shadow-sm'
                                      }
                                    `}
                                    style={conceptApproach === opt.key ? {
                                      borderColor: opt.color === 'sky' ? '#3D6355' : opt.color === 'blue' ? '#3D6355' : '#3D6355',
                                      backgroundColor: opt.color === 'sky' ? '#ECF0EA' : opt.color === 'blue' ? '#ECF0EA' : '#ECF0EA'
                                    } : {}}
                                    data-testid={`concept-approach-${opt.key}`}
                                  >
                                    {opt.badge && (
                                      <span className="absolute -top-2 right-3 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500 text-white">
                                        {opt.badge}
                                      </span>
                                    )}
                                    <div className="text-lg mb-1">{opt.icon}</div>
                                    <div className="font-semibold text-sm">{opt.label}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{opt.subtitle}</div>
                                    <p className="text-[11px] text-muted-foreground/80 mt-2 leading-relaxed">{opt.desc}</p>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Number of Buildings */}
                            <div className="space-y-1">
                              <Label className="text-xs">Number of Buildings</Label>
                              <div className="flex items-center gap-3">
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setConceptBuildings(Math.max(1, conceptBuildings - 1))} data-testid="concept-buildings-minus">
                                  <Minus className="w-3.5 h-3.5" />
                                </Button>
                                <Input
                                  type="number"
                                  min={1}
                                  value={conceptBuildings}
                                  onChange={(e) => setConceptBuildings(Math.max(1, parseInt(e.target.value) || 1))}
                                  className="h-8 w-20 text-center"
                                  data-testid="concept-buildings-input"
                                />
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setConceptBuildings(conceptBuildings + 1)} data-testid="concept-buildings-plus">
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>

                            {/* Tier-specific inputs */}
                            {conceptApproach === "lite" && (
                              <div className="p-3 rounded-lg bg-sky-50 border border-sky-100">
                                <div className="flex items-start gap-2">
                                  <Info className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-sky-700 leading-relaxed">
                                    Includes sourcing, cleanup, optimization, and material adjustments. No structural or architectural modifications included.
                                  </p>
                                </div>
                                <div className="mt-2 text-sm font-semibold text-sky-800">
                                  ${(2000).toLocaleString()} per building
                                </div>
                              </div>
                            )}

                            {conceptApproach === "plus" && (
                              <div className="space-y-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Modification Level</Label>
                                  <Select value={conceptModLevel} onValueChange={setConceptModLevel}>
                                    <SelectTrigger data-testid="concept-mod-level-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="light">
                                        <div>
                                          <span className="font-medium">Light Modifications</span>
                                          <span className="text-muted-foreground ml-2">+$1,500</span>
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="medium">
                                        <div>
                                          <span className="font-medium">Medium Modifications</span>
                                          <span className="text-muted-foreground ml-2">+$3,000</span>
                                        </div>
                                      </SelectItem>
                                      <SelectItem value="heavy">
                                        <div>
                                          <span className="font-medium">Heavy Modifications</span>
                                          <span className="text-muted-foreground ml-2">+$6,000</span>
                                        </div>
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <p className="text-[11px] text-muted-foreground">
                                    {conceptModLevel === "light" && "Material tweaks, small facade changes"}
                                    {conceptModLevel === "medium" && "Balconies, openings, proportion adjustments"}
                                    {conceptModLevel === "heavy" && "Major massing changes, structural reshaping"}
                                  </p>
                                </div>
                                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                                  <p className="text-xs text-blue-700">
                                    Includes base model adaptation + selected level of customization to match project vision.
                                  </p>
                                  <div className="mt-1.5 text-sm font-semibold text-blue-800">
                                    ${(3000).toLocaleString()} base + ${({light:1500,medium:3000,heavy:6000})[conceptModLevel].toLocaleString()} mod = ${(3000 + ({light:1500,medium:3000,heavy:6000})[conceptModLevel]).toLocaleString()} per building
                                  </div>
                                </div>
                              </div>
                            )}

                            {conceptApproach === "studio" && (
                              <div className="space-y-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Complexity Level</Label>
                                  <Select value={conceptComplexity} onValueChange={setConceptComplexity}>
                                    <SelectTrigger data-testid="concept-complexity-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="standard">Standard — $10,000</SelectItem>
                                      <SelectItem value="complex">Complex — $15,000</SelectItem>
                                      <SelectItem value="landmark">Landmark / Signature — Custom</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                {conceptComplexity === "landmark" && (
                                  <div className="space-y-1">
                                    <Label className="text-xs">Landmark Price per Building</Label>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-muted-foreground">$</span>
                                      <Input
                                        type="number"
                                        min={20000}
                                        value={conceptLandmarkPrice}
                                        onChange={(e) => setConceptLandmarkPrice(parseInt(e.target.value) || 20000)}
                                        className="h-8 w-32"
                                        data-testid="concept-landmark-price"
                                      />
                                    </div>
                                  </div>
                                )}
                                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                                  <p className="text-xs text-indigo-700 leading-relaxed">
                                    Includes full conceptual design process: massing studies, facade development, and iterative refinement.
                                  </p>
                                </div>
                              </div>
                            )}

                            <Separator />

                            {/* Add-ons */}
                            <div className="space-y-3">
                              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Add-ons</Label>

                              {/* Site Context */}
                              <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs flex items-center gap-1.5">
                                    <Trees className="w-3.5 h-3.5 text-emerald-500" />
                                    Site Context Modeling
                                  </Label>
                                  {conceptSiteContext !== "none" && (
                                    <span className="text-xs font-semibold text-emerald-600">+${({simple:1500,medium:3000,complex:5000})[conceptSiteContext]?.toLocaleString()}</span>
                                  )}
                                </div>
                                <Select value={conceptSiteContext} onValueChange={setConceptSiteContext}>
                                  <SelectTrigger className="h-8" data-testid="concept-site-context-select">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    <SelectItem value="simple">Simple — $1,500</SelectItem>
                                    <SelectItem value="medium">Medium — $3,000</SelectItem>
                                    <SelectItem value="complex">Complex — $5,000</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Render Optimization */}
                              <div className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-1.5">
                                  <Zap className="w-3.5 h-3.5 text-amber-500" />
                                  <Label className="text-xs">Rendering / Animation Optimization</Label>
                                  <div className="group relative">
                                    <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50 w-48 p-2 text-[10px] bg-popover border rounded-md shadow-md">
                                      Prepares model for animation, VEXOS integration, and high-performance rendering workflows.
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {conceptRenderOptimization && <span className="text-xs font-semibold text-amber-600">+$2,000</span>}
                                  <Switch checked={conceptRenderOptimization} onCheckedChange={setConceptRenderOptimization} data-testid="concept-render-opt-toggle" />
                                </div>
                              </div>

                              {/* VEXOS Structuring */}
                              <div className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-1.5">
                                  <Globe className="w-3.5 h-3.5 text-violet-500" />
                                  <Label className="text-xs">VEXOS Structuring</Label>
                                  <div className="group relative">
                                    <HelpCircle className="w-3 h-3 text-muted-foreground cursor-help" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-50 w-48 p-2 text-[10px] bg-popover border rounded-md shadow-md">
                                      Breaks model into usable components for interactive sales experiences.
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {conceptVexosStructuring && <span className="text-xs font-semibold text-violet-600">+$2,500</span>}
                                  <Switch checked={conceptVexosStructuring} onCheckedChange={setConceptVexosStructuring} data-testid="concept-vexos-toggle" />
                                </div>
                              </div>
                            </div>

                            {/* Sales Guidance Panel */}
                            <Collapsible>
                              <CollapsibleTrigger className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                                <HelpCircle className="w-3.5 h-3.5" />
                                <span>Sales Guidance</span>
                                <ChevronDown className="w-3 h-3" />
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="mt-2 p-3 rounded-lg bg-muted/50 border space-y-2.5 text-[11px] leading-relaxed">
                                  <div>
                                    <p className="font-semibold text-foreground">Quick Decision Frame</p>
                                    <p className="text-muted-foreground">Lite = Fast + Affordable · Plus = Balanced · Studio = Custom + Premium</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-foreground">Key Question to Ask</p>
                                    <p className="text-muted-foreground italic">"Are we mostly adjusting the look, or actually changing the structure of the building?"</p>
                                  </div>
                                  <div className="space-y-1">
                                    <p className="font-semibold text-amber-700">Rules</p>
                                    <p className="text-muted-foreground">If client says "custom design" → use Studio</p>
                                    <p className="text-muted-foreground">If unsure → default to Plus</p>
                                    <p className="text-muted-foreground">NEVER include rendering in this scope</p>
                                    <p className="text-muted-foreground">ALWAYS clarify modification level early</p>
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>

                            {/* Section Subtotal */}
                            <div className="flex items-center justify-between pt-2 border-t">
                              <span className="text-sm font-medium">Conceptual Exterior Modeling Total</span>
                              <span className="text-lg font-bold text-sky-600">${conceptModelingCalc.grandTotal.toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>


            {/* Marketing Strategy & Lead Generation */}
            <Collapsible open={expandedSections.marketing} onOpenChange={() => toggleSection("marketing")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-orange-500" />
                        Marketing Strategy & Lead Generation
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        {marketingEnabled && marketingCalc.grandTotal > 0 && (
                          <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">
                            {`$${Math.round(marketingCalc.monthlyBudget).toLocaleString()}/mo`}
                          </Badge>
                        )}
                        <Switch
                          checked={marketingEnabled}
                          onCheckedChange={(checked) => {
                            setMarketingEnabled(checked);
                            if (checked) setExpandedSections(prev => ({ ...prev, marketing: true }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="marketing-section-toggle"
                        />
                        {expandedSections.marketing ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {!marketingEnabled ? (
                      <p className="text-sm text-muted-foreground">
                        Enable to add marketing lead generation scope — Pre-Lease or Pre-Sale campaigns.
                      </p>
                    ) : (
                      <div className="space-y-6">
                        {/* Mode Toggle */}
                        <div className="space-y-2">
                          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Campaign Type</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {[{ key: "pre_lease", label: "Pre-Lease" }, { key: "pre_sale", label: "Pre-Sale" }].map(m => (
                              <button
                                key={m.key}
                                onClick={() => { setMarketingMode(m.key); setMarketingTier("t3"); setMarketingConvRate(m.key === "pre_lease" ? 3 : 1.5); }}
                                className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                  marketingMode === m.key
                                    ? "border-orange-500 bg-orange-50 text-orange-700"
                                    : "border-border hover:border-muted-foreground/30"
                                }`}
                                data-testid={`mktg-mode-${m.key}`}
                              >
                                {m.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tier Selection */}
                        <div className="space-y-2">
                          <Label className="text-xs">{marketingMode === "pre_lease" ? "Average Rent Per Unit" : "Average Price Per Unit"}</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {(marketingMode === "pre_lease"
                              ? [{ id: "t1", label: "below $1,500", sub: "CPL: $10" }, { id: "t2", label: "$1,500 - $2,500", sub: "CPL: $15" }, { id: "t3", label: "$2,500 - $4,500", sub: "CPL: $25" }, { id: "t4", label: "$4,500+", sub: "CPL: $40" }]
                              : [{ id: "t1", label: "below $600K", sub: "CPL: $35" }, { id: "t2", label: "$600K - $1.2M", sub: "CPL: $60" }, { id: "t3", label: "$1.2M - $2.5M", sub: "CPL: $75" }, { id: "t4", label: "$2.5M+", sub: "CPL: $100" }]
                            ).map(tier => (
                              <button
                                key={tier.id}
                                onClick={() => setMarketingTier(tier.id)}
                                className={`text-left p-3 rounded-lg border text-sm transition-all ${
                                  marketingTier === tier.id
                                    ? "border-orange-400 bg-orange-50"
                                    : "border-border hover:border-muted-foreground/30"
                                }`}
                                data-testid={`mktg-tier-${tier.id}`}
                              >
                                <div className="font-medium">{tier.label}</div>
                                <div className="text-xs text-muted-foreground">{tier.sub}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Units */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Units</Label>
                            <EditableSliderBadge value={marketingUnits} min={4} max={5000} onChange={setMarketingUnits} integer={true} testId="mktg-units-display" />
                          </div>
                          <Slider value={[marketingUnits]} onValueChange={([v]) => setMarketingUnits(v)} min={4} max={5000} step={1} data-testid="mktg-units-slider" />
                          <div className="flex justify-between text-[10px] text-muted-foreground"><span>4</span><span>5,000</span></div>
                        </div>

                        {/* Timeline */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">{marketingMode === "pre_lease" ? "Lease Out" : "Sell Out"} Timeline</Label>
                            <EditableSliderBadge
                              value={marketingTimeline}
                              min={6}
                              max={60}
                              step={3}
                              onChange={setMarketingTimeline}
                              integer={true}
                              testId="mktg-timeline-display"
                              formatter={(v) => v >= 36 ? `${v} months · ${v / 12} years` : `${v} months`}
                            />
                          </div>
                          <Slider value={[marketingTimeline]} onValueChange={([v]) => setMarketingTimeline(v)} min={6} max={60} step={3} data-testid="mktg-timeline-slider" />
                          <div className="flex justify-between text-[10px] text-muted-foreground"><span>6 months</span><span>60 months · 5 years</span></div>
                        </div>

                        {/* Conversion Rate */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Sales Team Conversion Rate</Label>
                            <EditableSliderBadge
                              value={marketingConvRate}
                              min={marketingMode === "pre_lease" ? 1.5 : 1}
                              max={marketingMode === "pre_lease" ? 8 : 5}
                              onChange={setMarketingConvRate}
                              integer={false}
                              decimals={1}
                              suffix="%"
                              testId="mktg-conv-display"
                            />
                          </div>
                          <Slider
                            value={[marketingConvRate * 10]}
                            onValueChange={([v]) => setMarketingConvRate(v / 10)}
                            min={(marketingMode === "pre_lease" ? 1.5 : 1) * 10}
                            max={(marketingMode === "pre_lease" ? 8 : 5) * 10}
                            step={5}
                            data-testid="mktg-conv-slider"
                          />
                          <div className="flex justify-between text-[10px] text-muted-foreground">
                            <span>{marketingMode === "pre_lease" ? "1.5" : "1"}%</span>
                            <span>{marketingMode === "pre_lease" ? "8" : "5"}%</span>
                          </div>
                        </div>

                        <Separator />

                        {/* Summary */}
                        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Recommended Monthly Budget</span>
                            <span className="text-lg font-bold text-orange-600">${Math.round(marketingCalc.monthlyBudget).toLocaleString()}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <span className="text-muted-foreground">Setup Fee</span>
                            <span className="text-right font-medium">${MKTG_SETUP_FEE.toLocaleString()}</span>
                            <span className="text-muted-foreground">Monthly Service Fee</span>
                            <span className="text-right font-medium">${MKTG_SERVICE_FEE.toLocaleString()}</span>
                            <span className="text-muted-foreground">Monthly Ad Spend</span>
                            <span className="text-right font-medium">${Math.round(marketingCalc.monthlyAdSpend).toLocaleString()}</span>
                            <span className="text-muted-foreground">Est. Monthly Leads</span>
                            <span className="text-right font-medium">{marketingCalc.monthlyLeads.toLocaleString()}</span>
                            <span className="text-muted-foreground">Total Est. Leads</span>
                            <span className="text-right font-medium">{marketingCalc.totalLeads.toLocaleString()}</span>
                          </div>
                        </div>

                        <Separator />

                        {/* AI-Enhanced Marketing Content — Packages + Custom */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Content Strategy</Label>
                              <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-violet-300 text-violet-500">Optional</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              {mktgContentEnabled && (
                                <div className="flex gap-1 p-0.5 bg-muted rounded-md">
                                  {[{ key: "package", label: "Packages" }, { key: "custom", label: "Custom" }].map(m => (
                                    <button key={m.key} onClick={() => setMktgContentMode(m.key)} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${mktgContentMode === m.key ? "bg-background shadow-sm" : "text-muted-foreground"}`} data-testid={`mktg-content-mode-${m.key}`}>
                                      {m.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <Switch checked={mktgContentEnabled} onCheckedChange={setMktgContentEnabled} data-testid="mktg-content-enabled-toggle" />
                            </div>
                          </div>

                          {mktgContentEnabled && mktgContentMode === "package" ? (
                            <>
                              <div className="grid grid-cols-3 gap-2">
                                {MKTG_CONTENT_PACKAGES.map(pkg => {
                                  const isSelected = mktgSelectedPackage === pkg.id;
                                  const tc = pkg.clipsPerMonth * marketingTimeline;
                                  const te = pkg.enhancementsBase;
                                  return (
                                    <button key={pkg.id} onClick={() => setMktgSelectedPackage(pkg.id)}
                                      className={`text-left p-3 rounded-lg border text-xs transition-all ${isSelected ? "border-violet-400 bg-violet-50" : "border-border hover:border-muted-foreground/30"}`}
                                      data-testid={`mktg-pkg-${pkg.id}`}>
                                      <div className="font-semibold">{pkg.label}</div>
                                      <div className="text-muted-foreground mt-1">{pkg.clipsPerMonth} clips/mo · {te} enh</div>
                                      <div className="font-semibold mt-1 text-violet-600">${((tc * 899) + (te * 599)).toLocaleString()}</div>
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                <div className="flex items-center gap-2">
                                  <RefreshCw className={`w-3.5 h-3.5 ${mktgRetainerEnabled ? "text-violet-500" : "text-muted-foreground"}`} />
                                  <Label className="text-xs">Monthly Retainer</Label>
                                  {mktgRetainerEnabled && <Badge className="text-[9px] px-1 py-0 bg-emerald-500/10 text-emerald-600 border-emerald-200">8% OFF</Badge>}
                                </div>
                                <Switch checked={mktgRetainerEnabled} onCheckedChange={setMktgRetainerEnabled} data-testid="mktg-retainer-toggle" />
                              </div>
                              {mktgRetainerEnabled && marketingCalc.retainerMonthly > 0 && (
                                <p className="text-xs font-semibold text-violet-600 text-right">${marketingCalc.retainerMonthly.toLocaleString()}/mo retainer</p>
                              )}
                            </>
                          ) : mktgContentEnabled && mktgContentMode === "custom" ? (
                            <>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2"><Label className="text-xs">Animated Renders</Label><span className="text-[10px] text-muted-foreground">$899/ea</span></div>
                                <Switch checked={mktgAiAnimatedEnabled} onCheckedChange={setMktgAiAnimatedEnabled} data-testid="mktg-ai-animated-toggle" />
                              </div>
                              {mktgAiAnimatedEnabled && (
                                <div className="flex items-center gap-3 ml-4">
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setMktgAiAnimatedQty(Math.max(1, mktgAiAnimatedQty - 1))}><Minus className="w-3 h-3" /></Button>
                                  <span className="text-sm font-semibold w-6 text-center" data-testid="mktg-ai-animated-qty">{mktgAiAnimatedQty}</span>
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setMktgAiAnimatedQty(mktgAiAnimatedQty + 1)}><Plus className="w-3 h-3" /></Button>
                                  <span className="text-xs font-semibold text-violet-600 ml-auto">${(mktgAiAnimatedQty * 899).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2"><Label className="text-xs">Render Enhancement</Label><span className="text-[10px] text-muted-foreground">$599/ea</span></div>
                                <Switch checked={mktgAiEnhanceEnabled} onCheckedChange={setMktgAiEnhanceEnabled} data-testid="mktg-ai-enhance-toggle" />
                              </div>
                              {mktgAiEnhanceEnabled && (
                                <div className="flex items-center gap-3 ml-4">
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setMktgAiEnhanceQty(Math.max(1, mktgAiEnhanceQty - 1))}><Minus className="w-3 h-3" /></Button>
                                  <span className="text-sm font-semibold w-6 text-center" data-testid="mktg-ai-enhance-qty">{mktgAiEnhanceQty}</span>
                                  <Button variant="outline" size="icon" className="h-6 w-6" onClick={() => setMktgAiEnhanceQty(mktgAiEnhanceQty + 1)}><Plus className="w-3 h-3" /></Button>
                                  <span className="text-xs font-semibold text-violet-600 ml-auto">${(mktgAiEnhanceQty * 599).toLocaleString()}</span>
                                </div>
                              )}
                              {(mktgAiAnimatedEnabled || mktgAiEnhanceEnabled) && (
                                <p className="text-[10px] text-muted-foreground italic">AI-assisted — no revision rounds included.</p>
                              )}
                            </>
                          ) : null}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t">
                          <span className="text-sm font-medium">Total Campaign Investment</span>
                          <span className="text-lg font-bold text-orange-600">${marketingCalc.grandTotal.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Collateral Suite */}
            <Collapsible open={expandedSections.collateral} onOpenChange={() => toggleSection("collateral")}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        Collateral Suite
                      </CardTitle>
                      <div className="flex items-center gap-3">
                        {collateralEnabled && collateralCalc.grandTotal > 0 && (
                          <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 tabular-nums">
                            ${collateralCalc.grandTotal.toLocaleString()}
                          </Badge>
                        )}
                        <Switch
                          checked={collateralEnabled}
                          onCheckedChange={(checked) => {
                            setCollateralEnabled(checked);
                            if (checked) setExpandedSections(prev => ({ ...prev, collateral: true }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="collateral-section-toggle"
                        />
                        {expandedSections.collateral ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    {!collateralEnabled ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Toggle on to add branding, sales collateral, and AI-enhanced content.</p>
                      </div>
                    ) : (
                      <CollateralBlock
                        value={collateralState}
                        onChange={setCollateralState}
                        prices={collateralPrices}
                        subtotal={collateralCalc.grandTotal}
                      />
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>


            {/* AI Content Studio Module */}
            <Collapsible open={expandedSections.aiContent} onOpenChange={() => toggleSection("aiContent")}>
              <Card className={`overflow-hidden border-white/[0.08] ${aiContentModuleEnabled ? 'bg-fuchsia-500/[0.04]' : 'bg-[#000000]'}`}>
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between px-6 py-5 hover:bg-white/[0.02] transition-colors" data-testid="ai-content-toggle-btn">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-fuchsia-400" />
                      <span className="text-[15px] font-semibold tracking-tight text-white">AI Content Studio</span>
                      {aiContentTotal > 0 && aiContentModuleEnabled && (
                        <Badge className="ml-1 bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/25 tabular-nums" data-testid="ai-content-subtotal">
                          ${aiContentTotal.toLocaleString()}
                        </Badge>
                      )}
                      {!aiContentModuleEnabled && <Badge className="ml-1 bg-white/[0.04] text-white/40 border-white/[0.08]">$0</Badge>}
                    </div>
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={aiContentModuleEnabled}
                        onCheckedChange={(checked) => {
                          setAiContentModuleEnabled(checked);
                          if (checked) setExpandedSections(prev => ({ ...prev, aiContent: true }));
                        }}
                        data-testid="ai-content-module-toggle"
                      />
                      {expandedSections.aiContent ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    {!aiContentModuleEnabled ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <Sparkles className="w-10 h-10 mx-auto mb-3 text-fuchsia-400/40" />
                        <p className="text-sm">AI reels, motion videos, lifestyle scenes & social bundles. Toggle on to configure.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {aiContentItems.map((item, idx) => {
                          const subtotal = _aiItemSubtotal(item);
                          const active = item.isMotionVariant
                            ? !!item.selectedVariantId
                            : (item.qty || 0) > 0;

                          // ── AI Motion — single-select variant picker ──
                          // 3 duration variants (30s/60s/90s) rendered as tiles.
                          // Clicking a tile selects; clicking the selected tile deselects.
                          if (item.isMotionVariant) {
                            return (
                              <div
                                key={item.id}
                                className={`p-4 rounded-xl border transition-all ${active ? 'border-violet-500/40 bg-violet-500/[0.07]' : 'border-border bg-card/40'}`}
                                data-testid={`ai-content-item-${item.id}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[14px] font-semibold text-foreground">{item.label}</span>
                                      <span className="text-[9px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-400 bg-violet-500/10">
                                        Select Duration
                                      </span>
                                    </div>
                                    {item.tagline && (
                                      <p className="text-[11px] text-violet-400/80 mt-1 tracking-wide font-medium">{item.tagline}</p>
                                    )}
                                  </div>
                                  {subtotal > 0 && (
                                    <div className="text-right flex-shrink-0">
                                      <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Subtotal</div>
                                      <div className="text-sm font-semibold tabular-nums text-violet-300" data-testid={`ai-${item.id}-subtotal`}>${subtotal.toLocaleString()}</div>
                                    </div>
                                  )}
                                </div>

                                <p className="text-[12px] text-muted-foreground leading-relaxed mt-3">{item.description}</p>

                                <div className="grid grid-cols-3 gap-2 mt-4">
                                  {(item.variants || []).map((v) => {
                                    const isSelected = item.selectedVariantId === v.id;
                                    return (
                                      <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => setAiContentItems(prev => prev.map((p, i) => {
                                          if (i !== idx) return p;
                                          const next = p.selectedVariantId === v.id ? null : v.id;
                                          return { ...p, selectedVariantId: next, qty: next ? 1 : 0, rate: next ? v.rate : 0 };
                                        }))}
                                        className={`p-3 rounded-lg border text-left transition-all ${isSelected ? 'border-violet-500/60 bg-violet-500/[0.10] shadow-[0_0_0_1px_rgba(139,92,246,0.25)_inset]' : 'border-border bg-card/30 hover:border-violet-500/30 hover:bg-card/60'}`}
                                        data-testid={`ai-motion-variant-${v.id}`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <div className={`text-[13px] font-semibold ${isSelected ? 'text-violet-200' : 'text-foreground/85'}`}>{v.label}</div>
                                            <div className="text-[10.5px] text-muted-foreground mt-0.5">${v.rate.toLocaleString()}</div>
                                          </div>
                                          {isSelected && <div className="w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>

                                <p className="text-[10px] text-muted-foreground/70 italic mt-3">One AI Motion selection per proposal. Tap a tile to select / deselect.</p>
                              </div>
                            );
                          }

                          // ── Premium block treatment for legacy "Advanced Render Enhancement" ──
                          // Restored from MarketingCalculator with full subtext: tagline, badge,
                          // enhancement process steps, disclaimer, and expandable sales positioning.
                          if (item.isPremiumBlock) {
                            return (
                              <div
                                key={item.id}
                                className={`p-4 rounded-xl border transition-all ${active ? 'border-rose-500/40 bg-rose-500/[0.08]' : 'border-border bg-card/40'}`}
                                data-testid={`ai-content-item-${item.id}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-[14px] font-semibold text-foreground">{item.label}</span>
                                      {item.badge && (
                                        <span className="text-[9px] font-bold tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border border-rose-500/30 text-rose-400 bg-rose-500/10">
                                          {item.badge}
                                        </span>
                                      )}
                                    </div>
                                    {item.tagline && (
                                      <p className="text-[11px] text-rose-400/80 mt-1 tracking-wide font-medium">{item.tagline}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">${item.rate.toLocaleString()}/{item.unit || "ea"}</span>
                                  </div>
                                </div>

                                <p className="text-[12px] text-muted-foreground leading-relaxed mt-3">
                                  {item.description}
                                </p>

                                {item.processSteps && item.processSteps.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/55 mb-2">Enhancement Process</p>
                                    <div className="grid grid-cols-2 gap-x-5 gap-y-1.5">
                                      {item.processSteps.map((step, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                          <Zap className="w-3 h-3 mt-[3px] text-rose-400 flex-shrink-0" />
                                          <span className="text-[11.5px] text-foreground/80 leading-snug">{step}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center gap-3 mt-4 pt-3 border-t border-border/60">
                                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: Math.max(0, (p.qty || 0) - 1) } : p))} data-testid={`ai-${item.id}-minus`}><Minus className="w-3 h-3" /></Button>
                                  <Input type="number" value={item.qty} onChange={(e) => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: Math.max(0, parseInt(e.target.value) || 0) } : p))} className="text-center h-8 text-sm w-14" data-testid={`ai-${item.id}-qty`} />
                                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: (p.qty || 0) + 1 } : p))} data-testid={`ai-${item.id}-plus`}><Plus className="w-3 h-3" /></Button>
                                  <span className="text-[11px] text-muted-foreground">{item.unit || "ea"}{(item.qty || 0) === 1 ? '' : 's'}</span>
                                  <div className="ml-auto text-right">
                                    <div className="text-sm font-semibold tabular-nums text-rose-400" data-testid={`ai-${item.id}-subtotal`}>{subtotal > 0 ? `$${subtotal.toLocaleString()}` : '—'}</div>
                                  </div>
                                </div>

                                {item.disclaimer && (
                                  <p className="text-[10.5px] text-muted-foreground/80 italic mt-2">{item.disclaimer}</p>
                                )}

                                {(item.salesTip || item.salesShort) && (
                                  <details className="mt-2 group">
                                    <summary className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground/80 transition-colors cursor-pointer list-none" data-testid={`ai-${item.id}-sales-tip-toggle`}>
                                      <HelpCircle className="w-3 h-3" />
                                      <span className="text-[10px] uppercase tracking-[0.12em] font-medium">Sales Positioning</span>
                                    </summary>
                                    <div className="mt-2 space-y-1.5 pl-5">
                                      {item.salesTip && <p className="text-[11.5px] text-foreground/75 leading-relaxed">{item.salesTip}</p>}
                                      {item.salesShort && <p className="text-[11px] text-foreground/55 italic">&ldquo;{item.salesShort}&rdquo;</p>}
                                    </div>
                                  </details>
                                )}
                              </div>
                            );
                          }

                          // Standard deliverable row
                          return (
                            <div key={item.id} className={`p-3 rounded-lg border transition-all ${active ? 'border-fuchsia-500/30 bg-fuchsia-500/5' : 'border-border bg-card/40'}`} data-testid={`ai-content-item-${item.id}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium">{item.label}</div>
                                  <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{item.description}</div>
                                  <div className="text-[11px] text-muted-foreground mt-1.5">${item.rate.toLocaleString()} / unit</div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: Math.max(0, (p.qty || 0) - 1) } : p))}><Minus className="w-3 h-3" /></Button>
                                  <Input type="number" value={item.qty} onChange={(e) => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: Math.max(0, parseInt(e.target.value) || 0) } : p))} onFocus={(e) => e.target.select()} className="text-center h-8 text-sm w-16 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setAiContentItems(prev => prev.map((p, i) => i === idx ? { ...p, qty: (p.qty || 0) + 1 } : p))}><Plus className="w-3 h-3" /></Button>
                                </div>
                                <div className="w-20 text-right text-sm font-medium tabular-nums text-fuchsia-300 self-center">{subtotal > 0 ? `$${subtotal.toLocaleString()}` : '—'}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* VEXOS Lot Explorer™ Module — Standalone premium software deployment.
                Custom 50/25/25 payment structure handled inside this module only.
                Persisted as `interactive_2d_sitemap` section + module_state.lot_explorer_enabled. */}
            <Collapsible open={expandedSections.lotExplorer} onOpenChange={() => toggleSection("lotExplorer")}>
              <Card className={`overflow-hidden border-white/[0.08] ${lotExplorerModuleEnabled ? 'bg-cyan-500/[0.05]' : 'bg-[#000000]'}`}>
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between px-6 py-5 hover:bg-white/[0.02] transition-colors" data-testid="lot-explorer-toggle-btn">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                      <span className="text-[15px] font-semibold tracking-tight text-white">VEXOS Lot Explorer&trade;</span>
                      <Badge variant="outline" className="ml-1 text-[10px] uppercase tracking-[0.1em] border-cyan-400/30 text-cyan-300 bg-cyan-500/[0.06]">Standalone Software</Badge>
                      {lotExplorerCalc.grandTotal > 0 && lotExplorerModuleEnabled && (
                        <Badge className="ml-1 bg-cyan-500/15 text-cyan-300 border-cyan-500/25 tabular-nums" data-testid="lot-explorer-subtotal">
                          ${lotExplorerCalc.grandTotal.toLocaleString()}
                        </Badge>
                      )}
                      {!lotExplorerModuleEnabled && <Badge className="ml-1 bg-white/[0.04] text-white/40 border-white/[0.08]">$0</Badge>}
                    </div>
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={lotExplorerModuleEnabled}
                        onCheckedChange={(checked) => {
                          setLotExplorerModuleEnabled(checked);
                          if (checked) setExpandedSections(prev => ({ ...prev, lotExplorer: true }));
                        }}
                        data-testid="lot-explorer-module-toggle"
                      />
                      {expandedSections.lotExplorer ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-5 pt-0">
                    {!lotExplorerModuleEnabled ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <MapPin className="w-10 h-10 mx-auto mb-3 text-cyan-400/40" />
                        <p className="text-sm max-w-md mx-auto">Enterprise-grade interactive sitemap & buyer-engagement platform. Per-lot pricing with dedicated <span className="text-cyan-300/80">50 / 25 / 25</span> payment structure.</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Number of Lots</Label>
                            <Input
                              type="number"
                              min="0"
                              value={lotExplorerLotCount || ""}
                              onChange={(e) => setLotExplorerLotCount(Math.max(0, parseInt(e.target.value) || 0))}
                              className="h-9"
                              placeholder="e.g. 120"
                              data-testid="lot-explorer-lot-count"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Price Per Lot ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={lotExplorerRatePerLot || ""}
                              onChange={(e) => setLotExplorerRatePerLot(Math.max(0, parseFloat(e.target.value) || 0))}
                              className="h-9"
                              data-testid="lot-explorer-rate-per-lot"
                            />
                          </div>
                        </div>

                        <div className="p-4 rounded-lg bg-cyan-500/[0.04] border border-cyan-500/15">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-[11px] uppercase tracking-[0.1em] text-cyan-300/70">Lot Explorer Subtotal</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">{lotExplorerCalc.lotCount.toLocaleString()} lots &times; ${lotExplorerCalc.ratePerLot.toLocaleString()}</div>
                            </div>
                            <div className="text-[22px] font-light text-cyan-200 tabular-nums" data-testid="lot-explorer-grand-total">
                              ${lotExplorerCalc.grandTotal.toLocaleString()}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Included Platform Features</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { state: lotExplorerIncludePoi, set: setLotExplorerIncludePoi, label: "POI Widget", testid: "lot-explorer-poi" },
                              { state: lotExplorerIncludeCrm, set: setLotExplorerIncludeCrm, label: "CRM Integration", testid: "lot-explorer-crm" },
                              { state: lotExplorerIncludeMedia, set: setLotExplorerIncludeMedia, label: "Media Pop-up Support", testid: "lot-explorer-media" },
                              { state: lotExplorerIncludeBackend, set: setLotExplorerIncludeBackend, label: "Backend Management", testid: "lot-explorer-backend" }
                            ].map((f, i) => (
                              <label key={i} className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${f.state ? 'border-cyan-500/30 bg-cyan-500/[0.05]' : 'border-border bg-card/40'}`}>
                                <Checkbox checked={f.state} onCheckedChange={(c) => f.set(!!c)} data-testid={f.testid} />
                                <span className="text-[12px] text-white/85">{f.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Lot Explorer Timeline — default 4 weeks with manual override */}
                        <div className="space-y-2.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Project Timeline</Label>
                            <label className="flex items-center gap-2 text-[11px] text-white/55 cursor-pointer">
                              <Checkbox
                                checked={lotExplorerTimelineOverrideEnabled}
                                onCheckedChange={(c) => setLotExplorerTimelineOverrideEnabled(!!c)}
                                data-testid="lot-explorer-timeline-override-toggle"
                              />
                              Manual override
                            </label>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className={`flex items-center gap-3 p-3 rounded-lg border ${lotExplorerTimelineOverrideEnabled ? 'border-border bg-card/30 opacity-60' : 'border-cyan-500/30 bg-cyan-500/[0.05]'}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.1em] text-white/45">Default</div>
                                <div className="text-[15px] font-semibold text-white tabular-nums" data-testid="lot-explorer-timeline-default-weeks">
                                  {lotExplorerCalc.timelineDefaultWeeks} {lotExplorerCalc.timelineDefaultWeeks === 1 ? "week" : "weeks"}
                                </div>
                              </div>
                            </div>
                            <div className={`flex items-center gap-2 p-3 rounded-lg border ${lotExplorerTimelineOverrideEnabled ? 'border-cyan-500/30 bg-cyan-500/[0.05]' : 'border-border bg-card/30 opacity-60'}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.1em] text-white/45 mb-1">Override (weeks)</div>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  disabled={!lotExplorerTimelineOverrideEnabled}
                                  value={lotExplorerTimelineOverrideWeeks}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "") { setLotExplorerTimelineOverrideWeeks(""); return; }
                                    const n = Math.max(1, Math.round(parseFloat(v) || 0));
                                    setLotExplorerTimelineOverrideWeeks(String(n));
                                  }}
                                  className="h-8 text-[13px]"
                                  placeholder="e.g. 6"
                                  data-testid="lot-explorer-timeline-override-input"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="text-[11px] text-white/50">
                            Effective: <span className="text-cyan-300 font-medium tabular-nums" data-testid="lot-explorer-timeline-effective">{lotExplorerCalc.timelineWeeks} {lotExplorerCalc.timelineWeeks === 1 ? "week" : "weeks"}</span>
                            {lotExplorerCalc.timelineIsOverridden && <span className="text-white/35 italic"> (override active)</span>}
                          </div>
                        </div>

                        {/* Management / Hosting / Support — recurring fee */}
                        <div className="space-y-2.5 p-4 rounded-lg bg-cyan-500/[0.03] border border-cyan-500/15" data-testid="lot-explorer-hosting-card">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-xs text-white/85 font-medium">Management, Hosting &amp; Support</Label>
                              <p className="text-[10.5px] text-white/45 mt-0.5 leading-snug">
                                Cloud hosting, uptime monitoring, security patches, backend platform updates, bug fixes, and direct support for your sales team.
                              </p>
                            </div>
                            <Badge className="bg-cyan-500/15 text-cyan-300 border-cyan-500/25 text-[10px]">Recurring</Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.1em] text-white/40">Monthly ($)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={lotExplorerHostingMonthly || ""}
                                onChange={(e) => setLotExplorerHostingMonthly(Math.max(0, parseInt(e.target.value) || 0))}
                                className="h-8 text-[13px]"
                                data-testid="lot-explorer-hosting-monthly"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.1em] text-white/40">Free months</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={lotExplorerHostingFreeMonths}
                                onChange={(e) => setLotExplorerHostingFreeMonths(Math.max(0, parseInt(e.target.value) || 0))}
                                className="h-8 text-[13px]"
                                data-testid="lot-explorer-hosting-free-months"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.1em] text-white/40">Lock-in (mo)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={lotExplorerHostingLockMonths}
                                onChange={(e) => setLotExplorerHostingLockMonths(Math.max(0, parseInt(e.target.value) || 0))}
                                className="h-8 text-[13px]"
                                data-testid="lot-explorer-hosting-lock-months"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/[0.05]">
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.1em] text-white/40">Billed monthly</div>
                              <div className="text-[14px] font-semibold text-white tabular-nums" data-testid="lot-explorer-hosting-display-monthly">
                                ${lotExplorerCalc.hostingMonthly.toLocaleString()}<span className="text-[11px] text-white/50 font-normal">/mo</span>
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.1em] text-white/40">{lotExplorerCalc.hostingLockMonths}-month lock-in</div>
                              <div className="text-[14px] font-semibold text-cyan-200 tabular-nums" data-testid="lot-explorer-hosting-lockin-total">
                                ${lotExplorerCalc.hostingLockInValue.toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="text-[10.5px] text-cyan-300/80 leading-snug pt-1">
                            <span className="font-medium">First {lotExplorerCalc.hostingFreeMonths} months included</span> · then ${lotExplorerCalc.hostingMonthly}/mo for the {lotExplorerCalc.hostingLockMonths}-month commitment.
                          </div>
                        </div>

                        {/* Lot Explorer Payment Plan — REQUIRED 50/25/25 */}
                        <div className="mt-2 p-4 rounded-xl bg-[#000000] border border-cyan-500/15" data-testid="lot-explorer-payment-card">
                          <div className="flex items-baseline justify-between mb-3">
                            <div>
                              <div className="text-[12px] font-semibold text-white">Lot Explorer Payment Plan</div>
                              <div className="text-[10px] uppercase tracking-[0.1em] text-cyan-300/60 mt-0.5">Deposit · V1 Delivery · Final Delivery</div>
                            </div>
                            <div className="text-[11px] text-muted-foreground tabular-nums">Total ${lotExplorerPayment.total.toLocaleString()}</div>
                          </div>
                          <div className="space-y-2">
                            {lotExplorerPayment.milestones.map((m) => (
                              <div key={m.key} className="flex items-start justify-between py-2 border-t border-white/[0.04] first:border-t-0">
                                <div className="min-w-0 pr-3">
                                  <div className="text-[13px] text-white/90">{m.percent}% {m.label}</div>
                                  <div className="text-[10.5px] text-white/45 mt-0.5 leading-snug">{m.note}</div>
                                </div>
                                <div className="text-[14px] font-semibold text-cyan-200 tabular-nums" data-testid={`lot-explorer-milestone-${m.key}`}>${m.amount.toLocaleString()}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <Card>
              <CardHeader><CardTitle className="text-lg">Internal Notes</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes visible only to your team..." rows={3} />
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Premium Quote Summary */}
          <div className="space-y-4">
            <Card className={`sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] ${presentationMode ? 'bg-[#000000]' : 'bg-[#000000]'} rounded-[24px]`} style={{ scrollbarColor: 'rgba(255,255,255,0.12) transparent', scrollbarWidth: 'thin' }}>
              {/* Premium Header with Segmented Control */}
              <CardHeader className={`pb-4 px-6 pt-6 ${presentationMode ? 'border-b border-white/[0.08]' : 'border-b border-white/[0.06]'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-[17px] font-light text-white tracking-tight leading-tight">
                      {presentationMode ? 'Project Overview' : 'Quote Summary'}
                    </h2>
                    {presentationMode && (projectName || clientName) && (
                      <p className="text-[12px] text-white/50 mt-1 truncate">
                        {projectName || "Project"} · {clientName || "Client"}
                      </p>
                    )}
                  </div>
                  {/* Premium Segmented Control */}
                  <div className="flex items-center bg-white/[0.04] border border-white/[0.06] rounded-[10px] p-0.5 shrink-0">
                    <button
                      onClick={() => setPresentationMode(false)}
                      className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium tracking-wide transition-all duration-200 ${!presentationMode ? 'bg-white/[0.08] text-white shadow-sm' : 'text-white/50 hover:text-white/75'}`}
                      data-testid="mode-calculator"
                    >
                      Calculator
                    </button>
                    <button
                      onClick={() => setPresentationMode(true)}
                      className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium tracking-wide transition-all duration-200 ${presentationMode ? 'bg-white/[0.08] text-white shadow-sm' : 'text-white/50 hover:text-white/75'}`}
                      data-testid="mode-presentation"
                    >
                      Presentation
                    </button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className={presentationMode ? 'pt-8 px-6' : 'px-6 py-2 text-sm'}>
                {presentationMode ? (
                  /* Premium Presentation Mode */
                  <PresentationSummary />
                ) : (
                  /* Calculator Mode - Detailed Line Items */
                  <>
                    {/* SECTION A: VEXOS Implementation */}
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger
                        className={`group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200 ${vexosModuleEnabled ? '' : 'opacity-50'}`}
                        data-testid="section-vexos-implementation"
                      >
                        <div className="flex items-center gap-2.5">
                          <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                          <span className="text-[14px] text-white font-medium tracking-tight">VEXOS Implementation</span>
                          {!vexosModuleEnabled && (
                            <span className="text-[9px] uppercase tracking-[0.1em] text-white/40 px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">Off</span>
                          )}
                        </div>
                        <span className="text-[15px] text-white font-semibold tabular-nums">${(vexosModuleEnabled ? vexosCalc.implementationTotal : 0).toLocaleString()}</span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-1 pb-3 pl-5 space-y-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-1 data-[state=open]:slide-in-from-top-1 duration-200">
                        {!vexosModuleEnabled ? (
                          <div className="py-3 text-[11px] text-white/40 italic">Module disabled. Toggle on the VEXOS Platform card to add scope.</div>
                        ) : (
                          <>
                            <SummarySection
                              title="Platform Setup"
                              total={vexosCalc.platformSubtotal}
                              items={platformItems}
                            />

                            {exteriorCalc.exteriorModelingTotal > 0 && (
                              <SummarySection
                                title="Exterior Modeling"
                                total={exteriorCalc.exteriorModelingTotal}
                                items={exteriorItems}
                              />
                            )}

                            {orbitCalc.orbitConfigurationTotal > 0 && (
                              <SummarySection
                                title="Orbit Configuration"
                                total={orbitCalc.orbitConfigurationTotal}
                                items={[
                                  ...(orbitCalc.masterOrbitCost > 0 ? [{
                                    label: "Suborbit",
                                    quantity: 1,
                                    rate: masterOrbitPrice,
                                    subtotal: orbitCalc.masterOrbitCost
                                  }] : []),
                                  ...orbitCalc.additionalOrbitDetails.map(orbit => ({
                                    label: orbit.type === "custom" 
                                      ? `Custom: ${orbit.customName || 'Unnamed'}`
                                      : orbit.typeLabel,
                                    quantity: orbit.quantity,
                                    rate: orbit.unitPrice,
                                    subtotal: orbit.subtotal
                                  }))
                                ]}
                              />
                            )}

                            <SummarySection
                              title="Floorplate Development"
                              total={vexosCalc.floorplateSubtotal}
                              items={floorplateItems}
                            />
                          </>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* SECTION B: Visualization */}
                    <Collapsible defaultOpen>
                      <CollapsibleTrigger
                        className={`group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200 ${vizModuleEnabled ? '' : 'opacity-50'}`}
                        data-testid="section-visualization"
                      >
                        <div className="flex items-center gap-2.5">
                          <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                          <span className="text-[14px] text-white font-medium tracking-tight">Visualization</span>
                          {!vizModuleEnabled && (
                            <span className="text-[9px] uppercase tracking-[0.1em] text-white/40 px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.06]">Off</span>
                          )}
                        </div>
                        <span className="text-[15px] text-white font-semibold tabular-nums">${(vizModuleEnabled ? vizCalc.grandTotal : 0).toLocaleString()}</span>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-1 pb-3 pl-5 space-y-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-1 data-[state=open]:slide-in-from-top-1 duration-200">
                        {!vizModuleEnabled ? (
                          <div className="py-3 text-[11px] text-white/40 italic">Module disabled. Toggle on the Visualization card to add scope.</div>
                        ) : (
                          <>
                            {vizCalc.interiorModelingSubtotal > 0 && (
                              <SummarySection
                                title="Interior Modeling"
                                total={vizCalc.interiorModelingSubtotal}
                                items={interiorModelingItems}
                              />
                            )}

                            {!vexosModuleEnabled && exteriorCalc.exteriorModelingTotal > 0 && (
                              <SummarySection
                                title="Exterior Modeling"
                                total={exteriorCalc.exteriorModelingTotal}
                                items={exteriorItems}
                              />
                            )}

                            {vizCalc.renderingSubtotal > 0 && (
                              <SummarySection
                                title="Renderings"
                                total={vizCalc.renderingSubtotal}
                                items={renderingItems}
                              />
                            )}

                            {vizCalc.vrSubtotal > 0 && (
                              <SummarySection
                                title="VR / 360° Tours"
                                total={vizCalc.vrSubtotal}
                                items={vrItems}
                              />
                            )}

                            {vizCalc.viewChangeSubtotal > 0 && (
                              <SummarySection
                                title="VR View-Change Duplicates"
                                total={vizCalc.viewChangeSubtotal}
                                items={[
                                  {
                                    label: `View-Change Duplicates (${vizCalc.totalViewChangeCount} ${vizCalc.totalViewChangeCount === 1 ? "tour" : "tours"})`,
                                    quantity: vizCalc.totalViewChangeCount,
                                    rate: vizCalc.viewChangeRate,
                                    subtotal: vizCalc.viewChangeSubtotal,
                                    sublabel: `Volume tier: $${vizCalc.viewChangeRate}/tour · Same floorplan, new backplate + 1–3 re-renders`,
                                  },
                                ]}
                              />
                            )}

                            {vizCalc.animationSubtotal > 0 && (
                              <SummarySection
                                title="Video Animation"
                                total={vizCalc.animationSubtotal}
                                items={[
                                  {
                                    label: getAnimationDisplayName(animationType, animationDuration),
                                    quantity: 1,
                                    rate: getVideoAnimationRate(animationType, animationDuration),
                                    subtotal: getVideoAnimationRate(animationType, animationDuration),
                                    sublabel: `${animationDuration}s • ${vizCalc.animationShots} shots${animationType === "cinematic" ? " • 3s/shot" : ""}`
                                  },
                                  ...(socialMediaPack ? [{
                                    label: SOCIAL_MEDIA_PACK.label,
                                    quantity: 1,
                                    rate: SOCIAL_MEDIA_PACK.price,
                                    subtotal: SOCIAL_MEDIA_PACK.price,
                                    sublabel: "Short-form cutdowns + vertical versions"
                                  }] : [])
                                ]}
                              />
                            )}

                            {/* Conceptual Exterior Modeling — consolidated under Visualization.
                                Surfaces even when the Viz module itself is OFF, because Concept
                                Modeling has its own enable toggle. */}
                            {conceptModelingCalc.grandTotal > 0 && (
                              <SummarySection
                                title={`Concept Modeling — ${conceptApproach === "lite" ? "Concept Lite" : conceptApproach === "plus" ? "Concept Plus" : "Concept Studio"}`}
                                total={conceptModelingCalc.grandTotal}
                                items={conceptModelingCalc.lineItems}
                              />
                            )}

                            {vizCalc.grandTotal === 0 && conceptModelingCalc.grandTotal === 0 && (
                              <p className="text-[12px] text-white/40 italic py-3">No visualization services selected</p>
                            )}
                          </>
                        )}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* SECTION B2: Conceptual Exterior Modeling — consolidated into the
                        Visualization section above. Block kept here for back-compat in case
                        Concept Modeling is enabled while the Viz module is OFF (rare). */}
                    {conceptModelingCalc.grandTotal > 0 && !vizModuleEnabled && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200" data-testid="section-concept-modeling">
                          <div className="flex items-center gap-2.5">
                            <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                            <span className="text-[14px] text-white font-medium tracking-tight">Concept Modeling</span>
                          </div>
                          <span className="text-[15px] text-white font-semibold tabular-nums">${conceptModelingCalc.grandTotal.toLocaleString()}</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-1 pb-3 pl-5 space-y-0">
                          <SummarySection
                            title={conceptApproach === "lite" ? "Concept Lite" : conceptApproach === "plus" ? "Concept Plus" : "Concept Studio"}
                            total={conceptModelingCalc.grandTotal}
                            items={conceptModelingCalc.lineItems}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* SECTION B3: Marketing Strategy */}
                    {marketingCalc.grandTotal > 0 && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200" data-testid="section-marketing">
                          <div className="flex items-center gap-2.5">
                            <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                            <span className="text-[14px] text-white font-medium tracking-tight">Marketing</span>
                          </div>
                          <span className="text-[15px] text-white font-semibold tabular-nums">${Math.round(marketingCalc.monthlyBudget).toLocaleString()}<span className="text-[12px] text-white/50 font-normal">/mo</span></span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-2 pb-3 pl-5 space-y-2 text-[12px]">
                          <div className="flex justify-between">
                            <span className="text-white/50">Campaign Type</span>
                            <span className="text-white/80">{marketingCalc.modeLabel}</span>
                          </div>
                          <div className="flex justify-between" data-testid="sidebar-campaign-duration">
                            <span className="text-white/50">Campaign Duration</span>
                            <span className="text-white/80 tabular-nums">{marketingTimeline} {marketingTimeline === 1 ? "month" : "months"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/50">Setup Fee</span>
                            <span className="text-white/80 tabular-nums">${MKTG_SETUP_FEE.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/50">Monthly Budget</span>
                            <span className="text-white/80 tabular-nums">${Math.round(marketingCalc.monthlyBudget).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/50">Est. Monthly Leads</span>
                            <span className="text-white/80 tabular-nums">{marketingCalc.monthlyLeads.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-white/[0.06]">
                            <span className="text-white/70">Total Campaign</span>
                            <span className="text-white font-medium tabular-nums">${marketingCalc.grandTotal.toLocaleString()}</span>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* SECTION B4: Collateral Suite */}
                    {collateralCalc.grandTotal > 0 && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200" data-testid="section-collateral">
                          <div className="flex items-center gap-2.5">
                            <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                            <span className="text-[14px] text-white font-medium tracking-tight">Collateral Suite</span>
                          </div>
                          <span className="text-[15px] text-white font-semibold tabular-nums">${collateralCalc.grandTotal.toLocaleString()}</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-2 pb-3 pl-5 space-y-1.5 text-[12px]">
                          {collateralCalc.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span className="text-white/60">{item.label}</span>
                              <span className="text-white/80 tabular-nums">${item.amount.toLocaleString()}</span>
                            </div>
                          ))}
                          {collateralCalc.aiRetainerMonthly > 0 && (
                            <div className="flex justify-between">
                              <span className="text-white/60">
                                AI Social ({collateralCalc.aiRetainerTier} clips/mo · {collateralCalc.retainerMonths}mo)
                              </span>
                              <span className="text-white/80 tabular-nums">${collateralCalc.aiRetainerTotal.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-1 border-t border-white/[0.06]">
                            <span className="text-white/70">Total</span>
                            <span className="text-white font-medium tabular-nums">${collateralCalc.grandTotal.toLocaleString()}</span>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* SECTION B5: VEXOS Lot Explorer™ — Standalone software */}
                    {lotExplorerModuleEnabled && lotExplorerCalc.grandTotal > 0 && (
                      <Collapsible defaultOpen>
                        <CollapsibleTrigger className="group flex items-center justify-between w-full py-4 border-b border-white/[0.06] transition-colors duration-200" data-testid="section-lot-explorer">
                          <div className="flex items-center gap-2.5">
                            <ChevronDown className="w-3.5 h-3.5 text-white/40 transition-transform duration-300 group-data-[state=closed]:-rotate-90" />
                            <span className="text-[14px] text-white font-medium tracking-tight">VEXOS Lot Explorer&trade;</span>
                            <span className="text-[9px] uppercase tracking-[0.1em] text-cyan-300/70 px-1.5 py-0.5 rounded bg-cyan-500/[0.08] border border-cyan-500/20">Standalone</span>
                          </div>
                          <span className="text-[15px] text-white font-semibold tabular-nums">${lotExplorerCalc.grandTotal.toLocaleString()}</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-2 pb-3 pl-5 space-y-1.5 text-[12px]">
                          <div className="flex justify-between">
                            <span className="text-white/60">Interactive Sitemap ({lotExplorerCalc.lotCount.toLocaleString()} lots &times; ${lotExplorerCalc.ratePerLot.toLocaleString()})</span>
                            <span className="text-white/80 tabular-nums">${lotExplorerCalc.grandTotal.toLocaleString()}</span>
                          </div>
                          {lotExplorerCalc.features.length > 0 && (
                            <div className="text-[10.5px] text-white/45 pt-0.5">Includes: {lotExplorerCalc.features.join(" · ")}</div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* SECTION B5b: Lot Explorer Payment Plan — REQUIRED 50/25/25 (sidebar mirror) */}
                    {lotExplorerModuleEnabled && lotExplorerCalc.grandTotal > 0 && (
                      <div className="mt-6 mb-2 p-5 bg-[#000000] border border-cyan-500/15 rounded-[20px]" data-testid="lot-explorer-payment-plan">
                        <div className="flex items-baseline justify-between mb-1">
                          <h3 className="text-[13px] font-semibold text-white tracking-tight">Payment Plan</h3>
                          <span className="text-[10px] uppercase tracking-[0.1em] text-cyan-300/70">Lot Explorer</span>
                        </div>
                        <p className="text-[11px] text-white/45 mb-5">Fixed 50 / 25 / 25 milestone schedule</p>
                        <div className="mb-5 pb-4 border-b border-white/[0.06]">
                          <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">Total Investment</p>
                          <p className="text-[26px] font-light text-white tabular-nums tracking-tight leading-none">
                            ${lotExplorerPayment.total.toLocaleString()}
                          </p>
                        </div>
                        <div className="space-y-2.5">
                          {lotExplorerPayment.milestones.map((m) => (
                            <div key={m.key} className="flex items-baseline justify-between">
                              <span className="text-[12px] text-white/70">{m.percent}% {m.label}</span>
                              <span className="text-[13px] text-cyan-200 font-medium tabular-nums">${m.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                        {lotExplorerCalc.hostingMonthly > 0 && (
                          <div className="mt-5 pt-4 border-t border-white/[0.06]">
                            <div className="flex items-baseline justify-between mb-1">
                              <span className="text-[11px] uppercase tracking-[0.1em] text-cyan-300/70">Hosting &amp; Support</span>
                              <span className="text-[13px] text-cyan-200 font-medium tabular-nums">${lotExplorerCalc.hostingMonthly}<span className="text-[10px] text-white/50 font-normal">/mo</span></span>
                            </div>
                            <p className="text-[10.5px] text-white/45 leading-snug">
                              First {lotExplorerCalc.hostingFreeMonths} months free · {lotExplorerCalc.hostingLockMonths}-mo commitment · ${lotExplorerCalc.hostingLockInValue.toLocaleString()} total
                            </p>
                          </div>
                        )}
                      </div>
                    )}



                    {/* SECTION C: VEXOS Platform Investment — SaaS-style payment model.
                        Replaces the legacy upfront-% + financing widget.
                        Hidden entirely when VEXOS module is OFF or has $0 scope. */}
                    {vexosModuleEnabled && vexosCalc.implementationTotal > 0 && (
                    <div className="mt-6 mb-2 p-5 bg-[#000000] border border-white/[0.06] rounded-[20px]" data-testid="vexos-payment-plan">
                      <div className="flex items-baseline justify-between mb-1">
                        <h3 className="text-[13px] font-semibold text-white tracking-tight">VEXOS Platform Investment</h3>
                        <span className="text-[10px] uppercase tracking-[0.1em] text-white/35">VEXOS</span>
                      </div>
                      <p className="text-[11px] text-white/45 mb-5">Premium recurring platform &amp; service ecosystem</p>

                      {/* Upfront Platform Setup Fee */}
                      <div className="mb-5 pb-5 border-b border-white/[0.06]">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">Upfront Platform Setup Fee</p>
                        <p className="text-[28px] font-light text-white tabular-nums tracking-tight leading-none mb-1.5" data-testid="vexos-setup-fee">
                          ${vexosPayment.setupFee.toLocaleString()}
                        </p>
                        <p className="text-[11px] text-white/45 leading-snug">
                          One-time deployment and implementation fee.
                        </p>
                      </div>

                      {/* Monthly VEXOS Platform Fee */}
                      <div className="mb-5 pb-5 border-b border-white/[0.06]">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">Monthly VEXOS Platform Fee</p>
                        <div className="flex items-baseline gap-1.5 mb-1.5">
                          <span className="text-[28px] font-light text-emerald-400 tabular-nums tracking-tight leading-none" data-testid="vexos-monthly-fee">
                            ${vexosPayment.monthlyFee.toLocaleString()}
                          </span>
                          <span className="text-[13px] text-white/50">/ month</span>
                        </div>
                        <p className="text-[11px] text-white/45 leading-snug">
                          Recurring platform, hosting, support, and software access fee.
                        </p>
                      </div>

                      {/* Platform Agreement Term — 12 months only */}
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2.5">Platform Agreement Term</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {VEXOS_CONTRACT_TERM_OPTIONS.map(months => (
                            <button
                              key={months}
                              onClick={() => setVexosPaymentTermMonths(months)}
                              className={`py-2.5 rounded-[10px] text-[12px] font-medium transition-all duration-200 border ${vexosPaymentTermMonths === months ? 'bg-[#8FA79B]/10 text-[#8FA79B] border-[#8FA79B]/30' : 'bg-white/[0.02] text-white/60 border-white/[0.06] hover:bg-white/[0.04] hover:text-white/80'}`}
                              data-testid={`vexos-contract-term-${months}`}
                            >
                              {months}<span className="text-[10px] text-white/40 ml-0.5">mo</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    )}

                    {/* SECTION D: Visualization Payment Plan
                        Now also covers Conceptual Modeling and AI Content Studio.
                        Hidden only when ALL three modules are off / $0. */}
                    {((vizModuleEnabled && vizCalc.grandTotal > 0) || (conceptModelingEnabled && conceptModelingCalc.grandTotal > 0) || (aiContentModuleEnabled && aiContentTotal > 0)) && (() => {
                      const _hasViz = vizModuleEnabled && vizCalc.grandTotal > 0;
                      const _hasConcept = conceptModelingEnabled && conceptModelingCalc.grandTotal > 0;
                      const _hasAi = aiContentModuleEnabled && aiContentTotal > 0;
                      const _parts = [];
                      if (_hasViz) _parts.push("Visualization");
                      if (_hasConcept) _parts.push("Concept");
                      if (_hasAi) _parts.push("AI Content");
                      const _pillLabel = _parts.join(" + ");
                      const _scopeLabel = _parts.length > 1 ? `${_pillLabel} Scope` : `${_pillLabel} Scope`;
                      const _includesNote = [];
                      if (_hasConcept) _includesNote.push("Conceptual Modeling");
                      if (_hasAi) _includesNote.push("AI Content Studio");
                      const _subNote = _includesNote.length > 0 ? ` · includes ${_includesNote.join(" + ")}` : "";
                      return (
                    <div className="mb-2 p-5 bg-[#000000] border border-white/[0.06] rounded-[20px]" data-testid="viz-payment-plan">
                      <div className="flex items-baseline justify-between mb-1">
                        <h3 className="text-[13px] font-semibold text-white tracking-tight">Payment Plan</h3>
                        <span className="text-[10px] uppercase tracking-[0.1em] text-white/35">{_pillLabel}</span>
                      </div>
                      <p className="text-[11px] text-white/45 mb-5">Milestone-based schedule{_subNote}</p>

                      <div className="mb-5 pb-5 border-b border-white/[0.06]">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-white/40 mb-2">{_scopeLabel}</p>
                        <p className="text-[24px] font-light text-white tabular-nums tracking-tight leading-none">
                          ${visualizationScopeTotal.toLocaleString()}
                        </p>
                      </div>

                      <div className="mb-5">
                        <p className="text-[11px] text-white/60 mb-2.5">Structure</p>
                        <Select value={paymentPreset} onValueChange={setPaymentPreset}>
                          <SelectTrigger className="h-10 bg-white/[0.02] border-white/[0.08] text-white text-[12px] rounded-[10px]" data-testid="payment-preset-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(PAYMENT_PRESETS).map(([key, preset]) => (
                              <SelectItem key={key} value={key}>{preset.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {paymentPreset === "custom" && (
                        <div className="space-y-2 mb-5 p-3 bg-white/[0.02] border border-white/[0.06] rounded-[12px]">
                          {customMilestones.map((milestone, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <Input type="number" value={milestone.percent} onChange={(e) => updateCustomMilestone(idx, "percent", parseInt(e.target.value) || 0)} className="w-14 h-8 text-center bg-white/[0.03] border-white/[0.08] text-white text-[11px]" placeholder="%" />
                              <span className="text-white/50 text-[11px]">%</span>
                              <Input value={milestone.label} onChange={(e) => updateCustomMilestone(idx, "label", e.target.value)} className="flex-1 h-8 bg-white/[0.03] border-white/[0.08] text-white text-[11px]" placeholder="Milestone label" />
                              <Button variant="ghost" size="icon" onClick={() => removeCustomMilestone(idx)} className="h-7 w-7 text-white/50 hover:text-white"><Minus className="w-3.5 h-3.5" /></Button>
                            </div>
                          ))}
                          <div className="flex items-center justify-between pt-1">
                            <button onClick={addCustomMilestone} className="flex items-center gap-1 text-[11px] text-[#8FA79B] hover:text-[#8FA79B]/80 transition-colors"><Plus className="w-3 h-3" /> Add milestone</button>
                            <span className={`text-[10px] ${customMilestoneTotal === 100 ? "text-[#8FA79B]" : "text-amber-400/80"}`}>Total: {customMilestoneTotal}%</span>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2.5">
                        {vizMilestoneAmounts.map((milestone, idx) => {
                          const _msCount = vizMilestoneAmounts.length;
                          const _hasMidpoint = _msCount >= 3;
                          const _isMidpointWithV1 = _hasMidpoint && idx === 1;
                          // 50/50 (2-milestone) plan: rewrite final row label, never show V1
                          const _is5050Final = !_hasMidpoint && idx === _msCount - 1 && idx > 0;
                          const _displayLabel = _is5050Final
                            ? "Due Before Final Delivery / Upon Approval of Assets"
                            : (_isMidpointWithV1
                              ? `${milestone.label} / V1 Delivery of Renderings${effectiveV1DeliveryWeek != null ? ` — Week ${effectiveV1DeliveryWeek}` : ""}`
                              : milestone.label);
                          const _rowLabelClass = _isMidpointWithV1
                            ? "text-[12px] text-indigo-300 font-semibold"
                            : "text-[12px] text-white/55";
                          const _rowAmountClass = _isMidpointWithV1
                            ? "text-[13px] text-indigo-300 font-semibold tabular-nums"
                            : "text-[13px] text-white font-medium tabular-nums";
                          return (
                            <div key={idx}>
                              <div className={`flex justify-between items-baseline ${_isMidpointWithV1 ? 'px-3 py-2 rounded-lg bg-indigo-500/[0.08] border border-indigo-400/[0.18]' : ''}`} data-testid={_isMidpointWithV1 ? 'viz-v1-milestone' : undefined}>
                                <span className={_rowLabelClass}>{_displayLabel}</span>
                                <span className={_rowAmountClass}>
                                  ${milestone.amount.toLocaleString()}
                                  {_isMidpointWithV1 && (
                                    <span className="text-[10px] font-normal text-indigo-300/55 ml-1">({milestone.percent}%)</span>
                                  )}
                                </span>
                              </div>
                              {/* V1 override controls — only show when plan has a midpoint (V1 actually applies) */}
                              {_isMidpointWithV1 && (
                                <div className="mt-1.5 mb-1 px-3 flex items-center gap-2">
                                  <Label className="text-[10px] text-white/45 whitespace-nowrap">Override week</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={vizV1DeliveryWeekOverride}
                                    onChange={(e) => setVizV1DeliveryWeekOverride(e.target.value)}
                                    placeholder={autoV1DeliveryWeek != null ? `auto: ${autoV1DeliveryWeek}` : "—"}
                                    className="h-7 w-20 text-[11px] bg-white/[0.04] border-white/[0.1] text-white"
                                    data-testid="viz-v1-override-input"
                                  />
                                  {isV1Overridden && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-[10px] text-white/50 hover:text-white"
                                      onClick={() => setVizV1DeliveryWeekOverride("")}
                                      data-testid="viz-v1-override-clear"
                                    >Reset</Button>
                                  )}
                                  <span className="text-[10px] text-white/35 italic whitespace-nowrap ml-auto">
                                    {isV1Overridden ? "(manual override)" : (autoV1DeliveryWeek != null ? "(end of Modeling phase)" : "(set timeline scope)")}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                    })()}

                    {/* Estimated Production Timeline — only relevant when there is actual
                        production work (Visualization or VEXOS). Hidden for Marketing /
                        Collateral / AI-only proposals where no production timeline exists. */}
                    {((vizModuleEnabled && vizCalc.grandTotal > 0) || (vexosModuleEnabled && vexosCalc.implementationTotal > 0)) && (
                    <TimelineEstimateWidget
                      deliverables={timelineDeliverables}
                      proposalTotal={oneTimeTotal}
                      overrideEnabledControlled={timelineOverrideEnabled}
                      overrideWeeksControlled={timelineOverrideWeeks}
                      onOverrideChange={({ enabled, weeks }) => {
                        setTimelineOverrideEnabled(enabled);
                        setTimelineOverrideWeeks(weeks);
                      }}
                      onEstimateChange={setTimelineEstimateSnapshot}
                      data-testid="combined-timeline-widget"
                    />
                    )}

                    {/* SUMMARY TOTALS — Premium Footer */}
                    <div className="mt-5 pt-5 border-t border-white/[0.08] space-y-3">
                      {/* Special Client Discount panel */}
                      <div className="p-3 bg-[#000000] border border-white/[0.06] rounded-[14px]" data-testid="special-discount-panel">
                        <div className="flex items-baseline justify-between mb-2">
                          <span className="text-[11px] uppercase tracking-[0.1em] text-white/45">Special Client Discount</span>
                          {hasDiscount && (
                            <button
                              onClick={() => setSpecialDiscountPct(0)}
                              className="text-[10px] text-white/45 hover:text-white/70 transition-colors"
                              data-testid="special-discount-reset"
                            >Clear</button>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-1.5 mb-2">
                          {[10, 15, 18].map(pct => (
                            <button
                              key={pct}
                              onClick={() => setSpecialDiscountPct(specialDiscountPct === pct ? 0 : pct)}
                              className={`py-1.5 rounded-[8px] text-[11px] font-medium transition-all duration-200 border ${specialDiscountPct === pct ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30' : 'bg-white/[0.02] text-white/60 border-white/[0.06] hover:bg-white/[0.04] hover:text-white/80'}`}
                              data-testid={`special-discount-${pct}`}
                            >{pct}%</button>
                          ))}
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            placeholder="Custom"
                            value={[10, 15, 18].includes(specialDiscountPct) ? "" : (specialDiscountPct || "")}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              if (isNaN(v)) setSpecialDiscountPct(0);
                              else setSpecialDiscountPct(Math.max(0, Math.min(100, v)));
                            }}
                            className="h-7 text-[11px] bg-white/[0.04] border-white/[0.06] text-white text-center px-1"
                            data-testid="special-discount-custom"
                          />
                        </div>
                        {hasDiscount ? (
                          <div className="space-y-1 pt-2 border-t border-white/[0.06]">
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-white/55">Subtotal Before Discount</span>
                              <span className="text-[12px] text-white/85 tabular-nums">${enabledSubtotal.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-baseline">
                              <span className="text-[11px] text-emerald-300/85">Special Client Discount (<span className="font-bold">{_safeDiscountPct}%</span>)</span>
                              <span className="text-[12px] text-emerald-300 tabular-nums">-${Math.round(discountAmount).toLocaleString()}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-[11px] text-white/40 italic pt-1">No discount applied</div>
                        )}
                      </div>

                      <div className="flex justify-between items-baseline">
                        <span className="text-[12px] text-white/55">Total Investment</span>
                        <span className="text-[17px] font-light text-white tabular-nums tracking-tight" data-testid="total-investment">${Math.round(enabledSubtotalAfterDiscount).toLocaleString()}</span>
                      </div>

                      <div className="mt-4 p-4 bg-[#000000] border border-white/[0.08] rounded-[16px]">
                        <div className="flex items-baseline justify-between">
                          <span className="text-[11px] uppercase tracking-[0.1em] text-white/50">Due at Signing</span>
                          <span className="text-[22px] font-light text-white tabular-nums tracking-tight" data-testid="due-at-signing">${totalDueToday.toLocaleString()}</span>
                        </div>
                      </div>

                      {vexosModuleEnabled && vexosPayment.totalVexos > 0 && (
                        <div className="mt-2 p-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-[14px]">
                          <div className="flex items-baseline justify-between">
                            <div className="flex flex-col">
                              <span className="text-[11px] uppercase tracking-[0.1em] text-emerald-400/80">Recurring Monthly</span>
                              <span className="text-[10px] text-white/45 mt-0.5">VEXOS Platform · {vexosPayment.contractTermMonths}-month agreement</span>
                            </div>
                            <span className="text-[18px] font-light text-emerald-400 tabular-nums tracking-tight" data-testid="recurring-monthly">
                              ${vexosPayment.recurringMonthly.toLocaleString()}<span className="text-[11px] font-normal text-white/40"> / mo</span>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Pre-flight Parity Badge — sidebar ↔ PDF reconciliation.
                    Hidden until the quote is saved; reflects live sidebar
                    state vs the last persisted section rollup so Sales sees
                    "unsaved changes" before they hit Generate Proposal. */}
                {!isNew && quoteId && quoteId !== "new" && enabledSubtotal > 0 && (() => {
                  const live = Math.round(enabledSubtotal);
                  const persisted = Math.round(reconcile?.sidebar_subtotal || 0);
                  const rollup = Math.round(reconcile?.backend_section_total || 0);
                  const unsaved = reconcile && persisted !== live;
                  const internalMismatch = reconcile && !reconcile.matches;
                  const ok = reconcile && !unsaved && !internalMismatch;
                  if (reconcileLoading && !reconcile) return null;
                  if (ok) {
                    return (
                      <div
                        className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-[10px] text-[11px] font-medium ${presentationMode ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}
                        data-testid="parity-badge-ok"
                        title={`Sidebar $${live.toLocaleString()} = Section rollup $${rollup.toLocaleString()}`}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Sidebar = PDF · totals reconciled
                      </div>
                    );
                  }
                  // Unsaved changes — live subtotal hasn't been persisted yet.
                  if (unsaved) {
                    const delta = live - persisted;
                    return (
                      <div
                        className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-[10px] text-[11px] font-medium ${presentationMode ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
                        data-testid="parity-badge-unsaved"
                        title={`Live sidebar $${live.toLocaleString()} differs from persisted $${persisted.toLocaleString()}`}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Unsaved changes · save to lock in PDF totals ({delta >= 0 ? '+' : ''}${delta.toLocaleString()})
                      </div>
                    );
                  }
                  // Persisted subtotal doesn't match section rollup — true drift.
                  if (internalMismatch) {
                    return (
                      <div
                        className={`mt-3 px-3 py-2 rounded-[10px] text-[11px] font-medium ${presentationMode ? 'bg-red-500/10 text-red-300 border border-red-500/20' : 'bg-red-50 text-red-700 border border-red-200'}`}
                        data-testid="parity-badge-mismatch"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" />
                          PDF Mismatch · save before exporting
                        </div>
                        {(reconcile.issues || []).slice(0, 1).map((iss, i) => (
                          <div key={i} className="opacity-80 leading-snug">{iss.message}</div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Generate Proposal Button - Always visible */}
                <div className={presentationMode ? "pt-6" : "pt-4"}>
                  {presentationMode ? (
                    <button
                      className="w-full flex items-center justify-center gap-2.5 bg-zinc-700 text-white font-medium py-3.5 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleGenerateProposal}
                      disabled={generatingProposal || !quoteId || isNew || enabledSubtotal <= 0}
                      data-testid="generate-proposal-btn"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="text-[15px]">{generatingProposal ? "Generating..." : "Generate Proposal"}</span>
                    </button>
                  ) : (
                    <Button 
                      className="w-full gap-2" 
                      variant="outline"
                      onClick={handleGenerateProposal}
                      disabled={generatingProposal || !quoteId || isNew || enabledSubtotal <= 0}
                      data-testid="generate-proposal-btn"
                    >
                      <FileText className="w-4 h-4" />
                      {generatingProposal ? "Generating..." : "Generate Proposal"}
                    </Button>
                  )}
                  {isNew && (
                    <p className={`text-xs text-center mt-3 ${presentationMode ? 'text-zinc-500' : 'text-muted-foreground'}`}>
                      Save the quote first to generate a proposal
                    </p>
                  )}
                  {!isNew && enabledSubtotal <= 0 && (
                    <p
                      className={`text-xs text-center mt-3 ${presentationMode ? 'text-zinc-400' : 'text-amber-600 dark:text-amber-500'}`}
                      data-testid="modules-off-helper"
                    >
                      Select at least one service category to build a proposal.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Proposal Modal */}
      {proposalHtml && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Generated Proposal</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrintProposal}>
                  <FileText className="w-4 h-4 mr-2" />
                  Print / Save PDF
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setProposalHtml(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                srcDoc={proposalHtml}
                className="w-full h-full min-h-[600px]"
                title="Proposal Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
